import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import * as XLSX from "xlsx";

// ============================================================
// ★ 한컴캠퍼스 급여관리 시스템 (payroll.ehancom.com)
// Version : 1.7.3
// Updated : 2026-05-22 (KST)
// Changes : ① 강사 정보 수정에 수당/공제 항목 추가
//           ② 강사 수당/공제 DB 저장 (instructor_allowances 테이블)
//           ③ 강사 명세서에 수당/공제 반영
//           ④ 강사 SMS/이메일 발송 시 수당/공제 반영
//           ⑤ 한컴캠퍼스 로고 교체 (고해상도)
// ============================================================
const APP_VERSION = "1.7.3";

// ── API 설정
const API_BASE = "https://payroll.ehancom.com/api";
let _token = (() => { try { return sessionStorage.getItem("payroll_token") || ""; } catch(e) { return ""; } })();
const setToken = (t) => {
  _token = t;
  try {
    if (t) sessionStorage.setItem("payroll_token", t);
    else sessionStorage.removeItem("payroll_token");
  } catch(e) {}
};
const getToken = () => _token;

const api = async (method, path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "서버 오류");
  return data;
};

// ============================================================
// ★ 급여관리 시스템 완전 통합본 (Step 1~4)
// ============================================================

// ── 보험료율 (2025년 기준)
const RATES = {
  pension: { emp: 0.0475,  er: 0.0475,  min: 390000, max: 6170000 }, // 국민연금 4.75%
  health:  { emp: 0.03595, er: 0.03595 },                             // 건강보험 3.595%
  ltc:     { rate: 0.1314 },                                           // 장기요양 13.14%
  employ:  { emp: 0.009,   er: 0.0115 },                              // 고용보험 0.9%
};

// 고용보험 면제 직책
const EMPLOY_EXEMPT = ["대표이사"]; // 대표이사만 고용보험 면제

// ── 간이세액표 (부양가족 1~5명)
const TAX_TABLE = [
  [0,1060000,[0,0,0,0,0]],
  [1060000,1200000,[21060,940,0,0,0]],
  [1200000,1400000,[29280,9160,0,0,0]],
  [1400000,1600000,[55350,33820,22010,10180,0]],
  [1600000,1800000,[75730,52480,38880,26960,15050]],
  [1800000,2000000,[96110,70140,55740,43830,31910]],
  [2000000,2200000,[116490,89600,74590,62690,50770]],
  [2200000,2500000,[147060,118790,102880,90970,79060]],
  [2500000,2800000,[186750,155000,139490,127580,115670]],
  [2800000,3000000,[220180,185850,170350,158430,146520]],
  [3000000,3500000,[303380,262460,246950,235040,223130]],
  [3500000,4000000,[378300,335650,320140,308230,296320]],
  [4000000,4500000,[469680,424290,408780,396870,384960]],
  [4500000,5000000,[566820,519380,503870,491960,480050]],
  [5000000,6000000,[663960,614470,598960,587050,575140]],
  [6000000,8000000,[979280,923720,908210,896300,884390]],
  [8000000,Infinity,[1438650,1377460,1361950,1350040,1338130]],
];

function lookupTax(gross, dep=1) {
  const d = Math.min(Math.max(dep,1),5)-1;
  for (const [lo,hi,taxes] of TAX_TABLE) {
    if (gross>=lo && gross<hi) return Math.max(0,taxes[d]||0);
  }
  return 0;
}

function calcEmployee(emp) {
  const allowTotal=(emp.allowances||[]).reduce((s,a)=>s+(a.amount||0),0);
  const gross=(emp.baseSalary||0)+allowTotal;
  const nonTax=Math.min((emp.allowances||[]).filter(a=>a.nonTaxable).reduce((s,a)=>s+a.amount,0),200000);
  const taxable=gross-nonTax;
  const penBasis=Math.min(Math.max(Math.floor(taxable/1000)*1000,RATES.pension.min),RATES.pension.max);
  const pension=Math.round(penBasis*RATES.pension.emp);
  const health=Math.round(taxable*RATES.health.emp);
  const ltc=Math.round(health*RATES.ltc.rate);
  // 대표이사/임원 고용보험 면제
  const isExempt=EMPLOY_EXEMPT.some(t=>(emp.position||"").includes(t));
  const employ=isExempt?0:Math.round(taxable*RATES.employ.emp);
  // 소득세/주민세 면제 여부 (직원별 설정)
  const isTaxExempt=emp.taxExempt||false;
  const incomeTax=isTaxExempt?0:lookupTax(taxable,emp.dependents??0);
  const residentTax=isTaxExempt?0:Math.round(incomeTax*0.1/10)*10;
  // 추가 공제 항목
  const extraDeduct=(emp.deductions||[]).reduce((s,d)=>s+(d.amount||0),0);
  const totalDeduct=pension+health+ltc+employ+incomeTax+residentTax+extraDeduct;
  const netPay=gross-totalDeduct;
  return {gross,taxable,penBasis,pension,health,ltc,employ,incomeTax,residentTax,extraDeduct,totalDeduct,netPay,isExempt,isTaxExempt};
}

function calcInstructor(amount, instructor) {
  const extraAllow = instructor ? (instructor.allowances||[]).reduce((s,a)=>s+(a.amount||0),0) : 0;
  const gross=(amount||0)+extraAllow;
  const incomeTax=Math.round(gross*0.03);
  const residentTax=Math.round(incomeTax*0.1);
  const extraDeduct = instructor ? (instructor.deductions||[]).reduce((s,d)=>s+(d.amount||0),0) : 0;
  const totalDeduct=incomeTax+residentTax+extraDeduct;
  const netPay=gross-totalDeduct;
  return {gross,incomeTax,residentTax,extraDeduct,totalDeduct,netPay};
}

// ── 발송 시뮬레이터 (Step 4)
function makeSlipData(person) {
  const calc = person.ptype === "instructor" ? calcInstructor(person.invoiceAmount) : calcEmployee(person);
  return {
    baseSalary: person.baseSalary||0, allowances: person.allowances||[],
    deductions: person.deductions||[],
    dept: person.dept||"", position: person.position||"",
    dependents: person.dependents??0,
    gross: calc.gross, netPay: calc.netPay, totalDeduct: calc.totalDeduct,
    pension: calc.pension||0, health: calc.health||0, ltc: calc.ltc||0,
    employ: calc.employ||0, incomeTax: calc.incomeTax||0, residentTax: calc.residentTax||0,
    extraDeduct: calc.extraDeduct||0,
    isExempt: calc.isExempt||false,
  };
}
// overrideSlipData: 수기 조정된 금액이 있을 경우 전달, 없으면 자동 계산
async function simulateSend(channel, person, month, overrideSlipData) {
  const slipData = overrideSlipData || makeSlipData(person);
  if (channel === "sms") {
    const res = await fetch(`${API_BASE}/send-slip-sms`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ personName: person.name, phone: person.phone, month, slipData, birthDate: person.birthDate||"" }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "SMS 발송 실패");
    return { ok: true };
  }
  if (channel === "email") {
    const res = await fetch(`${API_BASE}/send-slip-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ personName: person.name, email: person.email, month, slipData, birthDate: person.birthDate||"" }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "이메일 발송 실패");
    return { ok: true };
  }
  throw new Error("알 수 없는 채널");
}

// ── 샘플 데이터
const INIT_EMP = [];
const INIT_INS = [];

// ── 강사별 청구서 초기 데이터 (DB 연동 후 빈 객체로 초기화)
const INIT_INVOICES = {};

const fmt=n=>Math.round(n||0).toLocaleString("ko-KR");
const fmtW=n=>fmt(n)+"원";
// 만원 단위 천단위 쉼표: 45370000 → "4,537만원"
const fmtMan=n=>Math.round((n||0)/10000).toLocaleString("ko-KR")+"만원";
const nowDate=new Date();

// ── 전화번호 자동 포맷 (숫자만 입력 → 010-1234-5678)
const formatPhone=(v="")=>{
  const d=v.replace(/\D/g,"").slice(0,11); // 숫자만, 최대 11자리
  if(d.length<=3)  return d;
  if(d.length<=7)  return `${d.slice(0,3)}-${d.slice(3)}`;
  if(d.startsWith("02")){ // 서울 지역번호 02
    if(d.length<=9) return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
    return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
  }
  return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
};
const THIS_MONTH=`${nowDate.getFullYear()}년 ${nowDate.getMonth()+1}월`;

// ── 테마 프리셋
const THEMES = {
  dark: {
    label:"다크",emoji:"",
    bg:"#020817",surface:"#0a0f1e",card:"#0f172a",border:"#1e293b",border2:"#253553",
    text:"#e2e8f0",muted:"#475569",dim:"#1a2744",slate:"#64748b",
    row1:"#070d1a",row2:"transparent",thead:"#080e1a",inputBg:"#0c1428",
    infoBox:"#050d1f",subBox:"#080e1a",codeBox:"#050a14",memoBox:"#060c1a",
    filterBar:"#080e1a",logItem:"#070d1a",noteBox:"#0a1020",
  },
  darkgray: {
    label:"다크 그레이",emoji:"",
    bg:"#111827",surface:"#1f2937",card:"#27303f",border:"#374151",border2:"#4b5563",
    text:"#f3f4f6",muted:"#6b7280",dim:"#1f2a3a",slate:"#9ca3af",
    row1:"#1a2232",row2:"transparent",thead:"#1a2030",inputBg:"#1a2535",
    infoBox:"#1a2232",subBox:"#1a2232",codeBox:"#161e2e",memoBox:"#1a2232",
    filterBar:"#1a2232",logItem:"#1a2232",noteBox:"#1a2232",
  },
  navy: {
    label:"네이비",emoji:"",
    bg:"#0d1b2a",surface:"#1b2b3c",card:"#1e3448",border:"#2a4060",border2:"#2f4d6e",
    text:"#dbeafe",muted:"#5a7fa0",dim:"#162233",slate:"#7ba3bf",
    row1:"#152436",row2:"transparent",thead:"#152030",inputBg:"#152030",
    infoBox:"#152030",subBox:"#152030",codeBox:"#0f1a26",memoBox:"#152030",
    filterBar:"#152030",logItem:"#152030",noteBox:"#152030",
  },
  slate: {
    label:"슬레이트",emoji:"",
    bg:"#1e2533",surface:"#263044",card:"#2d3a50",border:"#3a4a62",border2:"#445572",
    text:"#e8edf5",muted:"#5c6f88",dim:"#1c2840",slate:"#7a90aa",
    row1:"#232e40",row2:"transparent",thead:"#222c3e",inputBg:"#222c3e",
    infoBox:"#222c3e",subBox:"#222c3e",codeBox:"#1c2636",memoBox:"#222c3e",
    filterBar:"#222c3e",logItem:"#222c3e",noteBox:"#222c3e",
  },
  light: {
    label:"라이트",emoji:"☀️",
    bg:"#f1f5f9",surface:"#ffffff",card:"#ffffff",border:"#e2e8f0",border2:"#cbd5e1",
    text:"#0f172a",muted:"#475569",dim:"#e8f0fe",slate:"#64748b",
    row1:"#f8fafc",row2:"transparent",thead:"#f1f5f9",inputBg:"#ffffff",
    infoBox:"#f8fafc",subBox:"#f8fafc",codeBox:"#f1f5f9",memoBox:"#f8fafc",
    filterBar:"#f1f5f9",logItem:"#f8fafc",noteBox:"#f1f5f9",
  },
  warm: {
    label:"웜 화이트",emoji:"",
    bg:"#faf7f2",surface:"#ffffff",card:"#fffdf9",border:"#e8ddd0",border2:"#d6c9b8",
    text:"#1c1410",muted:"#7c6a58",dim:"#f0e8dc",slate:"#a08878",
    row1:"#fdf9f4",row2:"transparent",thead:"#f5efe6",inputBg:"#fffef8",
    infoBox:"#fdf9f4",subBox:"#fdf9f4",codeBox:"#f5efe6",memoBox:"#fdf9f4",
    filterBar:"#f5efe6",logItem:"#fdf9f4",noteBox:"#f5efe6",
  },
};

// 현재 테마 (전역 — 컴포넌트 밖에서 get/set)
let _theme = THEMES.light;
let _themeListeners = [];
const getC = () => _theme;
const setTheme = (key) => {
  _theme = THEMES[key] || THEMES.light;
  _themeListeners.forEach(fn => fn(_theme));
};

// 테마를 구독하는 훅
function useTheme() {
  const [t, setT] = useState(_theme);
  useEffect(() => {
    const fn = (newT) => setT({...newT});
    _themeListeners.push(fn);
    return () => { _themeListeners = _themeListeners.filter(x => x !== fn); };
  }, []);
  return t;
}

// 고정 색상 (테마 무관)
const FIXED = {
  blue:"#3b82f6",blueL:"#60a5fa",green:"#22c55e",greenL:"#4ade80",
  red:"#ef4444",redL:"#f87171",amber:"#f59e0b",amberL:"#fbbf24",
  purple:"#a855f7",purpleL:"#c084fc",teal:"#14b8a6",tealL:"#2dd4bf",
};

// C: 테마 변수 + 고정 색상을 통합 조회 (Proxy로 항상 최신 _theme 반영)
const C = new Proxy({}, {
  get: (_, k) => (k in FIXED) ? FIXED[k] : _theme[k]
});

// ── UI 컴포넌트
const isDarkTheme=()=>["dark","darkgray","navy","slate"].includes(
  Object.keys(THEMES).find(k=>THEMES[k]===_theme)||"dark"
);

function Badge({children,color="blue"}){
  const isDark=isDarkTheme();
  const m={
    blue:  isDark ? [C.dim,   C.blueL]  : ["#dbeafe","#1d4ed8"],
    green: isDark ? ["#14532d","#4ade80"] : ["#dcfce7","#15803d"],
    amber: isDark ? ["#451a03",C.amberL] : ["#fef9c3","#a16207"],
    red:   isDark ? ["#450a0a",C.redL]   : ["#fee2e2","#b91c1c"],
    purple:isDark ? ["#2e1065",C.purpleL]: ["#f3e8ff","#7c3aed"],
    gray:  isDark ? ["#1f2937","#9ca3af"] : ["#f1f5f9","#475569"],
    teal:  isDark ? ["#042f2e",C.tealL]  : ["#ccfbf1","#0f766e"],
  };
  const [bg,fg]=m[color]||m.blue;
  return <span style={{background:bg,color:fg,fontSize:11,padding:"2px 9px",borderRadius:20,fontWeight:700,display:"inline-block"}}>{children}</span>;
}

function Btn({children,variant="primary",size="md",style:sx={},...props}){
  const isDark=isDarkTheme();
  const v={
    primary:{background:"linear-gradient(135deg,#3b82f6,#2563eb)",color:"#fff",border:"none"},
    secondary:{background:C.card,color:C.muted,border:`1px solid ${C.border}`},
    danger: isDark ? {background:"#450a0a",color:C.redL,border:"1px solid #7f1d1d"}
                   : {background:"#fee2e2",color:"#b91c1c",border:"1px solid #fca5a5"},
    success:{background:"linear-gradient(135deg,#16a34a,#15803d)",color:"#fff",border:"none"},
    ghost:{background:"transparent",color:C.slate,border:`1px solid ${C.border}`},
  };
  const s={sm:{fontSize:11,padding:"5px 12px"},md:{fontSize:13,padding:"8px 18px"},lg:{fontSize:15,padding:"12px 26px"}};
  return <button {...props} style={{borderRadius:9,fontWeight:700,cursor:"pointer",transition:"opacity .15s",...v[variant],...s[size],...sx}}>{children}</button>;
}

function Inp({label,...props}){
  return(
    <div style={{marginBottom:12}}>
      {label&&<label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>{label}</label>}
      <input {...props} style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box",...(props.style||{})}}/>
    </div>
  );
}

// 천단위 콤마 자동 표시 금액 입력 컴포넌트
function MoneyInp({label, value, onChange, placeholder, style, inputStyle}){
  const [raw, setRaw] = useState(value!=null&&value!==0?Number(value).toLocaleString("ko-KR"):"");
  useEffect(()=>{
    const num = Number(String(raw).replace(/,/g,""))||0;
    if(num !== Number(value)) setRaw(value!=null&&value!==0?Number(value).toLocaleString("ko-KR"):"");
  },[value]);
  const handleChange=(e)=>{
    const digits = e.target.value.replace(/[^0-9]/g,"");
    const num = parseInt(digits||"0",10);
    const formatted = num===0?"":num.toLocaleString("ko-KR");
    setRaw(formatted);
    onChange(num);
  };
  return(
    <div style={{marginBottom:12,...(style||{})}}>
      {label&&<label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>{label}</label>}
      <input
        type="text"
        value={raw}
        onChange={handleChange}
        placeholder={placeholder||"0"}
        style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box",textAlign:"right",...(inputStyle||{})}}
      />
    </div>
  );
}

function Sel({label,children,...props}){
  return(
    <div style={{marginBottom:12}}>
      {label&&<label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>{label}</label>}
      <select {...props} style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}>{children}</select>
    </div>
  );
}

function Card({children,style:sx={}}){
  return <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:22,...sx}}>{children}</div>;
}

function Modal({open,onClose,title,children,width=540}){
  if(!open)return null;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.78)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
      {/* 배경 클릭으로는 닫히지 않음 — 입력 중 데이터 유실 방지. 닫기는 ✕ 또는 취소 버튼으로만 가능 */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:28,width,maxWidth:"95vw",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <h2 style={{margin:0,fontSize:16,fontWeight:900,color:C.text}}>{title}</h2>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",lineHeight:1}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatCard({icon,label,value,sub,accent}){
  return(
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:13,padding:"16px 20px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:accent}}/>
      <div style={{fontSize:13,color:C.muted,marginBottom:4,fontWeight:700}}>{label}</div>
      <div style={{fontSize:24,fontWeight:900,color:C.text}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:C.slate,marginTop:2}}>{sub}</div>}
    </div>
  );
}

// ── 급여명세서 전용 페이지 (전체화면)
function PayslipPage({person,type,onClose,onSendLog,onUpdate}){
  const [sending,setSending]=useState(null);
  const [sent,setSent]=useState({email:false,sms:false});
  const [editing,setEditing]=useState(false);
  const [editPerson,setEditPerson]=useState(null);
  const [dedOverrides,setDedOverrides]=useState({}); // 공제 수기 조정값
  const [adjSaving,setAdjSaving]=useState(false);   // DB 저장 중
  const [adjLoaded,setAdjLoaded]=useState(false);   // DB 불러오기 완료 여부
  const [adjMemo,setAdjMemo]=useState("");           // 수기조정 메모

  // 명세서 열릴 때 DB에서 수기조정값 불러오기
  useEffect(()=>{
    if(!person||type!=="employee")return;
    api("GET",`/slip-adjustments/${person.id}/${encodeURIComponent(THIS_MONTH)}`)
      .then(data=>{
        if(data.adjustment){
          setDedOverrides({
            pension:     data.adjustment.pension,
            health:      data.adjustment.health,
            ltc:         data.adjustment.ltc,
            employ:      data.adjustment.employ,
            incomeTax:   data.adjustment.incomeTax,
            residentTax: data.adjustment.residentTax,
          });
          setAdjMemo(data.adjustment.memo||"");
        }
        setAdjLoaded(true);
      })
      .catch(()=>setAdjLoaded(true));
  },[person?.id]);

  if(!person)return null; // ※ 훅 이후에 위치 (React 훅 규칙 준수)
  const p2=editing&&editPerson?editPerson:person;
  const c=type==="employee"?calcEmployee(p2):calcInstructor(p2.invoiceAmount, p2);

  // ── 수기 조정된 공제 계산 (dedOverrides 우선, 없으면 자동 계산값 사용)
  const effDeds = {
    pension:     dedOverrides.pension     !== undefined ? dedOverrides.pension     : (c.pension     || 0),
    health:      dedOverrides.health      !== undefined ? dedOverrides.health      : (c.health      || 0),
    ltc:         dedOverrides.ltc         !== undefined ? dedOverrides.ltc         : (c.ltc         || 0),
    employ:      dedOverrides.employ      !== undefined ? dedOverrides.employ      : (c.employ      || 0),
    incomeTax:   dedOverrides.incomeTax   !== undefined ? dedOverrides.incomeTax   : (c.incomeTax   || 0),
    residentTax: dedOverrides.residentTax !== undefined ? dedOverrides.residentTax : (c.residentTax || 0),
  };
  const effTotalDeduct = effDeds.pension + effDeds.health + effDeds.ltc + effDeds.employ + effDeds.incomeTax + effDeds.residentTax + (c.extraDeduct||0);
  const effNetPay = c.gross - effTotalDeduct;
  const hasOverrides = Object.keys(dedOverrides).length > 0;

  const isEmp=type==="employee";

  // ── 수기조정값 DB 저장
  const saveAdjustment = async() => {
    if(!isEmp) return;
    setAdjSaving(true);
    try {
      await api("POST","/slip-adjustments",{
        empCode:     person.id,
        month:       THIS_MONTH,
        pension:     effDeds.pension,
        health:      effDeds.health,
        ltc:         effDeds.ltc,
        employ:      effDeds.employ,
        incomeTax:   effDeds.incomeTax,
        residentTax: effDeds.residentTax,
        baseSalary:  (editPerson||person).baseSalary,
        memo:        adjMemo,
      });
      alert("✅ 수기 조정값이 저장되었습니다.");
    } catch(e) {
      alert("❌ 저장 실패: "+e.message);
    } finally { setAdjSaving(false); }
  };

  // ── 수기조정값 DB 삭제(초기화)
  const resetAdjustment = async() => {
    if(!isEmp) return;
    try {
      await api("DELETE",`/slip-adjustments/${person.id}/${encodeURIComponent(THIS_MONTH)}`);
    } catch(e) {
      console.warn("조정값 DB 삭제 실패 (무시):", e.message);
    }
    // DB 저장 여부와 관계없이 화면 초기화
    setDedOverrides({});
    setAdjMemo("");
    alert("✅ 수기 조정값이 초기화되었습니다.");
  };

  const doSend=async(channel)=>{
    setSending(channel);
    // 편집 중이면 editPerson 사용, 없으면 원본 person 사용
    const sendPerson = (editing && editPerson) ? editPerson : person;
    const contact=channel==="email"?sendPerson.email:sendPerson.phone;
    try{
      // 수기 조정값이 있으면 조정된 slipData 생성
      const baseSlipData = makeSlipData(sendPerson);
      const adjustedSlipData = hasOverrides ? {
        ...baseSlipData,
        pension:     effDeds.pension,
        health:      effDeds.health,
        ltc:         effDeds.ltc,
        employ:      effDeds.employ,
        incomeTax:   effDeds.incomeTax,
        residentTax: effDeds.residentTax,
        totalDeduct: effTotalDeduct,
        netPay:      effNetPay,
      } : baseSlipData;
      await simulateSend(channel, sendPerson, THIS_MONTH, adjustedSlipData);
      onSendLog&&onSendLog({name:sendPerson.name,channel,contact,status:"성공",time:new Date().toLocaleTimeString("ko-KR")});
      setSent(p=>({...p,[channel]:true}));
      const adjNote = hasOverrides ? " (수기 조정 적용)" : "";
      alert(`✅ ${channel==="email"?"이메일":"SMS"} 발송 완료!${adjNote}\n수신: ${sendPerson.name} (${contact})`);
    }catch(e){
      onSendLog&&onSendLog({name:sendPerson.name,channel,contact,status:"실패",time:new Date().toLocaleTimeString("ko-KR"),error:e.message});
      alert(`❌ 발송 실패: ${e.message}`);
    }finally{setSending(null);}
  };

  const payItems = isEmp
    ? [{l:"기본급",v:person.baseSalary,note:""},...(person.allowances||[]).map(a=>({l:a.name,v:a.amount,note:a.nonTaxable?"비과세":""}))]
    : [{l:"청구금액",v:person.invoiceAmount,note:"사업소득"},...(person.allowances||[]).map(a=>({l:a.name,v:a.amount,note:a.nonTaxable?"비과세":""}))];

  const dedItems = isEmp
    ? [
        {l:"국민연금",   key:"pension",     rate:"4.75%",              v:effDeds.pension,     note:`기준소득 ${fmtW(c.penBasis)}`},
        {l:"건강보험",   key:"health",      rate:"3.595%",             v:effDeds.health,      note:""},
        {l:"장기요양보험",key:"ltc",         rate:"건강보험료×13.14%",   v:effDeds.ltc,         note:""},
        {l:"고용보험",   key:"employ",      rate:c.isExempt?"면제(임원)":"0.9%", v:effDeds.employ, note:c.isExempt?"대표이사/임원 면제":""},
        {l:"소득세",     key:"incomeTax",   rate:"간이세액표",           v:effDeds.incomeTax,   note:`부양가족 ${person.dependents??0}명`},
        {l:"주민세",     key:"residentTax", rate:"소득세×10%",           v:effDeds.residentTax, note:""},
        ...(person.deductions||[]).filter(d=>d.name&&d.amount>0).map(d=>({
          l:d.name, key:null, rate:"추가공제", v:d.amount, note:""
        })),
      ]
    : [
        {l:"소득세", key:"incomeTax",   rate:"3.0%",      v:effDeds.incomeTax,   note:"소득세법 제129조"},
        {l:"주민세", key:"residentTax", rate:"소득세×10%", v:effDeds.residentTax, note:"지방소득세"},
        ...(person.deductions||[]).filter(d=>d.name&&d.amount>0).map(d=>({l:d.name, key:null, rate:"추가공제", v:d.amount, note:""})),
      ];

  const totalPay=payItems.reduce((s,r)=>s+r.v,0);
  const deductRate=c.gross>0?((effTotalDeduct/c.gross)*100).toFixed(1):"0.0";

  // 인쇄용 테이블 공통 스타일
  const TH={background:"#1c3557",color:"#fff",fontWeight:700,fontSize:12,padding:"9px 14px",textAlign:"left"};
  const TD={fontSize:13,padding:"9px 14px",color:"#1a1a2e",borderBottom:"1px solid #e2e8f0"};
  const TDr={...TD,textAlign:"right",fontFamily:"'Courier New',monospace"};
  const TDc={...TD,textAlign:"center",color:"#475569"};

  return(
    <div style={{position:"fixed",inset:0,background:"#e8edf5",zIndex:500,overflowY:"auto",display:"flex",flexDirection:"column"}}>

      {/* ── 화면용 툴바 (인쇄 시 숨김) */}
      <div className="no-print" style={{background:"#1e293b",borderBottom:"1px solid #334155",padding:"0 28px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,position:"sticky",top:0,zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={onClose} style={{background:"#1d4ed8",border:"2px solid #3b82f6",borderRadius:8,padding:"7px 18px",color:"#ffffff",fontSize:13,fontWeight:800,cursor:"pointer",letterSpacing:"0.03em",boxShadow:"0 2px 8px rgba(59,130,246,0.4)"}}>← (목록으로) 돌아가기</button>
          <span style={{color:"#94a3b8",fontSize:13,fontWeight:500}}>명세서 미리보기</span>
          <span style={{color:"#ffffff",fontWeight:800,fontSize:14}}>{person.name}</span>
          <span style={{background:isEmp?"#1e3a8a":"#4c1d95",color:"#ffffff",fontSize:11,padding:"2px 8px",borderRadius:20,fontWeight:700}}>{isEmp?"직원":"강사"}</span>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {sent.email&&<span style={{fontSize:12,color:"#4ade80"}}>✓ 이메일 발송됨</span>}
          {sent.sms&&<span style={{fontSize:12,color:"#4ade80"}}>✓ SMS 발송됨</span>}
          {/* 수기 조정 DB 저장 버튼 */}
          {hasOverrides && !editing && isEmp && (
            <button onClick={saveAdjustment} disabled={adjSaving}
              style={{background:"#166534",color:"#4ade80",border:"1px solid #166534",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              {adjSaving?"저장중...":"💾 조정값 저장"}
            </button>
          )}
          {/* 수기 조정 초기화 버튼 */}
          {hasOverrides && !editing && isEmp && (
            <button onClick={resetAdjustment}
              style={{background:"#451a03",color:"#fbbf24",border:"1px solid #92400e",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              🔄 조정값 초기화
            </button>
          )}
          <button onClick={()=>{
            if(editing){
              // 수정 완료 — 변경된 데이터를 상위로 전달
              if(editPerson && onUpdate) onUpdate(editPerson);
              setEditing(false);
            } else {
              setEditPerson({...person});
              setDedOverrides({}); // 편집 시작 시 공제 수기값 초기화
              setEditing(true);
            }
          }} style={{background:editing?"#16a34a":"#1d4ed8",color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
            {editing?"✅ 수정 완료":"✏️ 금액 수기 조정"}
          </button>
          <Btn variant="primary" onClick={()=>doSend("sms")} disabled={!!sending}>
            {sending==="sms"?"발송중...":"SMS 발송"}
          </Btn>
          <Btn variant="success" onClick={()=>doSend("email")} disabled={!!sending}>
            {sending==="email"?"발송중...":"이메일 발송"}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={()=>window.print()}>인쇄 / PDF 저장</Btn>
        </div>
      </div>

      {/* ── 인쇄 용지 영역 */}
      <div style={{flex:1,display:"flex",justifyContent:"center",padding:"28px 20px 40px"}}>
        <div style={{width:680,background:"#fff",boxShadow:"0 4px 32px rgba(0,0,0,0.18)",borderRadius:4,overflow:"hidden",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',Arial,sans-serif"}}>
          {/* 수기 조정 안내 배너 */}
          {(editing || hasOverrides) && (
            <div className="no-print" style={{background: editing?"#1e3a8a":"#92400e",padding:"10px 20px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:16}}>{editing?"✏️":"⚠️"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:800,color:"#fff"}}>
                  {editing?"수기 조정 모드 — 금액을 직접 입력하세요":"수기 조정값 적용됨 — 발송 시 조정된 금액이 전송됩니다"}
                </div>
                <div style={{fontSize:11,color:editing?"#93c5fd":"#fde68a",marginTop:2}}>
                  {editing
                    ?"지급 항목(초록)·공제 항목(빨강) 입력란에 원하는 금액을 입력한 후 ✅ 수정 완료를 눌러주세요"
                    :`조정된 공제 항목: ${Object.keys(dedOverrides).join(", ")} | 실수령액: ${fmtW(effNetPay)}`}
                </div>
              </div>
              {hasOverrides && !editing && (
                <button onClick={()=>setDedOverrides({})}
                  style={{background:"rgba(0,0,0,0.3)",color:"#fde68a",border:"1px solid #fde68a",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  초기화
                </button>
              )}
            </div>
          )}

          {/* 명세서 상단 헤더 */}
          <div style={{background:"linear-gradient(135deg,#1c3557 0%,#1e40af 100%)",padding:"28px 36px 24px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",right:-30,top:-30,width:180,height:180,borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
            <div style={{position:"absolute",right:60,bottom:-40,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.04)"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",position:"relative"}}>
              <div>
                <div style={{fontSize:10,color:"#93c5fd",fontWeight:700,letterSpacing:"0.15em",marginBottom:6}}>{isEmp?"EMPLOYEE PAY SLIP":"INSTRUCTOR PAYMENT SLIP"}</div>
                <div style={{fontSize:26,fontWeight:900,color:"#fff",letterSpacing:"-0.5px",lineHeight:1.1}}>급 여 명 세 서</div>
                <div style={{fontSize:13,color:"#bfdbfe",marginTop:6,fontWeight:500}}>{THIS_MONTH} 분 지급</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:"#93c5fd",marginBottom:3}}>발행일</div>
                <div style={{fontSize:14,color:"#fff",fontWeight:700}}>{new Date().toLocaleDateString("ko-KR")}</div>
                <div style={{marginTop:10,fontSize:11,color:"#93c5fd"}}>문서번호</div>
                <div style={{fontSize:12,color:"#dbeafe",fontWeight:600,fontFamily:"monospace"}}>PAY-{new Date().getFullYear()}{String(new Date().getMonth()+1).padStart(2,"0")}-{person.id}</div>
              </div>
            </div>
          </div>

          <div style={{padding:"0 36px 32px"}}>

            {/* ① 인적사항 표 */}
            <div style={{marginTop:24,marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:3,height:13,background:"#1e40af",borderRadius:2}}/>
                인적사항
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                <tbody>
                  <tr>
                    <td style={{...TH,width:"16%",borderRight:"1px solid #2d5494"}}>성명</td>
                    <td style={{...TD,width:"34%",borderRight:"1px solid #e2e8f0",fontWeight:700}}>{person.name}</td>
                    <td style={{...TH,width:"16%",borderRight:"1px solid #2d5494"}}>{isEmp?"사원번호":"강사번호"}</td>
                    <td style={{...TD,width:"34%",fontFamily:"monospace"}}>{person.id}</td>
                  </tr>
                  <tr>
                    <td style={{...TH,borderRight:"1px solid #2d5494"}}>{isEmp?"부서":"강의과목"}</td>
                    <td style={{...TD,borderRight:"1px solid #e2e8f0"}}>{isEmp?person.dept:person.subject}</td>
                    <td style={{...TH,borderRight:"1px solid #2d5494"}}>{isEmp?"직급":"소득유형"}</td>
                    <td style={{...TD}}>{isEmp?person.position:"사업소득 (3.3%)"}</td>
                  </tr>
                  <tr>
                    <td style={{...TH,borderRight:"1px solid #2d5494"}}>연락처</td>
                    <td style={{...TD,borderRight:"1px solid #e2e8f0"}}>{person.phone||"—"}</td>
                    <td style={{...TH,borderRight:"1px solid #2d5494"}}>이메일</td>
                    <td style={{...TD,fontSize:12}}>{person.email||"—"}</td>
                  </tr>
                  {isEmp&&(
                    <tr>
                      <td style={{...TH,borderRight:"1px solid #2d5494",borderBottom:"none"}}>생년월일</td>
                      <td style={{...TD,borderRight:"1px solid #e2e8f0",borderBottom:"none"}}>{person.birthDate||"—"}</td>
                      <td style={{...TH,borderRight:"1px solid #2d5494",borderBottom:"none"}}>부양가족</td>
                      <td style={{...TD,borderBottom:"none"}}>{person.dependents??0}명</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ② 지급 내역 표 */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:3,height:13,background:"#16a34a",borderRadius:2}}/>
                지급 내역
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                <thead>
                  <tr>
                    <th style={{...TH,width:"50%",borderRight:"1px solid #2d5494"}}>지급 항목</th>
                    <th style={{...TH,width:"20%",textAlign:"center",borderRight:"1px solid #2d5494"}}>비고</th>
                    <th style={{...TH,width:"30%",textAlign:"right"}}>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {payItems.map((r,i)=>(
                    <tr key={i}>
                      <td style={{...TD,borderRight:"1px solid #e2e8f0"}}>{r.l}</td>
                      <td style={{...TDc,borderRight:"1px solid #e2e8f0"}}>
                        {r.note&&<span style={{background:"#f0fdf4",color:"#15803d",fontSize:10,padding:"1px 7px",borderRadius:10,fontWeight:700}}>{r.note}</span>}
                      </td>
                      <td style={{...TDr,color:"#15803d",fontWeight:700}}>
                        {editing && isEmp ? (
                          <input
                            type="text" value={Number(r.v).toLocaleString("ko-KR")} min={0}
                            onChange={e=>{
                              const val = Math.max(0, parseInt(e.target.value.replace(/,/g,"")||"0",10));
                              if(i===0) setEditPerson(p=>({...p,baseSalary:val}));
                              else setEditPerson(p=>({...p,allowances:p.allowances.map((a,ai)=>ai===i-1?{...a,amount:val}:a)}));
                            }}
                            style={{width:"100%",textAlign:"right",border:"2px solid #86efac",borderRadius:5,padding:"4px 8px",fontSize:13,background:"#f0fdf4",color:"#15803d",fontWeight:700,outline:"none",boxSizing:"border-box"}}
                          />
                        ) : editing && !isEmp && i===0 ? (
                          <input
                            type="text" value={Number(r.v).toLocaleString("ko-KR")} min={0}
                            onChange={e=>{
                              const val = Math.max(0, parseInt(e.target.value.replace(/,/g,"")||"0",10));
                              setEditPerson(p=>({...p,invoiceAmount:val}));
                            }}
                            style={{width:"100%",textAlign:"right",border:"2px solid #86efac",borderRadius:5,padding:"4px 8px",fontSize:13,background:"#f0fdf4",color:"#15803d",fontWeight:700,outline:"none",boxSizing:"border-box"}}
                          />
                        ) : fmtW(r.v)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{background:"#f0fdf4"}}>
                    <td style={{...TD,fontWeight:800,color:"#14532d",borderRight:"1px solid #e2e8f0",borderBottom:"none"}}>지급 합계</td>
                    <td style={{...TDc,borderRight:"1px solid #e2e8f0",borderBottom:"none"}}/>
                    <td style={{...TDr,fontWeight:900,fontSize:13,color:"#15803d",borderBottom:"none",whiteSpace:"nowrap"}}>{fmtW(totalPay)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ③ 공제 내역 표 */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:3,height:13,background:"#dc2626",borderRadius:2}}/>
                공제 내역
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1",tableLayout:"fixed"}}>
                <thead>
                  <tr>
                    <th style={{...TH,width:"32%",borderRight:"1px solid #2d5494"}}>공제 항목</th>
                    <th style={{...TH,width:"22%",textAlign:"center",borderRight:"1px solid #2d5494"}}>적용 요율</th>
                    <th style={{...TH,width:"22%",textAlign:"center",borderRight:"1px solid #2d5494"}}>산출 기준</th>
                    <th style={{...TH,width:"24%",textAlign:"right"}}>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {dedItems.map((r,i)=>(
                    <tr key={i} style={{background: dedOverrides[r.key]!==undefined ? "#fffbeb" : "transparent"}}>
                      <td style={{...TD,borderRight:"1px solid #e2e8f0"}}>
                        {r.l}
                        {!editing && dedOverrides[r.key]!==undefined && (
                          <span style={{fontSize:9,color:"#d97706",fontWeight:900,marginLeft:5,background:"#fef3c7",padding:"1px 5px",borderRadius:8}}>수정됨</span>
                        )}
                      </td>
                      <td style={{...TDc,borderRight:"1px solid #e2e8f0"}}>{r.rate}</td>
                      <td style={{...TDc,fontSize:11,color:"#64748b",borderRight:"1px solid #e2e8f0"}}>{r.note||"—"}</td>
                      <td style={{...TDr,color:"#b91c1c",fontWeight:700}}>
                        {editing ? (
                          <input
                            type="text" value={Number(r.v).toLocaleString("ko-KR")} min={0}
                            onChange={e=>{
                              const val = Math.max(0, parseInt(e.target.value.replace(/,/g,"")||"0",10));
                              setDedOverrides(p=>({...p,[r.key]:val}));
                            }}
                            style={{width:"100%",textAlign:"right",border:"2px solid #fca5a5",borderRadius:5,padding:"4px 8px",fontSize:13,background:"#fff5f5",color:"#b91c1c",fontWeight:700,outline:"none",boxSizing:"border-box"}}
                          />
                        ) : fmtW(r.v)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{background:"#fff5f5"}}>
                    <td style={{...TD,fontWeight:800,color:"#7f1d1d",borderRight:"1px solid #e2e8f0",borderBottom:"none"}}>공제 합계</td>
                    <td style={{...TDc,borderRight:"1px solid #e2e8f0",borderBottom:"none",color:"#94a3b8",fontSize:11}}>공제율 {deductRate}%</td>
                    <td style={{...TDc,borderRight:"1px solid #e2e8f0",borderBottom:"none"}}/>
                    <td style={{...TDr,fontWeight:900,fontSize:13,color:"#b91c1c",borderBottom:"none",whiteSpace:"nowrap"}}>{fmtW(effTotalDeduct)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ④ 실수령액 강조 박스 */}
            <div style={{background:"linear-gradient(135deg,#1c3557,#1e40af)",borderRadius:6,padding:"18px 28px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:isEmp?20:0}}>
              <div>
                <div style={{fontSize:11,color:"#93c5fd",fontWeight:700,marginBottom:4,letterSpacing:"0.1em"}}>
                  NET PAY · 실수령액
                  {hasOverrides && <span style={{fontSize:9,marginLeft:6,background:"#d97706",color:"#fff",padding:"1px 6px",borderRadius:8,fontWeight:900}}>수기조정</span>}
                </div>
                <div style={{fontSize:11,color:"#93c5fd"}}>지급합계 {fmtW(totalPay)} − 공제합계 {fmtW(effTotalDeduct)}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:32,fontWeight:900,color:"#fff",letterSpacing:"-1px",fontFamily:"'Courier New',monospace"}}>{fmtW(effNetPay)}</div>
                <div style={{fontSize:11,color:"#bfdbfe",marginTop:2}}>세후 수령 비율 {(100-Number(deductRate)).toFixed(1)}%</div>
              </div>
            </div>

            {/* ⑤ 회사 부담 보험료 (직원만) */}
            {isEmp&&(
              <div style={{marginBottom:0}}>
                <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-block",width:3,height:13,background:"#d97706",borderRadius:2}}/>
                  회사 부담 보험료 (참고 — 공제 항목 아님)
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                  <thead>
                    <tr>
                      {["국민연금 (4.75%)","건강보험 (3.595%)","장기요양","고용보험 (1.15%)"].map(h=>(
                        <th key={h} style={{background:"#fefce8",color:"#78350f",fontSize:11,fontWeight:700,padding:"8px 12px",textAlign:"center",borderRight:"1px solid #fde68a",borderBottom:"1px solid #fde68a"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {[
                        Math.round(c.penBasis*RATES.pension.er),
                        Math.round(c.taxable*RATES.health.er),
                        Math.round(Math.round(c.taxable*RATES.health.er)*RATES.ltc.rate),
                        c.isExempt?0:c.isExempt?0:c.isExempt?0:Math.round(c.taxable*RATES.employ.er),
                      ].map((v,i)=>(
                        <td key={i} style={{padding:"9px 12px",textAlign:"center",fontFamily:"monospace",fontWeight:700,color:"#92400e",fontSize:13,borderRight:i<3?"1px solid #e2e8f0":"none",background:"#fffbeb"}}>{fmtW(v)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* 하단 안내문 */}
            <div style={{marginTop:24,paddingTop:16,borderTop:"1px dashed #cbd5e1",fontSize:11,color:"#94a3b8",lineHeight:1.8}}>
              <div>• 본 명세서는 {THIS_MONTH} 분 급여 지급 내역을 기준으로 작성되었습니다.</div>
              <div>• {isEmp?"4대보험료는 2025년 적용 요율 기준입니다.":"원천징수액은 소득세법 제129조 제1항 제3호에 의거 사업소득의 3.3%입니다."}</div>
              <div>• 문의사항은 인사팀(☎ 02-2633-1184)으로 연락하시기 바랍니다.</div>
            </div>

          </div>{/* /padding */}
        </div>{/* /white paper */}
      </div>{/* /centering */}

      {/* ── 화면용 발송 패널 (인쇄 시 숨김) */}
      <div className="no-print" style={{background:"#1e293b",borderTop:"1px solid #334155",padding:"14px 28px",display:"flex",gap:16,alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:12,color:"#64748b"}}>
          {((editing&&editPerson)||person).email||"이메일 없음"}　　{((editing&&editPerson)||person).phone||"연락처 없음"}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="success" onClick={()=>doSend("email")} disabled={!!sending||sent.email}>
            {sending==="email"?"발송중...":sent.email?"✅ 이메일 발송완료":"이메일 발송"}
          </Btn>
          <Btn variant="primary" onClick={()=>doSend("sms")} disabled={!!sending||sent.sms}>
            {sending==="sms"?"발송중...":sent.sms?"✅ SMS 발송완료":"SMS 발송"}
          </Btn>
        </div>
      </div>

    </div>
  );
}

// ── 직원 폼
function EmpForm({open,onClose,initial,onSave,suggestedId}){
  const empty={id:"",name:"",dept:"",position:"",baseSalary:0,dependents:0,phone:"",email:"",birthDate:"",allowances:[],deductions:[],status:"active",taxExempt:false};
  const [f,setF]=useState(empty);
  useEffect(()=>{
    if(initial){ setF({...empty,...initial}); }
    else{ setF({...empty,id:suggestedId||""}); }
  },[open,initial,suggestedId]);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const setA=(i,k,v)=>setF(p=>{const a=[...p.allowances];a[i]={...a[i],[k]:k==="amount"?Number(v):v};return{...p,allowances:a};});
  const setD=(i,k,v)=>setF(p=>({...p,deductions:(p.deductions||[]).map((d,j)=>j===i?{...d,[k]:v}:d)}));
  return(
    <Modal open={open} onClose={onClose} title={initial?"직원 정보 수정":"직원 등록"} width={570}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
        <Inp label="사원번호" value={f.id} onChange={e=>set("id",e.target.value)} placeholder="HC005"/>
        <Inp label="성명" value={f.name} onChange={e=>set("name",e.target.value)}/>
        <Inp label="부서" value={f.dept} onChange={e=>set("dept",e.target.value)}/>
        <Inp label="직급" value={f.position} onChange={e=>set("position",e.target.value)}/>
        <MoneyInp label="기본급" value={f.baseSalary} onChange={v=>set("baseSalary",v)}/>
        <Inp label="부양가족 수" type="number" value={f.dependents} onChange={e=>set("dependents",Number(e.target.value))}/>
        <div style={{marginBottom:12}}>
          <label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>생년월일</label>
          <input
            type="text"
            value={f.birthDate||""}
            placeholder="19901231"
            onFocus={e=>{ if(f.birthDate) set("birthDate",""); }}
            style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}
            onChange={e=>{
              const digits=e.target.value.replace(/[^0-9]/g,"").slice(0,8);
              let formatted=digits;
              if(digits.length>4) formatted=digits.slice(0,4)+"-"+digits.slice(4);
              if(digits.length>6) formatted=digits.slice(0,4)+"-"+digits.slice(4,6)+"-"+digits.slice(6);
              set("birthDate",formatted);
            }}
          />
          <div style={{fontSize:10,color:C.slate,marginTop:3}}>숫자만 입력하면 자동으로 형식이 맞춰집니다 (예: 20200302)</div>
        </div>
        <Sel label="재직상태" value={f.status} onChange={e=>set("status",e.target.value)}><option value="active">재직</option><option value="leave">휴직</option><option value="resigned">퇴직</option></Sel>
        <Inp label="연락처 *" value={f.phone} onChange={e=>set("phone",formatPhone(e.target.value))} placeholder="010-0000-0000"/>
        <Inp label="이메일 *" value={f.email} onChange={e=>set("email",e.target.value)}/>
      </div>
      {/* 소득세/주민세 면제 설정 */}
      <div style={{background:f.taxExempt?"#451a03":"#1e293b",border:`2px solid ${f.taxExempt?"#f59e0b":"#475569"}`,borderRadius:8,padding:"14px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:12,minHeight:56}}>
        <input type="checkbox" id="taxExempt" checked={!!f.taxExempt} onChange={e=>set("taxExempt",e.target.checked)}
          style={{width:18,height:18,cursor:"pointer",flexShrink:0,accentColor:"#f59e0b"}}/>
        <label htmlFor="taxExempt" style={{cursor:"pointer",flex:1,display:"flex",flexDirection:"column",justifyContent:"center"}}>
          <div style={{fontSize:13,fontWeight:800,color:f.taxExempt?"#fbbf24":"#e2e8f0"}}>소득세 · 주민세 면제</div>
          <div style={{fontSize:11,color:f.taxExempt?"#fde68a":"#94a3b8",marginTop:3}}>체크 시 소득세와 주민세가 공제되지 않습니다 (임원, 외국인 등)</div>
        </label>
        {f.taxExempt&&<span style={{fontSize:11,background:"#f59e0b",color:"#1c1410",padding:"3px 10px",borderRadius:8,fontWeight:800,flexShrink:0}}>면제 적용중</span>}
      </div>
      {/* 수당 항목 */}
      <div style={{marginBottom:14,border:`2px solid ${C.border2}`,borderRadius:10,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <label style={{fontSize:12,color:C.text,fontWeight:800}}>💰 수당 항목</label>
          <button onClick={()=>setF(p=>({...p,allowances:[...p.allowances,{name:"",amount:0,nonTaxable:false}]}))}
            style={{background:"none",border:`1px dashed ${C.border2}`,borderRadius:7,color:C.muted,fontSize:11,padding:"4px 12px",cursor:"pointer",fontWeight:700}}>
            + 수당 추가
          </button>
        </div>
        {f.allowances.length===0&&(
          <div style={{fontSize:11,color:C.muted,textAlign:"center",padding:"8px 0"}}>수당 항목이 없습니다. 추가 버튼을 눌러주세요.</div>
        )}
        {f.allowances.map((a,i)=>(
          <div key={i} style={{display:"flex",gap:7,marginTop:7,alignItems:"center"}}>
            <input value={a.name} onChange={e=>setA(i,"name",e.target.value)} placeholder="수당명" style={{flex:1,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}}/>
            <MoneyInp value={a.amount} onChange={v=>setA(i,"amount",v)} style={{marginBottom:0,width:120}} inputStyle={{padding:"7px 10px",fontSize:12}}/>
            <label style={{display:"flex",alignItems:"center",gap:3,fontSize:11,color:C.muted,whiteSpace:"nowrap"}}><input type="checkbox" checked={!!a.nonTaxable} onChange={e=>setA(i,"nonTaxable",e.target.checked)}/> 비과세</label>
            <Btn variant="danger" size="sm" onClick={()=>setF(p=>({...p,allowances:p.allowances.filter((_,j)=>j!==i)}))}>삭제</Btn>
          </div>
        ))}
      </div>

      {/* 공제 항목 */}
      <div style={{marginBottom:14,border:`2px solid #7f1d1d`,borderRadius:10,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <label style={{fontSize:12,color:C.text,fontWeight:800}}>➖ 공제 항목 (추가 공제)</label>
          <button onClick={()=>setF(p=>({...p,deductions:[...(p.deductions||[]),{name:"",amount:0}]}))}
            style={{background:"none",border:"1px dashed #7f1d1d",borderRadius:7,color:C.redL,fontSize:11,padding:"4px 12px",cursor:"pointer",fontWeight:700}}>
            + 공제 추가
          </button>
        </div>
        {(f.deductions||[]).length===0&&(
          <div style={{fontSize:11,color:C.muted,textAlign:"center",padding:"8px 0"}}>추가 공제 항목이 없습니다. 추가 버튼을 눌러주세요.</div>
        )}
        {(f.deductions||[]).map((d,i)=>(
          <div key={i} style={{display:"flex",gap:7,marginTop:7,alignItems:"center"}}>
            <input value={d.name} onChange={e=>setD(i,"name",e.target.value)} placeholder="공제명" style={{flex:1,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}}/>
            <MoneyInp value={d.amount} onChange={v=>setD(i,"amount",v)} style={{marginBottom:0,width:120}} inputStyle={{padding:"7px 10px",fontSize:12}}/>
            <Btn variant="danger" size="sm" onClick={()=>setF(p=>({...p,deductions:(p.deductions||[]).filter((_,j)=>j!==i)}))}>삭제</Btn>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:10,marginTop:28}}>
        <Btn variant="primary" style={{flex:1}} onClick={async ()=>{
          if(!f.id||!f.id.trim()){
            alert("사원번호를 입력해주세요.");return;
          }
          if(!f.phone||f.phone==="010-0000-0000"||f.phone.trim()===""){
            alert("연락처는 필수 입력 항목입니다.");return;
          }
          if(!f.email||f.email.trim()===""){
            alert("이메일은 필수 입력 항목입니다.");return;
          }
          if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)){
            alert("이메일 형식이 올바르지 않습니다.");return;
          }
          const ok=await onSave(f);
          if(ok!==false) onClose();
        }}>저장</Btn>
        <Btn variant="secondary" style={{flex:1}} onClick={onClose}>취소</Btn>
      </div>
    </Modal>
  );
}

// ── 강사 폼
function InsForm({open,onClose,initial,onSave,suggestedId}){
  const empty={id:"",name:"",subject:"",phone:"",email:"",invoiceAmount:0,status:"active",birthdate:"",allowances:[],deductions:[]};
  const [f,setF]=useState(empty);
  useEffect(()=>{
    if(initial){ setF({...empty,...initial,allowances:initial.allowances||[],deductions:initial.deductions||[]}); }
    else{ setF({...empty,id:suggestedId||""}); }
  },[open, initial, suggestedId]);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const setA=(i,k,v)=>setF(p=>{const a=[...p.allowances];a[i]={...a[i],[k]:k==="amount"?Number(v):v};return{...p,allowances:a};});
  const setD=(i,k,v)=>setF(p=>({...p,deductions:(p.deductions||[]).map((d,j)=>j===i?{...d,[k]:v}:d)}));
  const isEdit=!!initial;

  return(
    <Modal open={open} onClose={onClose} title={isEdit?"강사 정보 수정":"강사 등록"} width={520}>
      {isEdit&&(
        <div style={{background:C.dim,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:C.muted,display:"flex",alignItems:"center",gap:8}}>
          ✏️ <strong style={{color:C.amberL}}>{initial.name}</strong> 강사 정보를 수정합니다 · 강사번호 변경 불가
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
        <div style={{marginBottom:12}}>
          <label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>강사번호 {isEdit&&<span style={{color:C.slate,fontWeight:400}}>(변경 불가)</span>}</label>
          <input value={f.id} readOnly={isEdit}
            onChange={e=>!isEdit&&set("id",e.target.value)}
            placeholder="I004"
            style={{width:"100%",background:isEdit?C.thead:C.inputBg,border:`1px solid ${isEdit?C.border:C.border2}`,borderRadius:8,padding:"8px 11px",color:isEdit?C.slate:C.text,fontSize:13,outline:"none",boxSizing:"border-box",cursor:isEdit?"not-allowed":"text"}}/>
        </div>
        <Inp label="성명" value={f.name} onChange={e=>set("name",e.target.value)}/>
        <Inp label="강의과목" value={f.subject} onChange={e=>set("subject",e.target.value)}/>
        <Inp label="연락처 *" value={f.phone} onChange={e=>set("phone",formatPhone(e.target.value))}/>
        <Inp label="이메일 *" value={f.email} onChange={e=>set("email",e.target.value)}/>
        <div style={{marginBottom:12,gridColumn:"1/-1"}}>
          <div style={{background:C.dim,border:`1px solid ${C.border2}`,borderRadius:8,padding:"9px 14px",fontSize:12,color:C.muted,display:"flex",alignItems:"center",gap:8}}>
            <span>청구금액은 강사가 <strong style={{color:C.amberL}}>청구서를 제출</strong>하면 <strong style={{color:C.greenL}}>강의시간 × 단가</strong>로 자동 계산됩니다.</span>
          </div>
        </div>
        <Sel label="상태" value={f.status} onChange={e=>set("status",e.target.value)}>
          <option value="active">활동중</option>
          <option value="inactive">비활동</option>
        </Sel>
        <div style={{marginBottom:12}}>
          <label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>
            생년월일 <span style={{color:C.slate,fontWeight:400}}>(로그인용 · yyyy-mm-dd)</span>
          </label>
          <input type="text" value={f.birthdate||""}
            onChange={e=>{
              const digits=e.target.value.replace(/[^0-9]/g,"").slice(0,8);
              let formatted=digits;
              if(digits.length>4) formatted=digits.slice(0,4)+"-"+digits.slice(4);
              if(digits.length>6) formatted=digits.slice(0,4)+"-"+digits.slice(4,6)+"-"+digits.slice(6);
              set("birthdate",formatted);
            }}
            placeholder="1990-05-14" maxLength={10}
            style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"monospace",letterSpacing:"0.05em"}}/>
          <div style={{fontSize:10,color:C.slate,marginTop:3}}>숫자만 입력하면 자동으로 형식이 맞춰집니다 (예: 19900514)</div>
        </div>
      </div>

      {/* 수당 항목 */}
      <div style={{marginBottom:14,border:`2px solid ${C.border2}`,borderRadius:10,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <label style={{fontSize:12,color:C.text,fontWeight:800}}>💰 수당 항목</label>
          <button onClick={()=>setF(p=>({...p,allowances:[...p.allowances,{name:"",amount:0,nonTaxable:false}]}))}
            style={{background:"none",border:`1px dashed ${C.border2}`,borderRadius:7,color:C.muted,fontSize:11,padding:"4px 12px",cursor:"pointer",fontWeight:700}}>
            + 수당 추가
          </button>
        </div>
        {f.allowances.length===0&&(
          <div style={{fontSize:11,color:C.muted,textAlign:"center",padding:"8px 0"}}>수당 항목이 없습니다. 추가 버튼을 눌러주세요.</div>
        )}
        {f.allowances.map((a,i)=>(
          <div key={i} style={{display:"flex",gap:7,marginTop:7,alignItems:"center"}}>
            <input value={a.name} onChange={e=>setA(i,"name",e.target.value)} placeholder="수당명"
              style={{flex:1,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}}/>
            <MoneyInp value={a.amount} onChange={v=>setA(i,"amount",v)} style={{marginBottom:0,width:120}} inputStyle={{padding:"7px 10px",fontSize:12}}/>
            <label style={{display:"flex",alignItems:"center",gap:3,fontSize:11,color:C.muted,whiteSpace:"nowrap"}}>
              <input type="checkbox" checked={!!a.nonTaxable} onChange={e=>setA(i,"nonTaxable",e.target.checked)}/> 비과세
            </label>
            <Btn variant="danger" size="sm" onClick={()=>setF(p=>({...p,allowances:p.allowances.filter((_,j)=>j!==i)}))}>삭제</Btn>
          </div>
        ))}
      </div>

      {/* 공제 항목 */}
      <div style={{marginBottom:14,border:"2px solid #7f1d1d",borderRadius:10,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <label style={{fontSize:12,color:C.text,fontWeight:800}}>➖ 공제 항목 (추가 공제)</label>
          <button onClick={()=>setF(p=>({...p,deductions:[...(p.deductions||[]),{name:"",amount:0}]}))}
            style={{background:"none",border:"1px dashed #7f1d1d",borderRadius:7,color:C.redL,fontSize:11,padding:"4px 12px",cursor:"pointer",fontWeight:700}}>
            + 공제 추가
          </button>
        </div>
        {(f.deductions||[]).length===0&&(
          <div style={{fontSize:11,color:C.muted,textAlign:"center",padding:"8px 0"}}>추가 공제 항목이 없습니다. 추가 버튼을 눌러주세요.</div>
        )}
        {(f.deductions||[]).map((d,i)=>(
          <div key={i} style={{display:"flex",gap:7,marginTop:7,alignItems:"center"}}>
            <input value={d.name} onChange={e=>setD(i,"name",e.target.value)} placeholder="공제명"
              style={{flex:1,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 10px",color:C.text,fontSize:12,outline:"none"}}/>
            <MoneyInp value={d.amount} onChange={v=>setD(i,"amount",v)} style={{marginBottom:0,width:120}} inputStyle={{padding:"7px 10px",fontSize:12}}/>
            <Btn variant="danger" size="sm" onClick={()=>setF(p=>({...p,deductions:(p.deductions||[]).filter((_,j)=>j!==i)}))}>삭제</Btn>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:10,marginTop:6}}>
        <Btn variant="primary" style={{flex:1}} onClick={async ()=>{
          if(!f.id||!f.id.trim()){
            alert("강사번호를 입력해주세요.");return;
          }
          if(!f.phone||f.phone.trim()===""){
            alert("연락처는 필수 입력 항목입니다.");return;
          }
          if(!f.email||f.email.trim()===""){
            alert("이메일은 필수 입력 항목입니다.");return;
          }
          if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)){
            alert("이메일 형식이 올바르지 않습니다.");return;
          }
          const ok=await onSave(f);
          if(ok!==false) onClose();
        }}>{isEdit?"수정 저장":"등록"}</Btn>
        <Btn variant="secondary" style={{flex:1}} onClick={onClose}>취소</Btn>
      </div>
    </Modal>
  );
}

// ── Step 4 발송 코드
const SEND_CODE={
  sendgrid:`// ── SendGrid 이메일 발송 (Node.js)
// npm install @sendgrid/mail

const sgMail = require('@sendgrid/mail');
sgMail.setApiKey(process.env.SENDGRID_API_KEY);

async function sendPayslipEmail(employee, payslip) {
  const msg = {
    to: employee.email,
    from: { email: 'payroll@ehancom.com', name: '한컴캠퍼스 급여팀' },
    subject: \`[\${payslip.year}년 \${payslip.month}월] \${employee.name}님 급여명세서\`,
    html: generatePayslipHTML(employee, payslip),
  };
  try {
    const [res] = await sgMail.send(msg);
    await saveLog({ channel:'email', status:'sent', contact:employee.email });
    return { ok: true, statusCode: res.statusCode };
  } catch (err) {
    await saveLog({ channel:'email', status:'failed', error:err.message });
    throw err;
  }
}`,
  sms:`// ── 알리고 SMS 발송 (Node.js)
// npm install axios form-data

const axios = require('axios');
const FormData = require('form-data');

async function sendPayslipSMS(person, payslip) {
  const message =
    \`[급여명세서] \${payslip.year}년 \${payslip.month}월\\n\` +
    \`성명: \${person.name}\\n\` +
    \`총지급: \${payslip.gross.toLocaleString()}원\\n\` +
    \`공제액: \${payslip.totalDeduct.toLocaleString()}원\\n\` +
    \`실수령: \${payslip.netPay.toLocaleString()}원\`;

  const form = new FormData();
  form.append('key',     process.env.ALIGO_API_KEY);
  form.append('user_id', process.env.ALIGO_USER_ID);
  form.append('sender',  '0200000000');
  form.append('receiver', person.phone.replace(/-/g,''));
  form.append('msg',     message);

  const res = await axios.post('https://apis.aligo.in/send/', form,
    { headers: form.getHeaders() });
  if (res.data.result_code !== '1')
    throw new Error(\`알리고 오류: \${res.data.message}\`);
  return { ok: true, msgId: res.data.msg_id };
}`,
  scheduler:`// ── 월별 자동 일괄 발송 스케줄러
// npm install node-cron

const cron = require('node-cron');

// 매월 25일 오전 9시 자동 발송 (Asia/Seoul)
cron.schedule('0 9 25 * *', async () => {
  const year  = new Date().getFullYear();
  const month = new Date().getMonth() + 1;

  // 확정된 급여 처리 회차 조회
  const run = await db.query(
    \`SELECT * FROM payroll_runs
     WHERE pay_year=$1 AND pay_month=$2
       AND status='confirmed'\`, [year, month]
  );
  if (!run.rows.length) return;

  // 직원 명세서 발송
  const emps = await db.query(
    \`SELECT ep.*, e.name, e.email, e.phone
     FROM employee_payslips ep
     JOIN employees e USING(emp_id)
     WHERE ep.run_id = $1\`, [run.rows[0].run_id]
  );
  for (const row of emps.rows) {
    await sendPayslipEmail(row, row);
    await sendPayslipSMS(row, row);
    await new Promise(r => setTimeout(r, 200)); // 과부하 방지
  }
}, { timezone: 'Asia/Seoul' });`,
  retry:`// ── 발송 실패 자동 재시도 (30분마다)
cron.schedule('*/30 * * * *', async () => {
  const failed = await db.query(
    \`SELECT * FROM notification_logs
     WHERE status = 'failed' AND retry_count < 3
     ORDER BY created_at\`
  );
  for (const log of failed.rows) {
    await db.query(
      \`UPDATE notification_logs
       SET status='retrying', retry_count=retry_count+1
       WHERE log_id=$1\`, [log.log_id]
    );
    try {
      if (log.channel === 'email') await sendPayslipEmail(/*...*/);
      else await sendPayslipSMS(/*...*/);
      await db.query(
        \`UPDATE notification_logs
         SET status='sent', sent_at=NOW()
         WHERE log_id=$1\`, [log.log_id]
      );
    } catch(e) {
      await db.query(
        \`UPDATE notification_logs
         SET status='failed', error_message=$2
         WHERE log_id=$1\`, [log.log_id, e.message]
      );
    }
  }
});`
};

function CodeBlock({code,lang="Node.js"}){
  const [copied,setCopied]=useState(false);
  const copy=()=>{navigator.clipboard?.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1800);};
  return(
    <div style={{background:C.codeBox,border:`1px solid ${C.border}`,borderRadius:11,overflow:"hidden"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 14px",background:C.surface,borderBottom:`1px solid ${C.border}`}}>
        <Badge color="blue">{lang}</Badge>
        <button onClick={copy} style={{background:"none",border:"none",color:copied?C.tealL:C.muted,fontSize:11,cursor:"pointer",fontWeight:700}}>{copied?"✓ 복사됨":"복사"}</button>
      </div>
      <div style={{overflowX:"auto",maxHeight:300,overflowY:"auto"}}>
        <pre style={{margin:0,padding:"14px 18px",fontSize:11,lineHeight:1.75,fontFamily:"'Fira Code',monospace",color:"#94a3b8"}}>
          {code.split("\n").map((line,i)=>(
            <div key={i} style={{display:"flex",gap:14}}>
              <span style={{color:"#1e2d45",userSelect:"none",minWidth:22,textAlign:"right",fontSize:10}}>{i+1}</span>
              <span style={{color:line.trim().startsWith("//")?"#3d5a80":line.match(/^(const|let|async|function|await|try|catch|for|if|return|throw)/)?"#93c5fd":"#94a3b8"}}>{line||" "}</span>
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

// ── 대시보드 페이지
function DashboardPage({employees,instructors}){
  const ae=employees.filter(e=>e.status==="active");
  const ai=instructors.filter(i=>i.status==="active");
  const empGross=ae.reduce((s,e)=>s+calcEmployee(e).gross,0);
  const empNet=ae.reduce((s,e)=>s+calcEmployee(e).netPay,0);
  const insNet=ai.reduce((s,i)=>s+calcInstructor(i.invoiceAmount).netPay,0);
  return(
    <div>
      <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:"0 0 5px"}}>급여 현황 대시보드</h1>
      <div style={{fontSize:13,color:C.muted,marginBottom:22}}>{THIS_MONTH} 기준</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:13,marginBottom:24}}>
        <StatCard label="재직 직원" value={`${ae.length}명`} sub={`전체 ${employees.length}명`} accent="linear-gradient(90deg,#3b82f6,#60a5fa)"/>
        <StatCard label="활동 강사" value={`${ai.length}명`} sub={`전체 ${instructors.length}명`} accent="linear-gradient(90deg,#a855f7,#c084fc)"/>
        <StatCard label="직원 총지급" value={fmtMan(empGross)} sub={`실수령 ${fmtMan(empNet)}`} accent="linear-gradient(90deg,#22c55e,#4ade80)"/>
        <StatCard icon="" label="이번달 총지급" value={fmtMan(empNet+insNet)} sub="직원+강사 합산" accent="linear-gradient(90deg,#f59e0b,#fbbf24)"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
        <Card>
          <h3 style={{fontSize:14,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>직원별 실수령액</h3>
          {ae.map(e=>{const c=calcEmployee(e);const pct=Math.round(c.netPay/c.gross*100);return(
            <div key={e.id} style={{marginBottom:13}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                <span style={{color:C.text,fontWeight:700}}>{e.name} <span style={{color:C.muted,fontWeight:400,fontSize:11}}>{e.position}</span></span>
                <span style={{color:C.blueL,fontWeight:700}}>{fmtW(c.netPay)}</span>
              </div>
              <div style={{height:5,background:C.border,borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#3b82f6,#60a5fa)",borderRadius:3}}/>
              </div>
              <div style={{fontSize:11,color:C.slate,marginTop:2}}>공제율 {100-pct}% · 총지급 {fmtW(c.gross)}</div>
            </div>
          );})}
        </Card>
        <Card>
          <h3 style={{fontSize:14,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>강사별 지급 현황</h3>
          {ai.map(i=>{const c=calcInstructor(i.invoiceAmount);return(
            <div key={i.id} style={{marginBottom:11,padding:"11px 13px",background:C.subBox,borderRadius:9,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{color:C.text,fontWeight:700,fontSize:13}}>{i.name}</span>
                <Badge color="purple">{i.subject}</Badge>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                <span style={{color:C.muted}}>청구 {fmtW(c.gross)}</span>
                <span style={{color:C.redL}}>공제 -{fmtW(c.totalDeduct)}</span>
                <span style={{color:C.blue,fontWeight:700}}>지급 {fmtW(c.netPay)}</span>
              </div>
            </div>
          );})}
          <div style={{marginTop:12,padding:"11px 14px",background:C.dim,borderRadius:9,display:"flex",justifyContent:"space-between"}}>
            <span style={{color:C.muted,fontWeight:700,fontSize:13}}>강사 총 지급예정</span>
            <span style={{color:C.blue,fontWeight:900,fontSize:16}}>{fmtW(insNet)}</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── 직원 관리 페이지
// ── 직원 일괄 등록 모달
// ── 강사/직원 공통: 기존 DB 목록을 기준으로 다음 순번 코드를 자동 생성
const nextCodeInfo=(list,prefixDefault)=>{
  let maxNum=0, prefix=prefixDefault;
  (list||[]).forEach(item=>{
    const m=/^([A-Za-z]*)(\d+)$/.exec(item.id||"");
    if(m){
      const num=parseInt(m[2],10);
      if(num>=maxNum){ maxNum=num; prefix=m[1]||prefixDefault; }
    }
  });
  return {maxNum,prefix};
};
// count개의 순번 코드를 미리 생성 (샘플 다운로드용)
const genSequentialCodes=(count,list,prefixDefault)=>{
  let {maxNum,prefix}=nextCodeInfo(list,prefixDefault);
  const codes=[];
  for(let i=0;i<count;i++){ maxNum+=1; codes.push(prefix+String(maxNum).padStart(3,"0")); }
  return codes;
};
// 업로드된 행들 중 번호가 비어있는 행에 자동으로 다음 순번 부여 (기존 DB + 이번 배치 내 중복 회피)
const assignAutoIds=(parsedRows,list,prefixDefault)=>{
  const used=new Set((list||[]).map(x=>x.id));
  parsedRows.forEach(r=>{ if(r.id&&r.id.trim()) used.add(r.id.trim()); });
  let {maxNum,prefix}=nextCodeInfo(list,prefixDefault);
  return parsedRows.map(r=>{
    if(r.id&&r.id.trim()) return {...r,id:r.id.trim()};
    let candidate;
    do{ maxNum+=1; candidate=prefix+String(maxNum).padStart(3,"0"); }while(used.has(candidate));
    used.add(candidate);
    return {...r,id:candidate};
  });
};

const EMPTY_EMP_ROW=()=>({id:"",name:"",dept:"",position:"",baseSalary:0,dependents:0,phone:"",email:"",birthDate:"",status:"active",allowances:[],deductions:[],_key:Math.random()});

function BulkEmpForm({open,onClose,onSave,employees}){
  const [rows,setRows]=useState([EMPTY_EMP_ROW(),EMPTY_EMP_ROW(),EMPTY_EMP_ROW()]);
  const [mode,setMode]=useState("table"); // table | csv | excel
  const [csvText,setCsvText]=useState("");
  const [errors,setErrors]=useState({});
  const [saved,setSaved]=useState(false);
  const fileInputRef=useRef(null);

  useEffect(()=>{if(open){setRows([EMPTY_EMP_ROW(),EMPTY_EMP_ROW(),EMPTY_EMP_ROW()]);setCsvText("");setErrors({});setSaved(false);setMode("table");}},[open]);

  const set=(i,k,v)=>setRows(p=>{const r=[...p];r[i]={...r[i],[k]:k==="baseSalary"||k==="dependents"?Number(v):v};return r;});
  const addRow=()=>setRows(p=>[...p,EMPTY_EMP_ROW()]);
  const delRow=i=>setRows(p=>p.filter((_,j)=>j!==i));

  const parseCSV=()=>{
    const parsed=csvText.trim().split("\n").filter(l=>l.trim()).map(line=>{
      const [id="",name="",dept="",position="",baseSalary="0",dependents="0",phone="",email=""]=line.split(",").map(s=>s.trim());
      return {id,name,dept,position,baseSalary:Number(baseSalary)||0,dependents:Number(dependents)||0,phone,email,birthDate:"",status:"active",allowances:[],_key:Math.random()};
    });
    setRows(parsed); setMode("table");
  };

  // ── 엑셀 샘플 다운로드 (기존 DB 사원번호 기준 다음 순번 자동 채움)
  const downloadSample=()=>{
    const codes=genSequentialCodes(5,employees,"HC");
    const header=["사원번호","성명","부서","직급","기본급","부양가족수","연락처","이메일"];
    const sampleRows=codes.map(c=>[c,"","","","","","",""]);
    const ws=XLSX.utils.aoa_to_sheet([header,...sampleRows]);
    ws['!cols']=[{wch:10},{wch:10},{wch:12},{wch:10},{wch:12},{wch:10},{wch:16},{wch:20}];
    const wb=XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb,ws,"직원목록");
    XLSX.writeFile(wb,`직원_일괄등록_샘플_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ── 엑셀 파일 업로드 및 파싱
  const handleExcelFile=(file)=>{
    if(!file) return;
    const reader=new FileReader();
    reader.onload=(e)=>{
      try{
        const data=new Uint8Array(e.target.result);
        const wb=XLSX.read(data,{type:"array"});
        const sheet=wb.Sheets[wb.SheetNames[0]];
        const json=XLSX.utils.sheet_to_json(sheet,{defval:""});
        let parsed=json.map(row=>({
          id:String(row["사원번호"]||"").trim(),
          name:String(row["성명"]||"").trim(),
          dept:String(row["부서"]||"").trim(),
          position:String(row["직급"]||"").trim(),
          baseSalary:Number(row["기본급"]||0)||0,
          dependents:Number(row["부양가족수"]||0)||0,
          phone:String(row["연락처"]||"").trim(),
          email:String(row["이메일"]||"").trim(),
          birthDate:"",status:"active",allowances:[],deductions:[],_key:Math.random()
        })).filter(r=>r.name);
        if(parsed.length===0){ alert("엑셀에서 읽을 수 있는 데이터가 없습니다. 샘플 양식을 확인해 주세요."); return; }
        parsed=assignAutoIds(parsed,employees,"HC");
        setRows(parsed);
        setMode("table");
      }catch(err){
        alert("엑셀 파일을 읽는 중 오류가 발생했습니다: "+err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validate=()=>{
    const e={};
    const seen={};
    rows.forEach((r,i)=>{
      if(!r.id.trim()) e[`${i}_id`]="필수";
      else if(seen[r.id]) e[`${i}_id`]="중복";
      else if((employees||[]).some(x=>x.id===r.id)) e[`${i}_id`]="이미 존재";
      seen[r.id]=true;
      if(!r.name.trim()) e[`${i}_name`]="필수";
      if(!r.dept.trim()) e[`${i}_dept`]="필수";
      if(r.baseSalary<=0) e[`${i}_sal`]="필수";
    });
    setErrors(e); return Object.keys(e).length===0;
  };

  const handleSave=()=>{
    if(!validate())return;
    onSave(rows.filter(r=>r.id&&r.name));
    setSaved(true); setTimeout(()=>{onClose();},900);
  };

  const cs=(err)=>({width:"100%",background:err?"#2d0a0a":C.inputBg,border:`1px solid ${err?"#ef4444":C.border2}`,borderRadius:6,padding:"6px 8px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"});

  const COLS=[
    {k:"id",       label:"사원번호", w:80,  ph:"E005"},
    {k:"name",     label:"성명",     w:72,  ph:"홍길동"},
    {k:"dept",     label:"부서",     w:90,  ph:"개발팀"},
    {k:"position", label:"직급",     w:72,  ph:"사원"},
    {k:"baseSalary",label:"기본급",  w:100, ph:"3000000",type:"number"},
    {k:"dependents",label:"부양",    w:48,  ph:"0",type:"number"},
    {k:"phone",    label:"연락처",   w:116, ph:"010-0000-0000"},
    {k:"email",    label:"이메일",   w:150, ph:"email@co.com"},
  ];

  const totalGross=rows.filter(r=>r.baseSalary>0).reduce((s,r)=>s+calcEmployee({...r,allowances:[]}).gross,0);

  return(
    <Modal open={open} onClose={onClose} title="직원 일괄 등록" width={920}>
      <div style={{display:"flex",gap:0,marginBottom:16,borderBottom:`1px solid ${C.border}`}}>
        {[["table","직접 입력"],["csv","CSV 붙여넣기"],["excel","엑셀 업로드"]].map(([v,l])=>(
          <button key={v} onClick={()=>setMode(v)} style={{padding:"8px 18px",background:"none",border:"none",borderBottom:`2px solid ${mode===v?C.blue:"transparent"}`,color:mode===v?C.blueL:C.muted,fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            {l}
            {v==="excel"&&<span style={{fontSize:9,fontWeight:800,color:C.amberL,background:C.dim,border:`1px solid ${C.border2}`,borderRadius:5,padding:"1px 5px"}}>NEW</span>}
          </button>
        ))}
      </div>

      {mode==="excel" ? (
        <div>
          <div style={{padding:"14px 16px",background:C.infoBox,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:14,fontSize:12,color:C.muted,lineHeight:1.9}}>
            <div style={{fontWeight:700,color:C.amberL,marginBottom:4}}>엑셀(.xlsx) 업로드 안내</div>
            <div>샘플 양식을 다운로드하여 작성 후 업로드해 주세요. <strong style={{color:C.text}}>사원번호</strong>를 비워두면 기존 등록된 사원번호 다음 순번으로 자동 부여됩니다.</div>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <Btn variant="secondary" style={{flex:1}} onClick={downloadSample}>📥 샘플 양식 다운로드 (.xlsx)</Btn>
            <Btn variant="primary" style={{flex:1}} onClick={()=>fileInputRef.current?.click()}>📤 엑셀 파일 업로드</Btn>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{display:"none"}}
              onChange={e=>{ handleExcelFile(e.target.files[0]); e.target.value=""; }}/>
          </div>
          <div style={{fontSize:11,color:C.slate,lineHeight:1.8}}>
            <div>· 업로드 후 "직접 입력" 탭에서 내용을 확인·수정한 뒤 저장할 수 있습니다.</div>
            <div>· 필수 항목(사원번호·성명·부서·기본급)이 비어있으면 저장 시 안내됩니다.</div>
          </div>
        </div>
      ) : mode==="csv" ? (
        <div>
          <div style={{padding:"10px 14px",background:C.infoBox,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:12,fontSize:12,color:C.muted,lineHeight:1.9}}>
            <div style={{fontWeight:700,color:C.amberL,marginBottom:3}}>CSV 형식</div>
            <div>사원번호, 성명, 부서, 직급, 기본급, 부양가족수, 연락처, 이메일</div>
            <div style={{color:C.slate}}>예: E005, 홍길동, 개발팀, 사원, 3000000, 1, 010-1111-2222, hong@co.com</div>
          </div>
          <textarea value={csvText} onChange={e=>setCsvText(e.target.value)} placeholder={"E005, 홍길동, 개발팀, 사원, 3000000, 1, 010-1111-2222, hong@co.com\nE006, 김철수, 마케팅팀, 대리, 3500000, 2, 010-3333-4444, kim@co.com"}
            style={{width:"100%",height:140,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"10px 12px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"monospace",lineHeight:1.7,resize:"vertical"}}/>
          <div style={{display:"flex",gap:10,marginTop:12}}>
            <Btn variant="primary" style={{flex:1}} onClick={parseCSV}>↩ 테이블로 가져오기</Btn>
            <Btn variant="secondary" style={{flex:1}} onClick={()=>setMode("table")}>취소</Btn>
          </div>
        </div>
      ) : (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,color:C.muted}}>총 <strong style={{color:C.text}}>{rows.length}명</strong> · <span style={{color:"#f87171"}}>*</span> 필수 항목</div>
            <div style={{display:"flex",gap:8}}>
              <Btn size="sm" variant="ghost" onClick={addRow}>+ 행 추가</Btn>
              <Btn size="sm" variant="secondary" onClick={()=>setMode("csv")}>CSV 전환</Btn>
            </div>
          </div>
          {/* 헤더 */}
          <div style={{display:"grid",gridTemplateColumns:"80px 72px 90px 72px 100px 48px 116px 150px 32px",gap:4,marginBottom:4,padding:"0 2px"}}>
            {COLS.map(c=>(
              <div key={c.k} style={{fontSize:10,fontWeight:700,color:C.muted}}>
                {c.label}{["id","name","dept","baseSalary"].includes(c.k)&&<span style={{color:C.redL}}> *</span>}
              </div>
            ))}
            <div/>
          </div>
          {/* 행 */}
          <div style={{maxHeight:380,overflowY:"auto",display:"flex",flexDirection:"column",gap:4,paddingRight:2}}>
            {rows.map((row,i)=>{
              const c=row.baseSalary>0?calcEmployee({...row,allowances:[]}):null;
              return(
                <div key={row._key}>
                  <div style={{display:"grid",gridTemplateColumns:"80px 72px 90px 72px 100px 48px 116px 150px 32px",gap:4,alignItems:"center"}}>
                    {COLS.map(col=>(
                      <input key={col.k} type={col.type||"text"} value={row[col.k]}
                        onChange={e=>set(i,col.k, col.k==="phone" ? formatPhone(e.target.value) : e.target.value)}
                        placeholder={col.ph}
                        style={cs(errors[`${i}_${col.k==="baseSalary"?"sal":col.k==="id"?"id":col.k==="name"?"name":col.k==="dept"?"dept":col.k}`])}/>
                    ))}
                    <button onClick={()=>delRow(i)} style={{width:28,height:28,borderRadius:6,background:"#450a0a",border:"1px solid #7f1d1d",color:C.redL,fontSize:14,cursor:"pointer"}}>×</button>
                  </div>
                  {c&&(
                    <div style={{marginTop:2,marginBottom:2,padding:"3px 8px",background:C.dim,borderRadius:5,display:"flex",gap:14,fontSize:10,color:C.muted}}>
                      <span>총지급 <strong style={{color:C.greenL}}>{fmtW(c.gross)}</strong></span>
                      <span>공제 <strong style={{color:C.redL}}>{fmtW(c.totalDeduct)}</strong></span>
                      <span>실수령 <strong style={{color:C.blueL}}>{fmtW(c.netPay)}</strong></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {rows.some(r=>r.baseSalary>0)&&(
            <div style={{marginTop:12,padding:"11px 16px",background:"linear-gradient(135deg,#1e3a5f,#0f2540)",borderRadius:10,display:"flex",gap:24,alignItems:"center"}}>
              <span style={{fontSize:11,color:C.muted,fontWeight:700}}>합계 ({rows.filter(r=>r.baseSalary>0).length}명)</span>
              <span style={{fontSize:12,color:C.muted}}>총 지급 <strong style={{color:C.greenL}}>{fmtW(totalGross)}</strong></span>
            </div>
          )}
          {Object.keys(errors).length>0&&(
            <div style={{marginTop:8,padding:"8px 12px",background:"#450a0a",borderRadius:7,fontSize:12,color:C.redL}}>⚠️ 빨간 테두리 항목을 확인해 주세요 (사원번호·성명·부서·기본급 필수, 사원번호는 중복될 수 없습니다)</div>
          )}
          <div style={{display:"flex",gap:10,marginTop:14}}>
            <Btn variant="primary" size="lg" style={{flex:1}} onClick={handleSave} disabled={saved}>{saved?"✅ 등록 완료!":"일괄 등록 저장"}</Btn>
            <Btn variant="secondary" size="lg" style={{flex:1}} onClick={onClose}>취소</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function EmpPage({employees,setEmployees,onPayslip,onEmpUpdate}){
  const [open,setOpen]=useState(false);
  const [bulkOpen,setBulkOpen]=useState(false);
  const [init,setInit]=useState(null);
  const [search,setSearch]=useState("");
  const [saving,setSaving]=useState(false);
  const [toast,setToast]=useState("");
  const list=employees.filter(e=>e.name.includes(search)||e.dept.includes(search)||e.id.includes(search));

  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(""),2500);};

  const loadEmps=async()=>{
    try{ const data=await api("GET","/employees"); setEmployees(data); showToast("✅ 새로고침 완료"); }
    catch(e){ showToast("❌ 불러오기 실패: "+e.message); }
  };

  // ── 사원번호 자동 생성 (기존 번호 중 가장 큰 숫자 + 1, 접두사는 기존 패턴을 따름)
  const nextEmpCode=()=>{
    let maxNum=0, prefix="HC";
    employees.forEach(e=>{
      const m=/^([A-Za-z]*)(\d+)$/.exec(e.id||"");
      if(m){
        const num=parseInt(m[2],10);
        if(num>=maxNum){ maxNum=num; prefix=m[1]||"HC"; }
      }
    });
    return prefix+String(maxNum+1).padStart(3,"0");
  };

  // 반환값: true(성공)/false(실패) — 모달을 닫을지는 호출부(EmpForm)가 이 값으로 판단
  const save=async(f)=>{
    if(!f.id || !f.id.trim()){
      showToast("❌ 사원번호를 입력해주세요.");
      return false;
    }
    const isEdit=employees.find(e=>e.id===f.id);
    // 신규 등록인데 이미 존재하는 사원번호면 차단 (수정 오작동 방지 + 중복 경고)
    if(!init && isEdit){
      showToast("❌ 이미 사용 중인 사원번호입니다. 다른 번호를 입력해주세요.");
      return false;
    }
    setSaving(true);
    try{
      if(isEdit) await api("PUT",`/employees/${f.id}`,f);
      else await api("POST","/employees",f);
      await loadEmps();
      // 수정된 직원 데이터를 상위로 전달 (명세서 즉시 반영)
      if(isEdit && onEmpUpdate) onEmpUpdate(f);
      showToast(isEdit?"✅ 직원 정보가 수정되었습니다.":"✅ 직원이 등록되었습니다.");
      return true;
    }catch(e){ showToast("❌ 저장 실패: "+e.message); return false; }
    finally{ setSaving(false); }
  };

  const bulkSave=async(list)=>{
    setSaving(true);
    try{
      for(const f of list) await api("POST","/employees",f);
      await loadEmps();
      showToast(`✅ ${list.length}명 일괄 등록 완료!`);
      setBulkOpen(false);
    }catch(e){ showToast("❌ 일괄 등록 실패: "+e.message); }
    finally{ setSaving(false); }
  };

  const deleteEmp=async(e)=>{
    if(!window.confirm(`"${e.name}" 직원을 삭제하시겠습니까?`))return;
    try{
      await api("DELETE",`/employees/${e.id}`);
      await loadEmps();
      showToast("직원이 삭제되었습니다.");
    }catch(err){ showToast("❌ 삭제 실패: "+err.message); }
  };

  return(
    <div>
      {toast&&(
        <div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:700,color:C.text,zIndex:9999,boxShadow:"0 6px 24px rgba(0,0,0,0.15)"}}>
          {toast}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:0}}>직원 관리</h1>
        <div style={{display:"flex",gap:9}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="이름·부서·번호 검색" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 13px",color:C.text,fontSize:12,outline:"none",width:200}}/>
          <Btn variant="ghost" onClick={loadEmps}>새로고침</Btn>
          <Btn variant="secondary" onClick={()=>setBulkOpen(true)} style={{border:`1px solid ${C.blue}`,color:C.blueL,background:C.dim}}>직원 등록 (일괄)</Btn>
          <Btn variant="primary" onClick={()=>{setInit(null);setOpen(true);}}>+ 직원 등록</Btn>
        </div>
      </div>
      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.thead}}>{["사원번호","성명","부서","직급","기본급","총지급액","공제합계","실수령액","상태",""].map(h=><th key={h} style={{padding:"10px 13px",color:C.muted,fontWeight:700,textAlign:"left",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap",fontSize:12}}>{h}</th>)}</tr></thead>
            <tbody>
              {list.length===0?(
                <tr><td colSpan={10} style={{padding:"40px",textAlign:"center",color:C.muted,fontSize:13}}>
                  {employees.length===0?"DB에서 데이터를 불러오는 중...":"검색 결과가 없습니다"}
                </td></tr>
              ):list.map((e,idx)=>{const c=calcEmployee(e);return(
                <tr key={e.id} style={{borderBottom:`1px solid ${C.border}`,background:idx%2===0?C.row1:C.row2}}>
                  <td style={{padding:"9px 13px",color:C.text,fontWeight:700,fontFamily:"monospace"}}>{e.id}</td>
                  <td style={{padding:"9px 13px",color:C.text,fontWeight:700}}>{e.name}</td>
                  <td style={{padding:"9px 13px",color:C.muted}}>{e.dept}</td>
                  <td style={{padding:"9px 13px",color:C.muted}}>{e.position}</td>
                  <td style={{padding:"9px 13px",color:C.text}}>{fmtW(e.baseSalary)}</td>
                  <td style={{padding:"9px 13px",color:C.greenL,fontWeight:700}}>{fmtW(c.gross)}</td>
                  <td style={{padding:"9px 13px",color:C.redL}}>{fmtW(c.totalDeduct)}</td>
                  <td style={{padding:"9px 13px",color:C.blueL,fontWeight:800}}>{fmtW(c.netPay)}</td>
                  <td style={{padding:"9px 13px"}}><Badge color={e.status==="active"?"green":e.status==="leave"?"amber":"gray"}>{e.status==="active"?"재직":e.status==="leave"?"휴직":"퇴직"}</Badge></td>
                  <td style={{padding:"9px 13px"}}><div style={{display:"flex",gap:5}}>
                    <Btn size="sm" variant="secondary" onClick={()=>onPayslip(e,"employee")}>명세서</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>{setInit(e);setOpen(true);}}>수정</Btn>
                    <Btn size="sm" variant="danger" onClick={()=>deleteEmp(e)}>삭제</Btn>
                  </div></td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </Card>
      <EmpForm open={open} onClose={()=>setOpen(false)} initial={init} onSave={save} suggestedId={nextEmpCode()}/>
      <BulkEmpForm open={bulkOpen} onClose={()=>setBulkOpen(false)} onSave={bulkSave} employees={employees}/>
    </div>
  );
}

// ── 강사 일괄 등록 모달
const EMPTY_ROW = () => ({id:"",name:"",subject:"",phone:"",email:"",unitPrice:0,hoursPerMonth:0,invoiceAmount:0,status:"active",birthdate:"",_key:Math.random()});

function BulkInsForm({open,onClose,onSave,instructors}){
  const [rows,setRows]=useState([EMPTY_ROW(),EMPTY_ROW(),EMPTY_ROW()]);
  const [mode,setMode]=useState("table"); // table | csv | excel
  const [csvText,setCsvText]=useState("");
  const [errors,setErrors]=useState({});
  const [saved,setSaved]=useState(false);
  const fileInputRef=useRef(null);

  useEffect(()=>{if(open){setRows([EMPTY_ROW(),EMPTY_ROW(),EMPTY_ROW()]);setCsvText("");setErrors({});setSaved(false);setMode("table");}},[open]);

  const setCell=(i,k,v)=>setRows(p=>{
    const r=[...p];
    const numKeys=["unitPrice","hoursPerMonth"];
    r[i]={...r[i],[k]:numKeys.includes(k)?Number(v):v};
    return r;
  });
  const addRow=()=>setRows(p=>[...p,EMPTY_ROW()]);
  const delRow=i=>setRows(p=>p.filter((_,j)=>j!==i));

  // CSV 파싱 — 형식: 강사번호,성명,강의과목,연락처,이메일,단가,월강의시간
  const parseCSV=()=>{
    const lines=csvText.trim().split("\n").filter(l=>l.trim());
    const parsed=lines.map((line)=>{
      const [id="",name="",subject="",phone="",email="",unitPrice="0",hoursPerMonth="0"]=line.split(",").map(s=>s.trim());
      return {id,name,subject,phone,email,unitPrice:Number(unitPrice)||0,hoursPerMonth:Number(hoursPerMonth)||0,invoiceAmount:0,status:"active",birthdate:"",_key:Math.random()};
    });
    setRows(parsed);
    setMode("table");
  };

  // ── 엑셀 샘플 다운로드 (기존 DB 강사번호 기준 다음 순번 자동 채움)
  const downloadSample=()=>{
    const codes=genSequentialCodes(5,instructors,"T");
    const header=["강사번호","성명","강의과목","연락처","이메일","시간당단가","월강의시간"];
    const sampleRows=codes.map(c=>[c,"","","","","",""]);
    const ws=XLSX.utils.aoa_to_sheet([header,...sampleRows]);
    ws['!cols']=[{wch:10},{wch:10},{wch:12},{wch:16},{wch:20},{wch:12},{wch:12}];
    const wb=XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb,ws,"강사목록");
    XLSX.writeFile(wb,`강사_일괄등록_샘플_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // ── 엑셀 파일 업로드 및 파싱
  const handleExcelFile=(file)=>{
    if(!file) return;
    const reader=new FileReader();
    reader.onload=(e)=>{
      try{
        const data=new Uint8Array(e.target.result);
        const wb=XLSX.read(data,{type:"array"});
        const sheet=wb.Sheets[wb.SheetNames[0]];
        const json=XLSX.utils.sheet_to_json(sheet,{defval:""});
        let parsed=json.map(row=>({
          id:String(row["강사번호"]||"").trim(),
          name:String(row["성명"]||"").trim(),
          subject:String(row["강의과목"]||"").trim(),
          phone:String(row["연락처"]||"").trim(),
          email:String(row["이메일"]||"").trim(),
          unitPrice:Number(row["시간당단가"]||0)||0,
          hoursPerMonth:Number(row["월강의시간"]||0)||0,
          invoiceAmount:0,status:"active",birthdate:"",_key:Math.random()
        })).filter(r=>r.name);
        if(parsed.length===0){ alert("엑셀에서 읽을 수 있는 데이터가 없습니다. 샘플 양식을 확인해 주세요."); return; }
        parsed=assignAutoIds(parsed,instructors,"T");
        setRows(parsed);
        setMode("table");
      }catch(err){
        alert("엑셀 파일을 읽는 중 오류가 발생했습니다: "+err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 유효성 검사
  const validate=()=>{
    const e={};
    const seen={};
    rows.forEach((r,i)=>{
      if(!r.id.trim())      e[`${i}_id`]="필수";
      else if(seen[r.id])   e[`${i}_id`]="중복";
      else if((instructors||[]).some(x=>x.id===r.id)) e[`${i}_id`]="이미 존재";
      seen[r.id]=true;
      if(!r.name.trim())    e[`${i}_name`]="필수";
      if(!r.subject.trim()) e[`${i}_subject`]="필수";
    });
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSave=()=>{
    if(!validate()) return;
    const valid=rows.filter(r=>r.id&&r.name).map(r=>({...r,invoiceAmount:0})); // 청구금액은 0으로 초기화
    onSave(valid);
    setSaved(true);
    setTimeout(()=>{onClose();},900);
  };

  const cellStyle=(err)=>({
    width:"100%",background:err?"#2d0a0a":C.inputBg,
    border:`1px solid ${err?"#ef4444":C.border2}`,
    borderRadius:6,padding:"6px 8px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box"
  });

  const COLS=[
    {k:"id",           label:"강사번호",      w:88,  ph:"I004"},
    {k:"name",         label:"성명",          w:76,  ph:"홍길동"},
    {k:"subject",      label:"강의과목",      w:96,  ph:"영어"},
    {k:"phone",        label:"연락처",        w:116, ph:"010-0000-0000"},
    {k:"email",        label:"이메일",        w:150, ph:"email@co.com"},
    {k:"unitPrice",    label:"시간당 단가(원)",w:110, ph:"200000",type:"number"},
    {k:"hoursPerMonth",label:"월 강의시간",   w:90,  ph:"12",   type:"number"},
  ];

  return(
    <Modal open={open} onClose={onClose} title="강사 일괄 등록" width={940}>
      {/* 안내 배너 */}
      <div style={{background:C.dim,border:`1px solid ${C.border2}`,borderRadius:10,padding:"10px 16px",marginBottom:14,fontSize:12,color:C.muted,display:"flex",alignItems:"center",gap:8}}>
        <span>청구금액은 입력하지 않습니다. 강사가 <strong style={{color:C.amberL}}>청구서를 제출</strong>하면 <strong style={{color:C.greenL}}>강의시간 × 단가</strong>로 자동 계산됩니다.<br/>
        <span style={{color:C.slate}}>단가·월강의시간은 참고용입니다. 실제 청구는 포털에서 강사가 직접 작성합니다.</span></span>
      </div>

      {/* 탭: 직접입력 / CSV붙여넣기 / 엑셀업로드 */}
      <div style={{display:"flex",gap:0,marginBottom:18,borderBottom:`1px solid ${C.border}`}}>
        {[["table","직접 입력"],["csv","CSV 붙여넣기"],["excel","엑셀 업로드"]].map(([v,l])=>(
          <button key={v} onClick={()=>setMode(v)} style={{padding:"8px 18px",background:"none",border:"none",borderBottom:`2px solid ${(mode===v)?C.blue:"transparent"}`,color:(mode===v)?C.blueL:C.muted,fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            {l}
            {v==="excel"&&<span style={{fontSize:9,fontWeight:800,color:C.amberL,background:C.dim,border:`1px solid ${C.border2}`,borderRadius:5,padding:"1px 5px"}}>NEW</span>}
          </button>
        ))}
      </div>

      {mode==="excel" ? (
        /* 엑셀 업로드 모드 */
        <div>
          <div style={{padding:"14px 16px",background:C.infoBox,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:14,fontSize:12,color:C.muted,lineHeight:1.9}}>
            <div style={{fontWeight:700,color:C.amberL,marginBottom:4}}>엑셀(.xlsx) 업로드 안내</div>
            <div>샘플 양식을 다운로드하여 작성 후 업로드해 주세요. <strong style={{color:C.text}}>강사번호</strong>를 비워두면 기존 등록된 강사번호 다음 순번으로 자동 부여됩니다.</div>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <Btn variant="secondary" style={{flex:1}} onClick={downloadSample}>📥 샘플 양식 다운로드 (.xlsx)</Btn>
            <Btn variant="primary" style={{flex:1}} onClick={()=>fileInputRef.current?.click()}>📤 엑셀 파일 업로드</Btn>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{display:"none"}}
              onChange={e=>{ handleExcelFile(e.target.files[0]); e.target.value=""; }}/>
          </div>
          <div style={{fontSize:11,color:C.slate,lineHeight:1.8}}>
            <div>· 업로드 후 "직접 입력" 탭에서 내용을 확인·수정한 뒤 저장할 수 있습니다.</div>
            <div>· 필수 항목(강사번호·성명·강의과목)이 비어있으면 저장 시 안내됩니다.</div>
          </div>
        </div>
      ) : mode==="csv" ? (
        /* CSV 입력 모드 */
        <div>
          <div style={{padding:"10px 14px",background:C.infoBox,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:12,fontSize:12,color:C.muted,lineHeight:1.9}}>
            <div style={{fontWeight:700,color:C.amberL,marginBottom:4}}>CSV 형식 안내</div>
            <div>강사번호, 성명, 강의과목, 연락처, 이메일, 시간당단가, 월강의시간</div>
            <div style={{color:C.slate,marginTop:2}}>예시: I004, 김강사, 영어회화, 010-1111-2222, kim@co.com, 200000, 12</div>
          </div>
          <textarea
            value={csvText}
            onChange={e=>setCsvText(e.target.value)}
            placeholder={"I004, 김강사, 영어회화, 010-1111-2222, kim@co.com, 200000, 12\nI005, 이강사, 수학, 010-3333-4444, lee@co.com, 150000, 12\nI006, 박강사, 코딩, 010-5555-6666, park@co.com, 250000, 8"}
            style={{width:"100%",height:160,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"10px 12px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"monospace",lineHeight:1.7,resize:"vertical"}}
          />
          <div style={{display:"flex",gap:10,marginTop:12}}>
            <Btn variant="primary" style={{flex:1}} onClick={parseCSV}>↩ 테이블로 가져오기</Btn>
            <Btn variant="secondary" style={{flex:1}} onClick={()=>setMode("table")}>취소</Btn>
          </div>
        </div>
      ) : (
        /* 테이블 직접 입력 모드 */
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:12,color:C.muted}}>
              총 <strong style={{color:C.text}}>{rows.length}명</strong> 입력 중 · <span style={{color:C.redL}}>*</span> 필수 항목
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn size="sm" variant="ghost" onClick={addRow}>+ 행 추가</Btn>
              <Btn size="sm" variant="secondary" onClick={()=>setMode("csv")}>CSV로 전환</Btn>
            </div>
          </div>

          {/* 헤더 */}
          <div style={{display:"grid",gridTemplateColumns:"88px 76px 96px 116px 150px 110px 90px 36px",gap:5,marginBottom:5,padding:"0 2px"}}>
            {COLS.map(c=>(
              <div key={c.k} style={{fontSize:10,fontWeight:700,color:C.muted,padding:"0 2px"}}>
                {c.label}{["id","name","subject"].includes(c.k)&&<span style={{color:C.redL}}> *</span>}
              </div>
            ))}
            <div/>
          </div>

          {/* 행 목록 */}
          <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:5,paddingRight:4}}>
            {rows.map((row,i)=>{
              const estimated = (row.unitPrice||0) * (row.hoursPerMonth||0);
              const preview = estimated > 0 ? calcInstructor(estimated) : null;
              return(
                <div key={row._key}>
                  <div style={{display:"grid",gridTemplateColumns:"88px 76px 96px 116px 150px 110px 90px 36px",gap:5,alignItems:"center"}}>
                    {COLS.map(col=>(
                      <input key={col.k}
                        type={col.type||"text"}
                        value={row[col.k]}
                        onChange={e=>setCell(i,col.k, col.k==="phone" ? formatPhone(e.target.value) : e.target.value)}
                        placeholder={col.ph}
                        style={cellStyle(errors[`${i}_${col.k==="id"?"id":col.k==="name"?"name":col.k==="subject"?"subject":""}`])}
                      />
                    ))}
                    <button onClick={()=>delRow(i)} style={{width:30,height:30,borderRadius:6,background:"#450a0a",border:"1px solid #7f1d1d",color:C.redL,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                  </div>
                  {/* 예상 청구금액 미리보기 */}
                  {preview ? (
                    <div style={{marginTop:2,marginBottom:3,padding:"4px 10px",background:C.subBox,borderRadius:5,display:"flex",gap:14,fontSize:10,color:C.muted,alignItems:"center"}}>
                      <span style={{color:C.slate}}>예상</span>
                      <span>청구 <strong style={{color:C.greenL}}>{fmtW(preview.gross)}</strong></span>
                      <span>소득세 <strong style={{color:C.redL}}>{fmtW(preview.incomeTax)}</strong></span>
                      <span>주민세 <strong style={{color:C.redL}}>{fmtW(preview.residentTax)}</strong></span>
                      <span>예상 실지급 <strong style={{color:C.purpleL}}>{fmtW(preview.netPay)}</strong></span>
                      <span style={{color:C.slate,marginLeft:"auto"}}>단가 {fmtW(row.unitPrice)} × {row.hoursPerMonth}h</span>
                    </div>
                  ) : (
                    <div style={{marginTop:2,marginBottom:3,padding:"3px 10px",fontSize:10,color:C.slate,fontStyle:"italic"}}>
                      단가·월강의시간 입력 시 예상 청구금액이 표시됩니다
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 합계 요약 */}
          {rows.some(r=>(r.unitPrice||0)*(r.hoursPerMonth||0)>0)&&(()=>{
            const validRows=rows.filter(r=>(r.unitPrice||0)*(r.hoursPerMonth||0)>0);
            const totGross=validRows.reduce((s,r)=>s+(r.unitPrice||0)*(r.hoursPerMonth||0),0);
            const totNet=validRows.reduce((s,r)=>s+calcInstructor((r.unitPrice||0)*(r.hoursPerMonth||0)).netPay,0);
            const totDed=totGross-totNet;
            return(
              <div style={{marginTop:12,padding:"12px 16px",background:"linear-gradient(135deg,#0f0a2e,#1a0a40)",borderRadius:10,border:"1px solid #3b1d8a",display:"flex",gap:24,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.muted,fontWeight:700}}>예상 합계 ({validRows.length}명)</span>
                <span style={{fontSize:12,color:C.muted}}>총 청구 <strong style={{color:C.greenL,fontSize:13}}>{fmtW(totGross)}</strong></span>
                <span style={{fontSize:12,color:C.muted}}>원천징수 <strong style={{color:C.redL,fontSize:13}}>{fmtW(totDed)}</strong></span>
                <span style={{fontSize:12,color:C.muted}}>총 지급 <strong style={{color:C.purpleL,fontSize:14}}>{fmtW(totNet)}</strong></span>
                <span style={{fontSize:10,color:C.slate,marginLeft:"auto"}}>※ 실제 청구서 제출 후 확정</span>
              </div>
            );
          })()}

          {Object.keys(errors).length>0&&(
            <div style={{marginTop:8,padding:"8px 12px",background:"#450a0a",borderRadius:7,fontSize:12,color:C.redL}}>
              ⚠️ 빨간 테두리 항목을 확인해 주세요 (강사번호, 성명, 강의과목은 필수, 강사번호는 중복될 수 없습니다)
            </div>
          )}

          <div style={{display:"flex",gap:10,marginTop:16}}>
            <Btn variant="primary" size="lg" style={{flex:1}} onClick={handleSave} disabled={saved}>
              {saved?"✅ 등록 완료!":"일괄 등록 저장"}
            </Btn>
            <Btn variant="secondary" size="lg" style={{flex:1}} onClick={onClose}>취소</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── 강사 포털 URL 안내 + 발송 모달
const PORTAL_URL = "https://payroll.ehancom.com/instructor";

function PortalInviteModal({open, onClose, instructor}){
  const [sendCh, setSendCh] = useState(null); // "sms"|"kakao"|null
  const [done, setDone] = useState(false);

  useEffect(()=>{ if(open){ setSendCh(null); setDone(false); } }, [open]);

  if(!open || !instructor) return null;

  const msg =
`[한컴캠퍼스] 강사 포털 접속 안내

안녕하세요, ${instructor.name} 강사님 강의료 청구서 작성 포털이 준비되었습니다.

접속 주소:
${PORTAL_URL}

로그인 방법:
• 성명: ${instructor.name}
• 생년월일: ${instructor.birthdate ? instructor.birthdate.replace(/-/g,".") : "등록된 생년월일"}

청구서 작성 → 강의 세부내역 입력 → 제출
문의: 교육운영팀 02-2672-1185`;

  const copyMsg = ()=>{ navigator.clipboard?.writeText(msg); alert("메시지가 복사되었습니다!"); };

  const openSMS = ()=>{
    const encoded = encodeURIComponent(msg);
    window.open(`sms:${instructor.phone}?body=${encoded}`, "_blank");
    setDone(true);
  };
  const openKakao = ()=>{
    navigator.clipboard?.writeText(msg);
    alert(`메시지가 클립보드에 복사되었습니다.\n카카오톡을 열어 ${instructor.name} 강사에게 붙여넣기 해주세요.`);
    setDone(true);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.78)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onClose}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:28,width:520,maxWidth:"95vw",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div>
            <div style={{fontSize:15,fontWeight:900,color:C.text}}>포털 접속 안내 발송</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>{instructor.name} 강사 · {instructor.phone}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* URL 카드 */}
        <div style={{background:C.thead,border:`1px solid #253553`,borderRadius:12,padding:"14px 18px",marginBottom:18}}>
          <div style={{fontSize:10,color:C.muted,fontWeight:700,marginBottom:6,letterSpacing:"0.08em"}}>강사 포털 주소</div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <code style={{flex:1,fontSize:13,color:C.tealL,fontFamily:"monospace",wordBreak:"break-all"}}>{PORTAL_URL}</code>
            <button onClick={()=>{navigator.clipboard?.writeText(PORTAL_URL); alert("URL 복사됨!");}}
              style={{background:C.subBox,border:`1px solid ${C.border2}`,borderRadius:6,padding:"5px 10px",color:C.blueL,fontSize:11,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>
              복사
            </button>
          </div>
        </div>

        {/* 발송 메시지 미리보기 */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6}}>발송될 메시지 미리보기</div>
          <div style={{background:"#f0f9ff",border:"1px solid #bfdbfe",borderRadius:10,padding:"14px 16px",position:"relative"}}>
            <pre style={{margin:0,fontSize:12,color:"#1e3a5f",lineHeight:1.75,whiteSpace:"pre-wrap",fontFamily:"'Apple SD Gothic Neo',sans-serif"}}>{msg}</pre>
            <button onClick={copyMsg}
              style={{position:"absolute",top:10,right:10,background:"#dbeafe",border:"1px solid #93c5fd",borderRadius:6,padding:"4px 10px",color:"#1d4ed8",fontSize:10,cursor:"pointer",fontWeight:700}}>
              복사
            </button>
          </div>
        </div>

        {/* 발송 버튼 */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
          <button onClick={openSMS}
            style={{background:"linear-gradient(135deg,#16a34a,#15803d)",color:"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            문자(SMS) 전송
          </button>
          <button onClick={openKakao}
            style={{background:"linear-gradient(135deg,#f5c842,#e6b800)",color:"#1a1200",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            카카오톡 전송
          </button>
        </div>

        {done&&(
          <div style={{background:"#052e16",border:"1px solid #166534",borderRadius:9,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#4ade80",textAlign:"center"}}>
            ✅ 발송 완료! 강사에게 포털 접속 안내가 전달되었습니다.
          </div>
        )}

        {/* 로그인 정보 요약 */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8}}>강사 로그인 정보 확인</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 16px",fontSize:12}}>
            <div style={{color:C.muted}}>성명 <strong style={{color:C.text}}>{instructor.name}</strong></div>
            <div style={{color:C.muted}}>생년월일 <strong style={{color:C.amberL}}>{instructor.birthdate ? instructor.birthdate.replace(/-/g,".") : "미등록"}</strong></div>
            <div style={{color:C.muted}}>연락처 <strong style={{color:C.text}}>{instructor.phone||"—"}</strong></div>
            <div style={{color:C.muted}}>강의과목 <strong style={{color:C.text}}>{instructor.subject}</strong></div>
          </div>
          {!instructor.birthdate&&(
            <div style={{marginTop:8,fontSize:11,color:C.redL}}>⚠️ 생년월일이 등록되지 않아 로그인 불가 — 강사 정보 수정에서 등록해 주세요</div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── 전체 일괄 URL 발송 모달
function BulkInviteModal({open, onClose, instructors}){
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(()=>{ if(open) setResults([]); }, [open]);

  if(!open) return null;

  const activeIns = instructors.filter(i=>i.status==="active");

  const bulkCopyAll = ()=>{
    const all = activeIns.map(i=>{
      const birth = i.birthdate ? i.birthdate.replace(/-/g,".") : "(생년월일 미등록)";
      return `${i.name}: ${PORTAL_URL} / 로그인: ${i.name} + ${birth}`;
    }).join("\n\n");
    navigator.clipboard?.writeText(all);
    alert("전체 강사 포털 안내 메시지가 복사되었습니다!");
  };

  const realBulkSend = async()=>{
    if(!window.confirm(`활동 중인 강사 ${activeIns.length}명에게 포털 접속 안내 SMS를 발송하시겠습니까?`))return;
    setSending(true); setResults([]);
    for(const ins of activeIns){
      if(!ins.phone){ setResults(p=>[...p,{name:ins.name,phone:"없음",ok:false,id:ins.id}]); continue; }
      try{
        const res=await fetch(`${API_BASE}/send-slip-sms`,{
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${getToken()}`},
          body:JSON.stringify({
            personName:ins.name,
            phone:ins.phone,
            month:THIS_MONTH,
            portalUrl:PORTAL_URL,
            isPortalInvite:true,
          }),
        });
        const data=await res.json();
        setResults(p=>[...p,{name:ins.name,phone:ins.phone,ok:data.ok||false,id:ins.id}]);
      }catch(e){
        setResults(p=>[...p,{name:ins.name,phone:ins.phone,ok:false,id:ins.id}]);
      }
    }
    setSending(false);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.78)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}} onClick={onClose}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:28,width:600,maxWidth:"95vw",maxHeight:"88vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div>
            <div style={{fontSize:15,fontWeight:900,color:C.text}}>포털 URL 일괄 발송</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>활동 중인 강사 {activeIns.length}명에게 포털 접속 안내</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* URL 표시 */}
        <div style={{background:"linear-gradient(135deg,#042f2e,#065f46)",border:"1px solid #047857",borderRadius:12,padding:"14px 18px",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:10,color:"#6ee7b7",fontWeight:700,marginBottom:4}}>강사 포털 접속 주소</div>
            <code style={{fontSize:14,color:"#a7f3d0",fontFamily:"monospace",fontWeight:700}}>{PORTAL_URL}</code>
          </div>
          <button onClick={()=>{navigator.clipboard?.writeText(PORTAL_URL); alert("복사됨!");}}
            style={{background:"#065f46",border:"1px solid #059669",borderRadius:7,padding:"6px 14px",color:"#6ee7b7",fontSize:12,cursor:"pointer",fontWeight:700}}>복사</button>
        </div>

        {/* 강사 목록 */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8}}>발송 대상 강사 목록</div>
          <div style={{background:C.thead,borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden",maxHeight:240,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:C.thead}}>{["강사번호","성명","과목","연락처","생년월일","상태"].map(h=><th key={h} style={{padding:"8px 12px",color:C.muted,fontWeight:700,textAlign:"left",fontSize:11,borderBottom:`1px solid ${C.border}`}}>{h}</th>)}</tr></thead>
              <tbody>
                {activeIns.map((ins,i)=>{
                  const res = results.find(r=>r.id===ins.id);
                  return(
                    <tr key={ins.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.row1:C.row2}}>
                      <td style={{padding:"8px 12px",color:C.slate,fontFamily:"monospace",fontSize:11}}>{ins.id}</td>
                      <td style={{padding:"8px 12px",color:C.text,fontWeight:700}}>{ins.name}</td>
                      <td style={{padding:"8px 12px",color:C.muted}}>{ins.subject}</td>
                      <td style={{padding:"8px 12px",color:ins.phone?C.text:C.redL}}>{ins.phone||"미등록 ⚠️"}</td>
                      <td style={{padding:"8px 12px",color:ins.birthdate?C.amberL:C.redL,fontSize:11,fontFamily:"monospace"}}>{ins.birthdate?ins.birthdate.replace(/-/g,"."):"미등록 ⚠️"}</td>
                      <td style={{padding:"8px 12px"}}>
                        {res ? <span style={{color:res.ok?C.greenL:C.redL,fontWeight:700}}>{res.ok?"✓ 발송됨":"✗ 실패"}</span>
                              : <span style={{color:C.slate,fontSize:11}}>대기</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 진행 결과 */}
        {results.length>0&&(
          <div style={{background:C.thead,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,display:"flex",gap:18,color:C.muted}}>
            <span>전송 <strong style={{color:C.text}}>{results.length}/{activeIns.length}</strong></span>
            <span>성공 <strong style={{color:C.greenL}}>{results.filter(r=>r.ok).length}</strong></span>
            <span>실패 <strong style={{color:C.redL}}>{results.filter(r=>!r.ok).length}</strong></span>
          </div>
        )}

        {/* 버튼들 */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <button onClick={realBulkSend} disabled={sending}
            style={{background:"linear-gradient(135deg,#16a34a,#15803d)",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:800,cursor:sending?"not-allowed":"pointer",opacity:sending?0.7:1}}>
            {sending?`발송 중... (${results.length}/${activeIns.length})`:"SMS 일괄 발송"}
          </button>
          <button onClick={bulkCopyAll}
            style={{background:"linear-gradient(135deg,#f5c842,#e6b800)",color:"#1a1200",border:"none",borderRadius:10,padding:"12px",fontSize:13,fontWeight:800,cursor:"pointer"}}>
            전체 메시지 복사
          </button>
        </div>

        <div style={{marginTop:12,fontSize:11,color:C.slate,lineHeight:1.7,padding:"8px 12px",background:C.thead,borderRadius:8}}>
          실제 SMS 발송은 알리고 API 연동 후 동작합니다. [전체 메시지 복사]로 카카오톡 단체 발송도 가능합니다.
        </div>
      </div>
    </div>
  );
}

// ── 강사 관리 페이지
function InsPage({instructors,setInstructors,onPayslip,onInsUpdate}){
  const [open,setOpen]=useState(false);
  const [bulkOpen,setBulkOpen]=useState(false);
  const [init,setInit]=useState(null);
  const [search,setSearch]=useState("");
  const [inviteTarget,setInviteTarget]=useState(null);
  const [bulkInviteOpen,setBulkInviteOpen]=useState(false);
  const [saving,setSaving]=useState(false);
  const [toast,setToast]=useState("");

  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(""),2500);};

  const filtered=instructors.filter(i=>i.name.includes(search)||(i.phone||"").includes(search));

  const loadInsts=async()=>{
    try{ const data=await api("GET","/instructors"); setInstructors(data); showToast("✅ 새로고침 완료"); }
    catch(e){ showToast("❌ 불러오기 실패: "+e.message); }
  };

  // ── 강사번호 자동 생성 (기존 번호 중 가장 큰 숫자 + 1, 접두사는 기존 패턴을 따름)
  const nextInstructorCode=()=>{
    let maxNum=0, prefix="T";
    instructors.forEach(i=>{
      const m=/^([A-Za-z]*)(\d+)$/.exec(i.id||"");
      if(m){
        const num=parseInt(m[2],10);
        if(num>=maxNum){ maxNum=num; prefix=m[1]||"T"; }
      }
    });
    return prefix+String(maxNum+1).padStart(3,"0");
  };

  // 반환값: true(성공)/false(실패) — 모달을 닫을지는 호출부(InsForm)가 이 값으로 판단
  const save=async(f)=>{
    if(!f.id || !f.id.trim()){
      showToast("❌ 강사번호를 입력해주세요.");
      return false;
    }
    const isEdit=instructors.find(i=>i.id===f.id);
    // 신규 등록인데 이미 존재하는 강사번호면 차단 (수정 오작동 방지 + 중복 경고)
    if(!init && isEdit){
      showToast("❌ 이미 사용 중인 강사번호입니다. 다른 번호를 입력해주세요.");
      return false;
    }
    setSaving(true);
    try{
      if(isEdit) await api("PUT",`/instructors/${f.id}`,f);
      else await api("POST","/instructors",f);
      await loadInsts();
      // 수정된 강사 데이터를 상위로 전달 (명세서 즉시 반영)
      if(isEdit && onInsUpdate) onInsUpdate(f);
      showToast(isEdit?"✅ 강사 정보가 수정되었습니다.":"✅ 강사가 등록되었습니다.");
      return true;
    }catch(e){ showToast("❌ 저장 실패: "+e.message); return false; }
    finally{ setSaving(false); }
  };

  const bulkSave=async(list)=>{
    setSaving(true);
    try{
      for(const f of list) await api("POST","/instructors",f);
      await loadInsts();
      showToast(`✅ ${list.length}명 일괄 등록 완료!`);
      setBulkOpen(false);
    }catch(e){ showToast("❌ 일괄 등록 실패: "+e.message); }
    finally{ setSaving(false); }
  };
  return(
    <div>
      {toast&&(
        <div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:700,color:C.text,zIndex:9999,boxShadow:"0 6px 24px rgba(0,0,0,0.15)"}}>
          {toast}
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:0}}>강사 관리</h1>
        <div style={{display:"flex",gap:9,alignItems:"center"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="이름·전화번호 검색" style={{background:C.card,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 13px",color:C.text,fontSize:12,outline:"none",width:180}}/>
          <Btn variant="ghost" onClick={loadInsts}>새로고침</Btn>
          <Btn variant="ghost" onClick={()=>setBulkInviteOpen(true)}>URL 일괄 발송</Btn>
          <Btn variant="secondary" onClick={()=>setBulkOpen(true)} style={{border:`1px solid ${C.blue}`,color:C.blueL,background:C.dim}}>강사 등록 (일괄)</Btn>
          <Btn variant="primary" onClick={()=>{setInit(null);setOpen(true);}}>+ 강사 등록</Btn>
        </div>
      </div>

      {/* 포털 URL 안내 카드 */}
      <div style={{background:"linear-gradient(135deg,#042f2e,#064e3b)",border:"1px solid #065f46",borderRadius:13,padding:"14px 20px",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{fontSize:26}}></div>
          <div>
            <div style={{fontSize:12,fontWeight:800,color:"#6ee7b7",marginBottom:3}}>강사 포털 접속 주소</div>
            <code style={{fontSize:13,color:"#a7f3d0",fontFamily:"monospace"}}>{PORTAL_URL}</code>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>강사들이 외부에서 청구서를 작성하는 전용 주소입니다</div>
          </div>
        </div>
        <div style={{display:"flex",gap:9}}>
          <button onClick={()=>{navigator.clipboard?.writeText(PORTAL_URL); alert("URL이 복사되었습니다!");}}
            style={{background:"#065f46",border:"1px solid #059669",borderRadius:8,padding:"8px 16px",color:"#6ee7b7",fontSize:12,cursor:"pointer",fontWeight:700}}>
            URL 복사
          </button>
          <button onClick={()=>setBulkInviteOpen(true)}
            style={{background:"linear-gradient(135deg,#059669,#047857)",border:"none",borderRadius:8,padding:"8px 18px",color:"#fff",fontSize:12,cursor:"pointer",fontWeight:700}}>
            전체 발송
          </button>
        </div>
      </div>

      {/* 요약 카드 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
        {[
          {l:"전체 강사",v:`${instructors.length}명`,c:C.purpleL,icon:"‍"},
          {l:"활동 중",v:`${instructors.filter(i=>i.status==="active").length}명`,c:C.greenL,icon:"✅"},
          {l:"총 청구금액",v:fmtW(instructors.filter(i=>i.status==="active").reduce((s,i)=>s+i.invoiceAmount,0)),c:C.amberL,icon:""},
          {l:"총 실지급액",v:fmtW(instructors.filter(i=>i.status==="active").reduce((s,i)=>s+calcInstructor(i.invoiceAmount).netPay,0)),c:C.blueL,icon:""},
        ].map(s=>(
          <div key={s.l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:11,padding:"13px 16px"}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:3}}>{s.icon} {s.l}</div>
            <div style={{fontSize:16,fontWeight:900,color:s.c}}>{s.v}</div>
          </div>
        ))}
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:C.thead}}>{["강사번호","성명","과목","청구금액","소득세(3%)","주민세(0.3%)","실지급액","상태",""].map(h=><th key={h} style={{padding:"10px 13px",color:C.muted,fontWeight:700,textAlign:"left",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap",fontSize:12}}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((i,idx)=>{const c=calcInstructor(i.invoiceAmount);return(
                <tr key={i.id} style={{borderBottom:`1px solid ${C.border}`,background:idx%2===0?C.row1:C.row2}}>
                  <td style={{padding:"9px 13px",color:C.text,fontWeight:700,fontFamily:"monospace"}}>{i.id}</td>
                  <td style={{padding:"9px 13px",color:C.text,fontWeight:700}}>{i.name}</td>
                  <td style={{padding:"9px 13px",color:C.muted}}>{i.subject}</td>
                  <td style={{padding:"9px 13px"}}>
                    {c.gross>0
                      ? <span style={{color:C.greenL,fontWeight:700}}>{fmtW(c.gross)}</span>
                      : <Badge color="gray">청구서 미제출</Badge>}
                  </td>
                  <td style={{padding:"9px 13px",color:c.gross>0?C.redL:C.muted}}>{c.gross>0?fmtW(c.incomeTax):"—"}</td>
                  <td style={{padding:"9px 13px",color:c.gross>0?C.redL:C.muted}}>{c.gross>0?fmtW(c.residentTax):"—"}</td>
                  <td style={{padding:"9px 13px",color:c.gross>0?C.blue:C.muted,fontWeight:800}}>{c.gross>0?fmtW(c.netPay):"—"}</td>
                  <td style={{padding:"9px 13px"}}><Badge color={i.status==="active"?"green":"gray"}>{i.status==="active"?"활동중":"비활동"}</Badge></td>
                  <td style={{padding:"9px 13px"}}>
                    <div style={{display:"flex",gap:5,flexWrap:"nowrap"}}>
                      <Btn size="sm" variant="secondary" onClick={()=>onPayslip(i,"instructor")}>명세서</Btn>
                      <Btn size="sm" variant="ghost" onClick={()=>{setInit(i);setOpen(true);}}>수정</Btn>
                      <button onClick={()=>setInviteTarget(i)}
                        style={{background:"#042f2e",border:"1px solid #065f46",color:"#6ee7b7",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                        URL
                      </button>
                      <Btn size="sm" variant="danger" onClick={()=>{
                        if(window.confirm(`"${i.name}" 강사를 삭제하시겠습니까?\n삭제 후 복구할 수 없습니다.`)){
                          api("DELETE",`/instructors/${i.id}`)
                            .then(()=>{ loadInsts(); showToast("강사가 삭제되었습니다."); })
                            .catch(e=>showToast("❌ 삭제 실패: "+e.message));
                        }
                      }}>삭제</Btn>
                    </div>
                  </td>
                </tr>
              );})}
              {filtered.length===0&&(
                <tr><td colSpan={9} style={{padding:"40px",textAlign:"center",color:C.muted,fontSize:13}}>검색 결과가 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <InsForm open={open} onClose={()=>setOpen(false)} initial={init} onSave={save} suggestedId={nextInstructorCode()}/>
      <BulkInsForm open={bulkOpen} onClose={()=>setBulkOpen(false)} onSave={bulkSave} instructors={instructors}/>
      <PortalInviteModal open={!!inviteTarget} onClose={()=>setInviteTarget(null)} instructor={inviteTarget}/>
      <BulkInviteModal open={bulkInviteOpen} onClose={()=>setBulkInviteOpen(false)} instructors={instructors}/>
    </div>
  );
}

// ── 계산기 페이지
function CalcPage(){
  const [t,setT]=useState("emp");
  const [baseRaw,setBaseRaw]=useState("3500000");
  const base=Number(baseRaw.replace(/,/g,""))||0;
  const [dep,setDep]=useState(0);
  const [inv,setInv]=useState(2000000);
  const [customAllowances,setCustomAllowances]=useState([]); // 추가 수당

  // 천단위 콤마 포맷
  const fmtComma=(v)=>Number(String(v).replace(/,/g,"")).toLocaleString("ko-KR");
  const parseComma=(v)=>Number(String(v).replace(/,/g,""))||0;

  // 수당 항목 (체크박스)
  const ALLOWANCE_PRESETS=[
    {key:"meal",    name:"식대",       amount:200000, nonTaxable:true,  defaultOn:true},
    {key:"trans",   name:"교통비",     amount:100000, nonTaxable:false, defaultOn:true},
    {key:"position",name:"직책수당",   amount:100000, nonTaxable:false, defaultOn:false},
    {key:"research",name:"연구개발비", amount:200000, nonTaxable:true,  defaultOn:false},
  ];

  const [allowances, setAllowances] = useState(
    ALLOWANCE_PRESETS.reduce((acc,a)=>({...acc,[a.key]:{checked:a.defaultOn,amount:a.amount,amountRaw:String(a.amount)}}),{})
  );

  const toggleAllow=(key)=>setAllowances(p=>({...p,[key]:{...p[key],checked:!p[key].checked}}));
  const setAmount=(key,val)=>{
    const num=parseComma(val)||0;
    setAllowances(p=>({...p,[key]:{...p[key],amount:num,amountRaw:fmtComma(num)}}));
  };

  // 추가 수당 관리
  const addCustomAllowance=()=>setCustomAllowances(p=>[...p,{id:Date.now(),name:"",amount:0,amountRaw:"0",nonTaxable:false}]);
  const removeCustomAllowance=(id)=>setCustomAllowances(p=>p.filter(a=>a.id!==id));
  const updateCustomAllowance=(id,k,v)=>setCustomAllowances(p=>p.map(a=>{
    if(a.id!==id) return a;
    if(k==="amount"){
      const num=parseComma(v)||0;
      return {...a,amount:num,amountRaw:fmtComma(num)};
    }
    return {...a,[k]:v};
  }));

  const activeAllowances=[
    ...ALLOWANCE_PRESETS.filter(a=>allowances[a.key]?.checked)
      .map(a=>({name:a.name,amount:allowances[a.key].amount,nonTaxable:a.nonTaxable})),
    ...customAllowances.filter(a=>a.name&&a.amount>0)
      .map(a=>({name:a.name,amount:a.amount,nonTaxable:a.nonTaxable})),
  ];

  const er=calcEmployee({baseSalary:base,dependents:dep,allowances:activeAllowances});
  const ir=calcInstructor(inv);

  return(
    <div>
      <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:"0 0 18px"}}>급여 계산기</h1>
      <div style={{display:"flex",gap:0,marginBottom:18,borderBottom:`1px solid ${C.border}`}}>
        {[["emp","직원 4대보험"],["ins","‍강사 원천징수"]].map(([v,l])=>(
          <button key={v} onClick={()=>setT(v)} style={{padding:"9px 22px",background:"none",border:"none",borderBottom:`3px solid ${t===v?C.blue:"transparent"}`,color:t===v?C.blueL:C.muted,fontSize:13,fontWeight:700,cursor:"pointer"}}>{l}</button>
        ))}
      </div>
      {t==="emp"&&(
        <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:18}}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* 기본급 */}
            <Card>
              <h3 style={{fontSize:13,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>기본 정보</h3>
              <label style={{display:"block",fontSize:11,color:C.muted,marginBottom:4,fontWeight:700}}>기본급</label>
              <input
                type="text" value={baseRaw}
                onChange={e=>{
                  const raw=e.target.value.replace(/[^0-9]/g,"");
                  setBaseRaw(raw?fmtComma(raw):"0");
                }}
                style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 11px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box",textAlign:"right"}}
              />
              <div style={{fontSize:11,color:C.blueL,marginTop:4,marginBottom:10}}>{fmtW(base)}</div>
              <label style={{display:"block",fontSize:11,color:C.muted,marginBottom:6,fontWeight:700}}>부양가족 수</label>
              <div style={{display:"flex",gap:5}}>
                {[0,1,2,3,4,5].map(n=>(
                  <button key={n} onClick={()=>setDep(n)}
                    style={{flex:1,padding:"8px 0",borderRadius:7,border:`1px solid ${dep===n?C.blue:C.border}`,background:dep===n?"#1e3a5f":C.card,color:dep===n?C.blueL:C.muted,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    {n}
                  </button>
                ))}
              </div>
            </Card>

            {/* 수당 항목 체크박스 */}
            <Card>
              <h3 style={{fontSize:13,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>수당 항목</h3>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {ALLOWANCE_PRESETS.map(a=>(
                  <div key={a.key}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:allowances[a.key]?.checked?6:0}}>
                      <input type="checkbox" checked={!!allowances[a.key]?.checked} onChange={()=>toggleAllow(a.key)}
                        style={{width:15,height:15,cursor:"pointer",flexShrink:0}}/>
                      <span style={{fontSize:12,fontWeight:700,color:allowances[a.key]?.checked?C.text:C.muted,flex:1}}>{a.name}</span>
                      <span style={{fontSize:10,padding:"1px 7px",borderRadius:10,fontWeight:700,
                        background:a.nonTaxable?"#dcfce7":"#fee2e2",
                        color:a.nonTaxable?"#15803d":"#b91c1c"}}>
                        {a.nonTaxable?"비과세":"과세"}
                      </span>
                    </div>
                    {allowances[a.key]?.checked&&(
                      <div style={{paddingLeft:23}}>
                        <input type="text" value={allowances[a.key].amountRaw||fmtComma(allowances[a.key].amount)}
                          onChange={e=>{
                            const raw=e.target.value.replace(/[^0-9]/g,"");
                            setAmount(a.key,raw||"0");
                          }}
                          style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"6px 10px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box",textAlign:"right"}}/>
                        <div style={{fontSize:10,color:C.blueL,marginTop:2}}>{fmtW(allowances[a.key].amount)}</div>
                      </div>
                    )}
                  </div>
                ))}

                {/* 추가 수당 */}
                {customAllowances.map(a=>(
                  <div key={a.id} style={{borderTop:`1px solid ${C.border}`,paddingTop:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                      <input type="text" value={a.name} onChange={e=>updateCustomAllowance(a.id,"name",e.target.value)}
                        placeholder="수당명 입력"
                        style={{flex:1,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"5px 8px",color:C.text,fontSize:12,outline:"none"}}/>
                      <label style={{display:"flex",alignItems:"center",gap:3,fontSize:11,color:C.muted,whiteSpace:"nowrap",cursor:"pointer"}}>
                        <input type="checkbox" checked={!!a.nonTaxable} onChange={e=>updateCustomAllowance(a.id,"nonTaxable",e.target.checked)}/> 비과세
                      </label>
                      <button onClick={()=>removeCustomAllowance(a.id)}
                        style={{background:"#450a0a",border:"none",borderRadius:6,color:"#f87171",fontSize:11,padding:"3px 8px",cursor:"pointer",fontWeight:700}}>삭제</button>
                    </div>
                    <input type="text" value={a.amountRaw}
                      onChange={e=>{
                        const raw=e.target.value.replace(/[^0-9]/g,"");
                        updateCustomAllowance(a.id,"amount",raw||"0");
                      }}
                      style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"6px 10px",color:C.text,fontSize:12,outline:"none",boxSizing:"border-box",textAlign:"right"}}/>
                    <div style={{fontSize:10,color:C.blueL,marginTop:2}}>{fmtW(a.amount)}</div>
                  </div>
                ))}

                <button onClick={addCustomAllowance}
                  style={{marginTop:6,background:"none",border:`1px dashed ${C.border2}`,borderRadius:7,color:C.muted,fontSize:12,padding:"6px",cursor:"pointer",fontWeight:700}}>
                  + 수당 추가
                </button>
              </div>
              {activeAllowances.length>0&&(
                <div style={{marginTop:12,padding:"9px 12px",background:C.dim,borderRadius:8,fontSize:11,color:C.muted}}>
                  수당 합계 <strong style={{color:C.text}}>{fmtW(activeAllowances.reduce((s,a)=>s+a.amount,0))}</strong>
                  　비과세 <strong style={{color:C.greenL}}>{fmtW(Math.min(activeAllowances.filter(a=>a.nonTaxable).reduce((s,a)=>s+a.amount,0),200000))}</strong>
                </div>
              )}
            </Card>
          </div>

          {/* 계산 결과 */}
          <Card>
            <h3 style={{fontSize:13,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>계산 결과</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:11,marginBottom:18}}>
              {[{l:"총 지급액",v:fmtW(er.gross),c:C.greenL},{l:"공제 합계",v:fmtW(er.totalDeduct),c:C.redL},{l:"실수령액",v:fmtW(er.netPay),c:C.blueL}].map(s=>(
                <div key={s.l} style={{background:C.thead,borderRadius:9,padding:"13px",border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:4,fontWeight:700}}>{s.l}</div>
                  <div style={{fontSize:16,fontWeight:900,color:s.c}}>{s.v}</div>
                </div>
              ))}
            </div>

            {/* 지급 내역 */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8,letterSpacing:"0.06em"}}>지급 내역</div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.muted}}>기본급</span>
                <span style={{color:C.greenL,fontWeight:600,fontFamily:"monospace"}}>{fmtW(base)}</span>
              </div>
              {activeAllowances.map((a,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                  <span style={{color:C.muted}}>
                    {a.name}
                    <span style={{fontSize:10,marginLeft:6,color:a.nonTaxable?C.greenL:C.redL}}>({a.nonTaxable?"비과세":"과세"})</span>
                  </span>
                  <span style={{color:C.greenL,fontWeight:600,fontFamily:"monospace"}}>{fmtW(a.amount)}</span>
                </div>
              ))}
            </div>

            {/* 공제 내역 */}
            <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8,letterSpacing:"0.06em"}}>공제 내역</div>
            {[{l:"국민연금 (4.75%)",v:er.pension},{l:"건강보험 (3.595%)",v:er.health},{l:"장기요양보험",v:er.ltc},{l:"고용보험 (0.9%)",v:er.employ},{l:"소득세",v:er.incomeTax},{l:"주민세",v:er.residentTax}].map((r,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.muted}}>{r.l}</span>
                <span style={{color:C.redL,fontWeight:600,fontFamily:"monospace"}}>{fmtW(r.v)}</span>
              </div>
            ))}

            {/* 회사 부담 */}
            <div style={{marginTop:14,padding:"12px 14px",background:C.dim,borderRadius:9,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:11,fontWeight:700,color:C.amberL,marginBottom:8}}>회사 부담 보험료 (참고)</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
                {[
                  {l:"국민연금",v:Math.round(er.penBasis*RATES.pension.er)},
                  {l:"건강보험",v:Math.round(er.taxable*RATES.health.er)},
                  {l:"장기요양",v:Math.round(Math.round(er.taxable*RATES.health.er)*RATES.ltc.rate)},
                  {l:"고용보험",v:er.isExempt?0:Math.round(er.taxable*RATES.employ.er)},
                ].map(s=>(
                  <div key={s.l} style={{background:C.card,borderRadius:7,padding:"8px 10px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:10,color:C.muted,marginBottom:2}}>{s.l}</div>
                    <div style={{fontSize:12,fontWeight:700,color:C.amberL,fontFamily:"monospace"}}>{fmtW(s.v)}</div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      )}
      {t==="ins"&&(
        <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:18}}>
          <Card>
            <h3 style={{fontSize:13,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>입력</h3>
            <MoneyInp label="청구금액" value={inv} onChange={v=>setInv(v)}/>
            <div style={{fontSize:11,color:C.purpleL,marginTop:-8,marginBottom:14}}>{fmtW(inv)}</div>
            <div style={{padding:"12px",background:C.subBox,borderRadius:8,border:`1px solid ${C.border}`,fontSize:11,color:C.muted,lineHeight:1.9}}>
              <div style={{fontWeight:700,color:C.purpleL,marginBottom:4}}>적용 법령</div>
              <div>소득세법 제129조 제1항 제3호</div>
              <div>사업소득 원천징수율: <strong style={{color:C.amberL}}>3.0%</strong></div>
              <div>지방소득세 (소득세×10%): <strong style={{color:C.amberL}}>0.3%</strong></div>
              <div style={{marginTop:4,fontWeight:700,color:C.purpleL}}>합계: 3.3%</div>
            </div>
          </Card>
          <Card>
            <h3 style={{fontSize:13,fontWeight:800,color:C.muted,marginTop:0,marginBottom:14}}>원천징수 계산</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:11,marginBottom:18}}>
              {[{l:"청구금액",v:fmtW(ir.gross),c:C.greenL},{l:"원천징수액",v:fmtW(ir.totalDeduct),c:C.redL},{l:"실지급액",v:fmtW(ir.netPay),c:C.purpleL}].map(s=>(
                <div key={s.l} style={{background:C.thead,borderRadius:9,padding:"13px",border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:10,color:C.muted,marginBottom:4,fontWeight:700}}>{s.l}</div>
                  <div style={{fontSize:16,fontWeight:900,color:s.c}}>{s.v}</div>
                </div>
              ))}
            </div>
            {[{l:"청구금액",v:ir.gross,c:C.greenL},{l:"소득세 (3%)",v:ir.incomeTax,c:C.redL},{l:"주민세 (0.3%)",v:ir.residentTax,c:C.redL},{l:"실지급액",v:ir.netPay,c:C.purpleL}].map((r,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.muted}}>{r.l}</span>
                <span style={{color:r.c,fontWeight:700,fontFamily:"monospace"}}>{fmtW(r.v)}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── 발송 관리 페이지 (Step 4)
function SendPage({employees,instructors,logs,setLogs,onPreviewPayslip}){
  const [cTab,setCTab]=useState("closed");
  const [sending,setSending]=useState(false);
  const [prog,setProg]=useState(0);
  const [previewFilter,setPreviewFilter]=useState("all");
  const [logsLoading,setLogsLoading]=useState(false);
  const all=[
    ...employees.filter(e=>e.status==="active").map(p=>({...p,ptype:"employee"})),
    ...instructors.filter(i=>i.status==="active").map(p=>({...p,ptype:"instructor"})),
  ];
  const filtered=previewFilter==="all"?all:all.filter(p=>p.ptype===previewFilter);

  // DB에서 발송 이력 로드
  const loadLogs=async()=>{
    setLogsLoading(true);
    try{
      const data=await api("GET","/sendlogs");
      setLogs(data.map(l=>({
        id:l.id,
        name:l.person_name,
        ptype:l.person_type,
        channel:l.channel,
        contact:l.contact,
        status:l.status,
        time:new Date(l.created_at).toLocaleTimeString("ko-KR"),
        error:l.error_msg||"",
      })));
    }catch(e){ console.error("발송 이력 로드 실패:",e.message); }
    finally{ setLogsLoading(false); }
  };

  useEffect(()=>{ loadLogs(); },[]);

  // DB에 로그 저장 + 로컬 상태 업데이트
  const addLog=async(e)=>{
    setLogs(p=>[{id:Date.now(),...e},...p]);
    try{
      await api("POST","/sendlog",{
        personName:e.name, personType:e.ptype||"",
        channel:e.channel, contact:e.contact, status:e.status,
      });
    }catch(err){ console.error("로그 저장 실패:",err.message); }
  };

  const sendOne=async(p,ch)=>{
    const contact=ch==="email"?p.email:p.phone;
    try{
      await simulateSend(ch,p,THIS_MONTH);
      await addLog({name:p.name,ptype:p.ptype,channel:ch,contact,status:"성공",time:new Date().toLocaleTimeString("ko-KR")});
    }catch(e){
      await addLog({name:p.name,ptype:p.ptype,channel:ch,contact,status:"실패",time:new Date().toLocaleTimeString("ko-KR"),error:e.message});
    }
  };

  const bulkSend=async(ch)=>{
    setSending(true);setProg(0);
    for(let i=0;i<all.length;i++){
      await sendOne(all[i],ch);
      setProg(Math.round(((i+1)/all.length)*100));
    }
    setSending(false);setProg(0);
    alert(`✅ ${ch==="email"?"이메일":"SMS"} 일괄 발송 완료! (${all.length}명)`);
  };

  const [checked,setChecked]=useState({});
  const [sentIds,setSentIds]=useState(new Set());
  const [partialSending,setPartialSending]=useState(false);

  // 발송 완료 처리 — 목록에서 제외
  const markSent=(id)=>setSentIds(p=>new Set([...p,id]));

  // 체크박스 토글
  const toggleCheck=(id)=>setChecked(p=>({...p,[id]:!p[id]}));
  const toggleAll=()=>{
    const visible=filtered.filter(p=>!sentIds.has(p.id));
    const allChecked=visible.every(p=>checked[p.id]);
    const next={};
    visible.forEach(p=>{ next[p.id]=!allChecked; });
    setChecked(p=>({...p,...next}));
  };
  const checkedList=filtered.filter(p=>!sentIds.has(p.id)&&checked[p.id]);
  const pendingList=filtered.filter(p=>!sentIds.has(p.id));

  // 선택 발송
  const sendSelected=async(ch)=>{
    if(checkedList.length===0){alert("발송할 대상을 선택해 주세요.");return;}
    if(!window.confirm(`${checkedList.length}명에게 ${ch==="email"?"이메일":"SMS"}을 발송하시겠습니까?`))return;
    setPartialSending(true);
    for(const p of checkedList){
      await sendOne(p,ch);
      markSent(p.id);
      setChecked(prev=>({...prev,[p.id]:false}));
    }
    setPartialSending(false);
    alert(`✅ ${checkedList.length}명 발송 완료!`);
  };

  // 개별 즉시 발송
  const sendOneAndMark=async(p,ch)=>{
    await sendOne(p,ch);
    markSent(p.id);
  };

  const CODE_TABS=[{id:"sendgrid",l:"SendGrid"},{id:"sms",l:"알리고 SMS"},{id:"scheduler",l:"자동 스케줄러"},{id:"retry",l:"♻️ 재시도 로직"}];

  return(
    <div>
      <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:"0 0 5px"}}>명세서 발송 관리</h1>
      <div style={{fontSize:13,color:C.muted,marginBottom:20}}>SendGrid 이메일 + 알리고 SMS 연동</div>

      {/* 일괄 발송 */}
      <Card style={{marginBottom:18}}>
        <h3 style={{fontSize:14,fontWeight:800,color:C.text,marginTop:0,marginBottom:12}}>{THIS_MONTH} 전체 일괄 발송</h3>
        <div style={{display:"flex",gap:11,alignItems:"center",flexWrap:"wrap",marginBottom:12}}>
          <Btn variant="success" size="lg" onClick={()=>bulkSend("email")} disabled={sending}>{sending?"⏳ 발송 중...":"전체 이메일 일괄 발송"}</Btn>
          <Btn variant="primary" size="lg" onClick={()=>bulkSend("sms")} disabled={sending}>{sending?"⏳ 발송 중...":"전체 SMS 일괄 발송"}</Btn>
          <span style={{fontSize:12,color:C.muted}}>대상: {all.length}명 (직원 {employees.filter(e=>e.status==="active").length} + 강사 {instructors.filter(i=>i.status==="active").length})</span>
        </div>
        {sending&&(
          <div>
            <div style={{height:7,background:C.border,borderRadius:4,overflow:"hidden",marginBottom:5}}>
              <div style={{height:"100%",width:`${prog}%`,background:"linear-gradient(90deg,#3b82f6,#22c55e)",borderRadius:4,transition:"width .3s"}}/>
            </div>
            <div style={{fontSize:11,color:C.blueL}}>발송 중... {prog}%</div>
          </div>
        )}
      </Card>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18}}>

        {/* 개별 발송 — 체크박스 선택 */}
        <Card>
          {/* 헤더 */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <h3 style={{fontSize:14,fontWeight:800,color:C.text,margin:0}}>
              개별 발송
              {sentIds.size>0&&<span style={{fontSize:11,color:C.greenL,fontWeight:600,marginLeft:8}}>✓ {sentIds.size}명 발송완료</span>}
            </h3>
            <div style={{display:"flex",gap:0,background:C.thead,borderRadius:8,padding:3}}>
              {[["all","전체"],["employee","직원"],["instructor","강사"]].map(([v,l])=>(
                <button key={v} onClick={()=>setPreviewFilter(v)}
                  style={{padding:"4px 10px",borderRadius:6,background:previewFilter===v?C.dim:"transparent",color:previewFilter===v?C.blueL:C.muted,border:"none",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* 선택 발송 툴바 */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:C.thead,borderRadius:8,marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="checkbox"
                checked={pendingList.length>0&&pendingList.every(p=>checked[p.id])}
                onChange={toggleAll}
                style={{width:15,height:15,cursor:"pointer"}}/>
              <span style={{fontSize:12,color:C.muted}}>
                {checkedList.length>0
                  ?<strong style={{color:C.text}}>{checkedList.length}명 선택됨</strong>
                  :`미발송 ${pendingList.length}명`}
              </span>
            </div>
            <div style={{display:"flex",gap:6}}>
              <Btn size="sm" variant="success" onClick={()=>sendSelected("email")} disabled={partialSending||checkedList.length===0}>
                {partialSending?"발송 중...":"선택 이메일"}
              </Btn>
              <Btn size="sm" variant="primary" onClick={()=>sendSelected("sms")} disabled={partialSending||checkedList.length===0}>
                {partialSending?"발송 중...":"선택 SMS"}
              </Btn>
            </div>
          </div>

          {/* 목록 */}
          <div style={{maxHeight:320,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
            {pendingList.length===0?(
              <div style={{textAlign:"center",color:C.greenL,padding:"36px 0",fontSize:13,fontWeight:700}}>
                ✅ 모든 대상에게 발송 완료!
              </div>
            ):pendingList.map(p=>{
              const calc=p.ptype==="employee"?calcEmployee(p):calcInstructor(p.invoiceAmount);
              const isChecked=!!checked[p.id];
              return(
                <div key={p.id} style={{background:isChecked?C.dim:C.card,borderRadius:10,border:`2px solid ${isChecked?C.blue:C.border}`,overflow:"hidden",transition:"all .15s"}}>
                  <div style={{display:"flex",alignItems:"center",padding:"10px 13px",gap:10}}>
                    {/* 체크박스 */}
                    <input type="checkbox" checked={isChecked} onChange={()=>toggleCheck(p.id)}
                      style={{width:16,height:16,cursor:"pointer",flexShrink:0}}/>
                    {/* 인원 정보 */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,color:C.text,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                        {p.name}
                        <Badge color={p.ptype==="employee"?"blue":"purple"}>{p.ptype==="employee"?"직원":"강사"}</Badge>
                      </div>
                      <div style={{fontSize:10,color:C.muted,marginTop:2}}>{p.email} · 실수령 <strong style={{color:C.blueL}}>{fmtW(calc.netPay)}</strong></div>
                    </div>
                    {/* 버튼 */}
                    <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
                      <button onClick={()=>onPreviewPayslip(p,p.ptype)}
                        style={{background:C.dim,border:`1px solid ${C.border2}`,color:C.blueL,borderRadius:7,padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        </button>
                      <Btn size="sm" variant="success" onClick={()=>sendOneAndMark(p,"email")}></Btn>
                      <Btn size="sm" variant="primary" onClick={()=>sendOneAndMark(p,"sms")}></Btn>
                    </div>
                  </div>
                  {/* 급여 요약 바 */}
                  <div style={{display:"flex",fontSize:10,borderTop:`1px solid ${C.border}`}}>
                    <div style={{flex:1,padding:"5px 10px",color:C.muted}}>총지급 <strong style={{color:C.greenL}}>{fmtW(calc.gross)}</strong></div>
                    <div style={{flex:1,padding:"5px 10px",color:C.muted,borderLeft:`1px solid ${C.border}`}}>공제 <strong style={{color:C.redL}}>{fmtW(calc.totalDeduct)}</strong></div>
                    <div style={{flex:1,padding:"5px 10px",color:C.muted,borderLeft:`1px solid ${C.border}`}}>실수령 <strong style={{color:C.blueL}}>{fmtW(calc.netPay)}</strong></div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* 발송 이력 */}
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{fontSize:14,fontWeight:800,color:C.text,margin:0}}>발송 이력</h3>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:11,color:C.muted}}>{logs.filter(l=>l.status==="성공").length}성공 / {logs.filter(l=>l.status==="실패").length}실패</span>
              <Btn size="sm" variant="ghost" onClick={loadLogs}>새로고침</Btn>
            </div>
          </div>
          {logsLoading
            ?<div style={{textAlign:"center",color:C.muted,padding:"36px 0",fontSize:13}}>불러오는 중...</div>
            :logs.length===0
              ?<div style={{textAlign:"center",color:C.muted,padding:"36px 0",fontSize:13}}>아직 발송 이력이 없습니다</div>
              :(
                <div style={{maxHeight:290,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
                  {logs.slice(0,40).map(l=>(
                    <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 11px",background:C.logItem,borderRadius:8,border:`1px solid ${C.border}`}}>
                      <div style={{display:"flex",gap:7,alignItems:"center"}}>
                        <span style={{fontSize:13}}>{l.channel==="email"?"":""}</span>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{l.name}</div>
                          <div style={{fontSize:10,color:C.muted}}>{l.contact}</div>
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <Badge color={l.status==="성공"?"green":"red"}>{l.status}</Badge>
                        <div style={{fontSize:10,color:C.muted,marginTop:1}}>{l.time}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )
          }
        </Card>
      </div>

      {/* Step 4 코드 뷰어 — 접기/펼치기 */}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}} onClick={()=>setCTab(p=>p==="closed"?"sendgrid":"closed")}>
          <h3 style={{fontSize:14,fontWeight:800,color:C.text,margin:0}}>Step 4 — 발송 시스템 연동 코드</h3>
          <span style={{fontSize:13,color:C.muted}}>{cTab==="closed"?"▼ 펼치기":"▲ 접기"}</span>
        </div>
        {cTab!=="closed"&&(
          <div style={{marginTop:14}}>
            <div style={{display:"flex",gap:0,marginBottom:14,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap"}}>
              {CODE_TABS.map(t=>(
                <button key={t.id} onClick={()=>setCTab(t.id)} style={{padding:"8px 16px",background:"none",border:"none",borderBottom:`2px solid ${cTab===t.id?C.blueL:"transparent"}`,color:cTab===t.id?C.blueL:C.muted,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                  {t.l}
                </button>
              ))}
            </div>
            <CodeBlock code={SEND_CODE[cTab]}/>
            <div style={{marginTop:10,padding:"9px 13px",background:C.noteBox,borderRadius:8,fontSize:11,color:C.muted}}>
              {cTab==="sendgrid"&&"npm install @sendgrid/mail  |  환경변수: SENDGRID_API_KEY"}
              {cTab==="sms"&&"npm install axios form-data  |  환경변수: ALIGO_API_KEY, ALIGO_USER_ID"}
              {cTab==="scheduler"&&"npm install node-cron  |  매월 25일 09:00 (Asia/Seoul) 자동 실행"}
              {cTab==="retry"&&"♻️ 30분마다 실패 발송 자동 재시도 (최대 3회)"}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// ★ 메인 앱

// ════════════════════════════════════════════════════════════
// ★ 강사 포털 — 로그인 + 청구서/강의 세부내역 관리
// ════════════════════════════════════════════════════════════

// ── 강사 로그인 화면
function InstructorLogin({instructors, onLogin}){
  const [name,setName]=useState("");
  const [birth,setBirth]=useState("");
  const [error,setError]=useState("");
  const [shake,setShake]=useState(false);

  // yyyy.mm.dd → yyyy-mm-dd 변환해서 비교
  const normalizeBirth=(s)=>s.replace(/\./g,"-").trim();

  // 입력 자동 포맷: 숫자만 받아서 자동으로 . 삽입
  const handleBirth=(e)=>{
    let v=e.target.value.replace(/[^0-9.]/g,"");
    // 숫자만 추출 후 포맷
    const digits=v.replace(/\./g,"");
    let formatted="";
    if(digits.length<=4) formatted=digits;
    else if(digits.length<=6) formatted=digits.slice(0,4)+"."+digits.slice(4);
    else formatted=digits.slice(0,4)+"."+digits.slice(4,6)+"."+digits.slice(6,8);
    setBirth(formatted);
    setError("");
  };

  const tryLogin=()=>{
    if(!name.trim()||!birth.trim()){setError("성명과 생년월일을 모두 입력해 주세요.");return;}
    const normalized=normalizeBirth(birth);
    const found=instructors.find(i=>
      i.name.trim()===name.trim() &&
      i.birthdate===normalized &&
      i.status==="active"
    );
    if(found){
      onLogin(found);
    } else {
      setError("이름 또는 생년월일이 일치하지 않거나 등록되지 않은 강사입니다.");
      setShake(true);
      setTimeout(()=>setShake(false),500);
    }
  };

  const birthOk=birth.match(/^\d{4}\.\d{2}\.\d{2}$/);

  // ── 강사 포털 전용 라이트 팔레트
  const IP={
    bg:"#f0f4ff", card:"#ffffff", border:"#dde5f4", border2:"#c7d4ef",
    text:"#1e2a4a", muted:"#6475a0", slate:"#8496bf",
    inputBg:"#ffffff", dim:"#eef2fb",
    purple:"#7c3aed", purpleL:"#9f67f5", purpleDim:"#f3eeff", purpleBorder:"#d4b8fc",
    green:"#16a34a", red:"#dc2626",
  };

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(145deg,#eef2ff 0%,#f5f0ff 50%,#eff6ff 100%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif",position:"relative",overflow:"hidden"}}>
      {/* 배경 장식 */}
      <div style={{position:"absolute",top:"5%",left:"5%",width:350,height:350,borderRadius:"50%",background:"rgba(124,58,237,0.07)",filter:"blur(70px)",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:"5%",right:"5%",width:280,height:280,borderRadius:"50%",background:"rgba(59,130,246,0.07)",filter:"blur(60px)",pointerEvents:"none"}}/>

      <div style={{width:"100%",maxWidth:420,padding:"16px",boxSizing:"border-box",position:"relative",zIndex:1}}>
        <div style={{background:IP.card,border:`1px solid ${IP.border}`,borderRadius:22,padding:"36px 28px",boxShadow:"0 20px 60px rgba(124,58,237,0.1),0 4px 16px rgba(0,0,0,0.06)",animation:shake?"shake 0.4s ease":"none"}}>

          {/* 로고 */}
          <div style={{textAlign:"center",marginBottom:30}}>
            <img src="data:image/png;base64,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" alt="logo" style={{width:72,height:72,objectFit:"contain",marginBottom:14}}/>
            <div style={{fontSize:21,fontWeight:900,color:IP.text,marginBottom:4,letterSpacing:"-0.3px"}}>강사 포털</div>
            <div style={{fontSize:12,color:IP.muted,letterSpacing:"0.06em"}}>Instructor Self-Service Portal</div>
          </div>

          {/* 안내 박스 */}
          <div style={{background:IP.purpleDim,border:`1px solid ${IP.purpleBorder}`,borderRadius:10,padding:"11px 14px",marginBottom:24,fontSize:12,color:IP.muted,lineHeight:1.8}}>
            <div style={{fontWeight:700,color:IP.purple,marginBottom:2}}>로그인 안내</div>
            <div>관리자에게 등록된 강사만 접속 가능합니다.</div>
            <div>이름과 생년월일이 일치해야 로그인됩니다.</div>
          </div>

          {/* 성명 */}
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:11,color:IP.purple,fontWeight:700,marginBottom:6,letterSpacing:"0.05em"}}>성명</label>
            <input value={name} onChange={e=>{setName(e.target.value);setError("");}}
              onKeyDown={e=>e.key==="Enter"&&tryLogin()}
              placeholder="등록된 성명 입력"
              autoFocus
              style={{width:"100%",background:IP.inputBg,border:`1px solid ${error?IP.red:IP.border2}`,borderRadius:10,padding:"12px 14px",color:IP.text,fontSize:14,outline:"none",boxSizing:"border-box",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.04)"}}/>
          </div>

          {/* 생년월일 */}
          <div style={{marginBottom:24}}>
            <label style={{display:"block",fontSize:12,color:IP.purple,fontWeight:800,marginBottom:6,letterSpacing:"0.05em"}}>
              생년월일 <span style={{color:IP.text,fontWeight:700,fontSize:12}}>yyyy.mm.dd 형식</span>
            </label>
            <div style={{position:"relative"}}>
              <input
                value={birth}
                onChange={handleBirth}
                onKeyDown={e=>e.key==="Enter"&&tryLogin()}
                placeholder="1990.05.14"
                maxLength={10}
                style={{width:"100%",background:IP.inputBg,border:`1px solid ${error?IP.red:birthOk?IP.green:IP.border2}`,borderRadius:10,padding:"12px 44px 12px 14px",color:IP.text,fontSize:15,outline:"none",boxSizing:"border-box",fontFamily:"monospace",letterSpacing:"0.08em",transition:"border .2s",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.04)"}}
              />
              <div style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:14}}>
                {birth.length===0?"":birthOk?"✅":"⌛"}
              </div>
            </div>
            <div style={{marginTop:5,display:"flex",gap:0,alignItems:"center"}}>
              {["yyyy",".","mm",".","dd"].map((seg,i)=>(
                <span key={i} style={{fontSize:11,color:i%2===0?IP.text:IP.purple,fontFamily:"monospace",fontWeight:700}}>{seg}</span>
              ))}
              <span style={{fontSize:11,color:IP.text,marginLeft:6,fontWeight:700}}>— 숫자 입력 시 자동 완성</span>
            </div>
          </div>

          {error&&(
            <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:9,padding:"10px 14px",marginBottom:16,fontSize:12,color:IP.red,lineHeight:1.6,display:"flex",gap:6,alignItems:"flex-start"}}>
              ⚠️ {error}
            </div>
          )}

          <button onClick={tryLogin}
            style={{width:"100%",background:"linear-gradient(135deg,#7c3aed,#6d28d9)",color:"#fff",border:"none",borderRadius:11,padding:"14px",fontSize:14,fontWeight:800,cursor:"pointer",letterSpacing:"0.02em",boxShadow:"0 6px 20px rgba(124,58,237,0.35)"}}>
            로그인
          </button>
          <div style={{marginTop:18,textAlign:"center",fontSize:12,color:IP.text,fontWeight:600}}>로그인 문제는 관리자(인사팀)에게 문의하세요</div>
        </div>
      <style dangerouslySetInnerHTML={{__html:`@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}`}}/>
      </div>
    </div>
  );
}

// ── 강의 세부내역 행 (순서-날짜-강의과목-교육시간-단가-강의료-메모)
function LectureRow({row,onChange,onDelete,idx}){
  const cs={
    background:"#fff",
    border:"1px solid #94a3b8",   // 더 진한 테두리
    borderRadius:5,padding:"6px 8px",
    color:"#0f172a",              // 더 진한 글자
    fontSize:12,fontWeight:500,
    outline:"none",width:"100%",boxSizing:"border-box"
  };

  const handleDate=(e)=>{
    const digits=e.target.value.replace(/[^0-9]/g,"");
    let f="";
    if(digits.length<=4) f=digits;
    else if(digits.length<=6) f=digits.slice(0,4)+"."+digits.slice(4);
    else f=digits.slice(0,4)+"."+digits.slice(4,6)+"."+digits.slice(6,8);
    onChange("date",f);
  };

  const handleHours=(v)=>{
    const hours=Number(v)||0;
    onChange("hours",hours);
    onChange("fee", Math.round(hours * (row.unitPrice||0)));
  };
  const handleUnitPrice=(v)=>{
    const unitPrice=Number(v)||0;
    onChange("unitPrice",unitPrice);
    onChange("fee", Math.round((row.hours||0) * unitPrice));
  };

  return(
    <tr style={{borderBottom:"1px solid #cbd5e1",background:idx%2===0?"#f8fafc":"#fff"}}>
      {/* 순서 */}
      <td style={{padding:"7px 8px",color:"#475569",fontSize:12,fontWeight:600,textAlign:"center",background:"#f1f5f9",width:28}}>{idx+1}</td>
      {/* 날짜 */}
      <td style={{padding:"5px 5px",width:106}}>
        <input value={row.date} onChange={handleDate} placeholder="2025.04.01" maxLength={10}
          style={{...cs,fontFamily:"monospace",fontSize:12,letterSpacing:"0.04em",width:98}}/>
      </td>
      {/* 강의 과목/내용 */}
      <td style={{padding:"5px 5px"}}>
        <input value={row.subject} onChange={e=>onChange("subject",e.target.value)} placeholder="강의 과목/내용"
          style={{...cs,minWidth:130}}/>
      </td>
      {/* 교육시간 */}
      <td style={{padding:"5px 5px",width:68}}>
        <input type="number" value={row.hours} onChange={e=>handleHours(e.target.value)} min={0.5} step={0.5}
          style={{...cs,textAlign:"right",width:58}}/>
      </td>
      {/* 단가 */}
      <td style={{padding:"5px 5px",width:100}}>
        <input type="text" value={(row.unitPrice||0).toLocaleString("ko-KR")}
          onChange={e=>handleUnitPrice(e.target.value.replace(/,/g,""))} placeholder="단가"
          style={{...cs,textAlign:"right",width:92}}/>
      </td>
      {/* 강의료 (자동계산 — 읽기전용) */}
      <td style={{padding:"5px 5px",width:108}}>
        <div style={{background:"#eff6ff",border:"1px solid #3b82f6",color:"#1e40af",fontWeight:700,
          fontFamily:"monospace",padding:"6px 8px",borderRadius:5,fontSize:12,textAlign:"right",width:98,boxSizing:"border-box"}}>
          {(row.fee||0).toLocaleString("ko-KR")}
        </div>
      </td>
      {/* 메모 */}
      <td style={{padding:"5px 5px"}}>
        <input value={row.memo} onChange={e=>onChange("memo",e.target.value)} placeholder="메모"
          style={{...cs,minWidth:90}}/>
      </td>
      {/* 삭제 */}
      <td style={{padding:"5px 6px",textAlign:"center",width:36}}>
        <button onClick={onDelete} style={{background:"#fee2e2",border:"1px solid #fca5a5",color:"#dc2626",borderRadius:5,padding:"3px 7px",fontSize:11,cursor:"pointer",fontWeight:700}}>✕</button>
      </td>
    </tr>
  );
}

// ── 청구서 문서 양식 (전체화면 A4 스타일)
function InvoiceForm({open,onClose,initial,instructor,onSave}){
  const thisMonth=`${new Date().getFullYear()}년 ${new Date().getMonth()+1}월`;
  const makeEmpty=()=>({
    id:`INV-${instructor.id}-${Date.now()}`,
    month:thisMonth,invoiceAmount:0,
    lectures:[{id:Date.now(),date:"",subject:"",hours:1,unitPrice:0,fee:0,memo:""}],
    memo:"",status:"작성중",createdAt:new Date().toLocaleDateString("ko-KR")
  });
  const [f,setF]=useState(makeEmpty());
  const [savedMsg,setSavedMsg]=useState("");

  useEffect(()=>{setF(initial?{...initial}:makeEmpty());setSavedMsg("");},[open,initial]);

  const setField=(k,v)=>setF(p=>({...p,[k]:v}));
  const addLecture=()=>setF(p=>({...p,lectures:[...p.lectures,{id:Date.now(),date:"",subject:"",hours:1,unitPrice:0,fee:0,memo:""}]}));
  const updLecture=(i,k,v)=>setF(p=>{const l=[...p.lectures];l[i]={...l[i],[k]:v};return{...p,lectures:l};});
  const delLecture=i=>setF(p=>({...p,lectures:p.lectures.filter((_,j)=>j!==i)}));

  const lectureTotal=f.lectures.reduce((s,l)=>s+(l.fee||0),0);
  const totalHours=f.lectures.reduce((s,l)=>s+(l.hours||0),0);
  const tax=calcInstructor(lectureTotal);

  // 임시저장
  const handleTempSave=()=>{
    onSave({...f,invoiceAmount:lectureTotal,status:"작성중"});
    setSavedMsg("임시저장 완료");
    setTimeout(()=>setSavedMsg(""),2200);
  };
  // 청구서 제출
  const handleSubmit=()=>{
    if(!window.confirm("청구서를 제출하시겠습니까?\n제출 후에도 관리자 승인 전에는 수정할 수 있습니다."))return;
    onSave({...f,invoiceAmount:lectureTotal,status:"제출완료"});
    setSavedMsg("제출 완료! 관리자 검토 후 승인됩니다.");
    setTimeout(()=>{setSavedMsg("");onClose();},1800);
  };

  const isSubmitted=f.status==="제출완료"||f.status==="승인";
  const isApproved=f.status==="승인";

  if(!open)return null;

  const TH={background:"#1c3557",color:"#fff",fontWeight:700,fontSize:11,padding:"8px 12px",textAlign:"left",whiteSpace:"nowrap"};
  const THc={...TH,textAlign:"center"};
  const THr={...TH,textAlign:"right"};

  return(
    <div style={{position:"fixed",inset:0,background:"#eef2ff",zIndex:2000,overflowY:"auto",display:"flex",flexDirection:"column",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',Arial,sans-serif"}}>

      {/* 상단 툴바 */}
      <div style={{background:"#fff",borderBottom:"1px solid #dde5f4",padding:"0 24px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,position:"sticky",top:0,zIndex:10,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={onClose} style={{background:"#f0f4ff",border:"1px solid #dde5f4",borderRadius:7,padding:"5px 14px",color:"#6475a0",fontSize:12,cursor:"pointer"}}>← 돌아가기</button>
          <span style={{color:"#8496bf",fontSize:13}}>청구서 {initial?"수정":"작성"}</span>
          <span style={{background:"#f3eeff",color:"#7c3aed",border:"1px solid #d4b8fc",fontSize:11,padding:"2px 10px",borderRadius:20,fontWeight:700}}>{instructor.name} 강사</span>
          <span style={{
            fontSize:11,padding:"2px 10px",borderRadius:20,fontWeight:700,
            background:isApproved?"#f0fdf4":isSubmitted?"#fef9c3":"#f0f4ff",
            color:isApproved?"#15803d":isSubmitted?"#a16207":"#6475a0",
            border:`1px solid ${isApproved?"#86efac":isSubmitted?"#fde68a":"#dde5f4"}`
          }}>
            {isApproved?"✅ 승인완료":isSubmitted?"제출완료":"작성중"}
          </span>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {savedMsg&&<span style={{fontSize:12,color:"#16a34a",fontWeight:600}}>{savedMsg}</span>}
          {!isApproved&&(
            <>
              <button onClick={handleTempSave}
                style={{background:"#f8fafc",border:"1px solid #dde5f4",color:"#6475a0",borderRadius:8,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                임시저장
              </button>
              <button onClick={handleSubmit}
                style={{background:"linear-gradient(135deg,#7c3aed,#6d28d9)",color:"#fff",border:"none",borderRadius:8,padding:"7px 20px",fontSize:13,fontWeight:800,cursor:"pointer",boxShadow:"0 4px 12px rgba(124,58,237,0.3)"}}>
                청구서 제출
              </button>
            </>
          )}
          {isApproved&&<span style={{fontSize:12,color:"#16a34a",fontWeight:600}}>관리자 승인 완료 — 수정 잠금</span>}
        </div>
      </div>

      {/* 승인 전 수정 안내 배너 */}
      {isSubmitted&&!isApproved&&(
        <div style={{background:"#fef9c3",borderBottom:"1px solid #fde68a",padding:"8px 24px",fontSize:12,color:"#92400e",display:"flex",alignItems:"center",gap:8}}>
          ✏️ 제출된 청구서입니다. <strong>관리자 승인 전까지 수정 가능합니다.</strong> 수정 후 다시 [청구서 제출] 버튼을 눌러주세요.
        </div>
      )}

      {/* A4 문서 */}
      <div style={{flex:1,display:"flex",justifyContent:"center",padding:"28px 20px 20px"}}>
        <div style={{width:720,background:"#fff",boxShadow:"0 4px 32px rgba(0,0,0,0.22)",borderRadius:4}}>

          {/* 문서 헤더 */}
          <div style={{background:"linear-gradient(135deg,#1c3557,#1e40af)",padding:"26px 36px 22px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",right:-20,top:-20,width:160,height:160,borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",position:"relative"}}>
              <div>
                <div style={{fontSize:10,color:"#93c5fd",fontWeight:700,letterSpacing:"0.15em",marginBottom:5}}>INVOICE · 강의료 청구서</div>
                <div style={{fontSize:24,fontWeight:900,color:"#fff",letterSpacing:"-0.3px"}}>강 의 료 청 구 서</div>
                <div style={{fontSize:12,color:"#bfdbfe",marginTop:5}}>{f.month} 분</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10,color:"#93c5fd",marginBottom:2}}>문서번호</div>
                <div style={{fontSize:12,color:"#fff",fontWeight:700,fontFamily:"monospace"}}>{f.id}</div>
                <div style={{fontSize:10,color:"#93c5fd",marginTop:6,marginBottom:2}}>작성일</div>
                <div style={{fontSize:12,color:"#dbeafe",fontWeight:600}}>{f.createdAt}</div>
              </div>
            </div>
          </div>

          <div style={{padding:"24px 36px 28px"}}>

            {/* ① 수신 / 청구인 정보 — 2단 나란히 */}
            <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:16,marginBottom:22}}>
              {/* 수신처 */}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-block",width:3,height:13,background:"#1e40af",borderRadius:2}}/>
                  수신처 (청구 대상)
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                  <tbody>
                    <tr>
                      <td style={{...TH,width:"28%",borderRight:"1px solid #2d5494",fontSize:11,whiteSpace:"nowrap"}}>기관명</td>
                      <td style={{padding:"8px 14px",fontSize:13,color:"#111827",fontWeight:800,borderBottom:"1px solid #e2e8f0",whiteSpace:"nowrap"}}>(주)한컴캠퍼스</td>
                    </tr>
                    <tr>
                      <td style={{...TH,borderRight:"1px solid #2d5494",fontSize:11,whiteSpace:"nowrap"}}>담당부서</td>
                      <td style={{padding:"8px 14px",fontSize:13,color:"#1e293b",borderBottom:"1px solid #e2e8f0",whiteSpace:"nowrap"}}>교육운영팀</td>
                    </tr>
                    <tr>
                      <td style={{...TH,borderRight:"1px solid #2d5494",fontSize:11,borderBottom:"none",whiteSpace:"nowrap"}}>청구월</td>
                      <td style={{padding:"8px 14px",fontSize:13,color:"#1e293b",fontWeight:500,borderBottom:"none",whiteSpace:"nowrap"}}>{f.month}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* 청구인 */}
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-block",width:3,height:13,background:"#7c3aed",borderRadius:2}}/>
                  청구인 정보
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                  <tbody>
                    <tr>
                      <td style={{...TH,width:"36%",borderRight:"1px solid #2d5494",fontSize:11}}>성명</td>
                      <td style={{padding:"8px 12px",fontSize:13,color:"#111827",fontWeight:700,borderBottom:"1px solid #e2e8f0"}}>{instructor.name}</td>
                    </tr>
                    <tr>
                      <td style={{...TH,borderRight:"1px solid #2d5494",fontSize:11}}>강의과목</td>
                      <td style={{padding:"8px 12px",fontSize:13,color:"#0f172a",fontWeight:600,borderBottom:"1px solid #e2e8f0"}}>{instructor.subject}</td>
                    </tr>
                    <tr>
                      <td style={{...TH,borderRight:"1px solid #2d5494",fontSize:11,borderBottom:"none"}}>연락처</td>
                      <td style={{padding:"8px 12px",fontSize:13,color:"#1e293b",borderBottom:"none"}}>{instructor.phone||"—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ② 비고 입력 */}
            <div style={{marginBottom:20}}>
              <label style={{display:"block",fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:6}}>
                <span style={{display:"inline-block",width:3,height:11,background:"#64748b",borderRadius:2,marginRight:6,verticalAlign:"middle"}}/>
                청구 메모 / 특이사항
              </label>
              <input value={f.memo} onChange={e=>setField("memo",e.target.value)}
                disabled={isApproved}
                placeholder="특이사항, 보강 내용, 추가 설명 등"
                style={{width:"100%",background:isApproved?"#f9fafb":"#f8faff",border:"1px solid #cbd5e1",borderRadius:6,padding:"9px 12px",color:"#0f172a",fontSize:13,fontWeight:500,outline:"none",boxSizing:"border-box",cursor:isApproved?"not-allowed":"text"}}/>
            </div>

            {/* ③ 강의 세부내역 */}
            <div style={{marginBottom:18}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-block",width:3,height:13,background:"#16a34a",borderRadius:2}}/>
                  강의 세부내역
                </div>
                {!isApproved&&(
                  <button onClick={addLecture}
                    style={{background:"#f0fdf4",border:"1px solid #86efac",color:"#15803d",borderRadius:6,padding:"5px 13px",fontSize:11,cursor:"pointer",fontWeight:700}}>
                    + 강의 추가
                  </button>
                )}
              </div>
              <div style={{border:"1px solid #cbd5e1",borderRadius:4,overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr>
                      <th style={{...THc,width:28,borderRight:"1px solid #2d5494"}}>#</th>
                      <th style={{...TH,width:106,borderRight:"1px solid #2d5494"}}>날짜</th>
                      <th style={{...TH,borderRight:"1px solid #2d5494"}}>강의 과목 / 내용</th>
                      <th style={{...THr,width:68,borderRight:"1px solid #2d5494"}}>교육시간(h)</th>
                      <th style={{...THr,width:100,borderRight:"1px solid #2d5494"}}>단가</th>
                      <th style={{...THr,width:108,borderRight:"1px solid #2d5494"}}>강의료</th>
                      <th style={{...TH,width:100,borderRight:"1px solid #2d5494"}}>메모</th>
                      {!isApproved&&<th style={{...THc,width:36}}/>}
                    </tr>
                  </thead>
                  <tbody>
                    {f.lectures.map((row,i)=>(
                      isApproved
                      // 승인 후: 읽기 전용 행
                      ? <tr key={row.id} style={{borderBottom:"1px solid #e5e7eb",background:i%2===0?"#f9fafb":"#fff"}}>
                          <td style={{padding:"8px 10px",textAlign:"center",color:"#9ca3af",fontSize:11}}>{i+1}</td>
                          <td style={{padding:"8px 10px",color:"#0f172a",fontFamily:"monospace",fontSize:12,fontWeight:500}}>{row.date}</td>
                          <td style={{padding:"8px 10px",color:"#0f172a",fontWeight:700}}>{row.subject}</td>
                          <td style={{padding:"8px 10px",textAlign:"right",color:"#1e293b",fontWeight:600}}>{row.hours}h</td>
                          <td style={{padding:"8px 10px",textAlign:"right",color:"#1e293b",fontFamily:"monospace",fontWeight:600}}>{fmtW(row.unitPrice||0)}</td>
                          <td style={{padding:"8px 10px",textAlign:"right",color:"#15803d",fontWeight:700,fontFamily:"monospace"}}>{fmtW(row.fee)}</td>
                          <td style={{padding:"8px 10px",color:"#334155",fontSize:12}}>{row.memo||"—"}</td>
                        </tr>
                      // 작성/제출 중: 편집 가능 행
                      : <LectureRow key={row.id} row={row} idx={i}
                          onChange={(k,v)=>updLecture(i,k,v)}
                          onDelete={()=>delLecture(i)}
                        />
                    ))}
                    {/* 합계 행 */}
                    <tr style={{background:"#f0f9ff",borderTop:"2px solid #1e40af"}}>
                      <td colSpan={2} style={{padding:"9px 12px",fontSize:12,color:"#1e40af",fontWeight:800}}>합계</td>
                      <td style={{padding:"9px 12px",fontSize:11,color:"#64748b"}}>{f.lectures.filter(l=>l.subject).length}건</td>
                      <td style={{padding:"9px 12px",textAlign:"right",fontSize:13,fontWeight:800,color:"#1e40af",fontFamily:"monospace"}}>{totalHours}h</td>
                      <td/>
                      <td style={{padding:"9px 12px",textAlign:"right",fontSize:13,fontWeight:900,color:"#1e40af",fontFamily:"monospace"}}>{fmtW(lectureTotal)}</td>
                      <td colSpan={isApproved?1:2}/>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ④ 원천징수 */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#1c3557",letterSpacing:"0.1em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-block",width:3,height:13,background:"#dc2626",borderRadius:2}}/>
                원천징수 내역 (소득세법 제129조)
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",border:"1px solid #cbd5e1"}}>
                <tbody>
                  {[
                    {l:"강의료 합계",  v:fmtW(lectureTotal), c:"#15803d"},
                    {l:"소득세 (3.0%)",v:fmtW(tax.incomeTax),  c:"#b91c1c"},
                    {l:"주민세 (0.3%)",v:fmtW(tax.residentTax),c:"#b91c1c"},
                    {l:"원천징수 합계",v:fmtW(tax.totalDeduct),c:"#b91c1c"},
                  ].map((r,i,arr)=>(
                    <tr key={r.l}>
                      <td style={{...TH,width:"30%",borderRight:"1px solid #2d5494",fontSize:11,borderBottom:i<arr.length-1?"1px solid #2d5494":"none",padding:"10px 16px"}}>{r.l}</td>
                      <td style={{padding:"10px 20px",fontFamily:"monospace",fontWeight:700,color:r.c,fontSize:14,textAlign:"right",borderBottom:i<arr.length-1?"1px solid #e2e8f0":"none"}}>{r.v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ⑤ 실수령액 */}
            <div style={{background:"linear-gradient(135deg,#1c3557,#1e40af)",borderRadius:6,padding:"16px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
              <div>
                <div style={{fontSize:11,color:"#93c5fd",fontWeight:700,letterSpacing:"0.1em",marginBottom:3}}>실 수 령 액 (NET AMOUNT)</div>
                <div style={{fontSize:11,color:"#93c5fd"}}>{fmtW(lectureTotal)} − 원천징수 {fmtW(tax.totalDeduct)}</div>
              </div>
              <div style={{fontSize:28,fontWeight:900,color:"#fff",fontFamily:"monospace",letterSpacing:"-0.5px"}}>{fmtW(tax.netPay)}</div>
            </div>

            {/* ⑥ 서명란 */}
            <div style={{borderTop:"1px dashed #cbd5e1",paddingTop:18,marginBottom:24}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:11,color:"#9ca3af",marginBottom:10,fontWeight:700,letterSpacing:"0.05em"}}>청구인 서명</div>
                  <div style={{height:48,borderBottom:"1px solid #e5e7eb",marginBottom:6}}/>
                  <div style={{fontSize:12,color:"#374151"}}>{instructor.name} (인)</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:11,color:"#9ca3af",marginBottom:10,fontWeight:700,letterSpacing:"0.05em"}}>수령 기관 확인</div>
                  <div style={{height:48,borderBottom:"1px solid #e5e7eb",marginBottom:6,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                    {isApproved&&<span style={{fontSize:20,marginBottom:4}}>✅</span>}
                  </div>
                  <div style={{fontSize:12,color:"#374151"}}>(주)한컴캠퍼스 (인)</div>
                </div>
              </div>
              <div style={{marginTop:12,fontSize:10,color:"#9ca3af",lineHeight:1.8,textAlign:"center"}}>
                위 금액을 정히 청구합니다. · 소득세법 제129조에 의거 사업소득 원천징수율 3.3% 적용
              </div>
            </div>

            {/* ⑦ 하단 버튼 (승인 전) */}
            {!isApproved&&(
              <div style={{borderTop:"1px solid #e5e7eb",paddingTop:20,display:"flex",gap:12,justifyContent:"center"}}>
                <button onClick={handleTempSave}
                  style={{minWidth:160,background:"#f8fafc",border:"2px solid #cbd5e1",color:"#374151",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:8,justifyContent:"center",transition:"all .15s"}}>
                  임시저장
                </button>
                <button onClick={handleSubmit}
                  style={{minWidth:180,background:"linear-gradient(135deg,#7c3aed,#6d28d9)",color:"#fff",border:"none",borderRadius:10,padding:"12px 32px",fontSize:14,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:8,justifyContent:"center",boxShadow:"0 4px 14px rgba(124,58,237,0.4)"}}>
                  청구서 제출
                </button>
              </div>
            )}

            {/* 승인 완료 안내 */}
            {isApproved&&(
              <div style={{borderTop:"1px solid #e5e7eb",paddingTop:16,textAlign:"center"}}>
                <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:"12px 28px",fontSize:13,color:"#15803d",fontWeight:700}}>
                  ✅ 관리자가 이 청구서를 승인하였습니다. 수정이 잠겨 있습니다.
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* 하단 여백 */}
      <div style={{height:32}}/>
    </div>
  );
}

// ── 강사 포털 메인 (로그인 후 화면)
function InstructorPortal({instructor,invoices,setInvoices,onLogout}){
  const [tab,setTab]=useState("list");
  const [formOpen,setFormOpen]=useState(false);
  const [editTarget,setEditTarget]=useState(null);
  const [viewTarget,setViewTarget]=useState(null);

  // ── 강사 포털 전용 라이트 팔레트
  const IP={
    bg:"#f4f7ff", surface:"#ffffff", card:"#ffffff", border:"#dde5f4", border2:"#c7d4ef",
    text:"#1e2a4a", muted:"#6475a0", slate:"#8496bf", dim:"#eef2fb",
    thead:"#f0f4ff", row1:"#f8f9ff", row2:"transparent",
    inputBg:"#ffffff",
    purple:"#7c3aed", purpleL:"#9f67f5", purpleDim:"#f3eeff", purpleBorder:"#d4b8fc",
    green:"#16a34a", greenL:"#15803d", red:"#dc2626", redL:"#b91c1c",
    amber:"#d97706", blue:"#2563eb",
  };

  const myInvoices=invoices[instructor.id]||[];
  const c=calcInstructor(instructor.invoiceAmount);

  const reloadInvoices=async()=>{
    try{ const data=await api("GET","/invoices"); setInvoices(data); }
    catch(e){ console.error("청구서 로드 실패:",e.message); }
  };

  const saveInvoice=async(inv)=>{
    try{
      if(inv.dbId){
        await fetch(`${API_BASE}/invoices/${inv.dbId}`,{
          method:"PUT",
          headers:{"Content-Type":"application/json","Authorization":`Bearer ${getToken()}`},
          body:JSON.stringify({invoiceAmount:inv.invoiceAmount,lectures:inv.lectures,memo:inv.memo,status:inv.status}),
        });
      } else {
        await fetch(`${API_BASE}/invoices`,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({instCode:instructor.id,month:inv.month,invoiceAmount:inv.invoiceAmount,lectures:inv.lectures,memo:inv.memo,status:inv.status}),
        });
      }
      await reloadInvoices();
    }catch(e){ console.error("청구서 저장 실패:",e.message); }
    setFormOpen(false); setEditTarget(null);
  };

  const deleteInvoice=async(id)=>{
    if(!window.confirm("이 청구서를 삭제하시겠습니까?"))return;
    try{
      await fetch(`${API_BASE}/invoices/${id}`,{method:"DELETE"});
      await reloadInvoices();
    }catch(e){ console.error("청구서 삭제 실패:",e.message); }
  };

  const statusColor={"작성중":"blue","제출완료":"amber","승인":"green","반려":"red"};

  return(
    <div style={{minHeight:"100vh",background:IP.bg,fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif",display:"flex",flexDirection:"column"}}>

      {/* 포털 헤더 */}
      <div style={{background:"linear-gradient(135deg,#7c3aed,#6d28d9)",borderBottom:"none",padding:"0 28px",height:58,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,boxShadow:"0 2px 16px rgba(124,58,237,0.25)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <img src="data:image/png;base64,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" alt="logo" style={{width:40,height:40,objectFit:"contain",borderRadius:8,background:"rgba(255,255,255,0.9)",padding:2}}/>
          <div>
            <div style={{fontSize:14,fontWeight:900,color:"#fff"}}>강사 포털</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.65)"}}>청구서 · 강의 세부내역 관리</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{instructor.name} 강사님</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.6)"}}>{instructor.subject} · {instructor.id}</div>
          </div>
          <button onClick={onLogout} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.35)",color:"#fff",borderRadius:8,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:700}}>로그아웃</button>
        </div>
      </div>

      <div style={{flex:1,padding:"26px 30px",overflowY:"auto"}}>

        {/* 내 정보 요약 카드 */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:26}}>
          {[
            {l:"이번달 청구금액",v:instructor.invoiceAmount>0?fmtW(instructor.invoiceAmount):"미제출",c:IP.purple,icon:""},
            {l:"소득세 (3%)",v:instructor.invoiceAmount>0?fmtW(c.incomeTax):"—",c:IP.red,icon:""},
            {l:"주민세 (0.3%)",v:instructor.invoiceAmount>0?fmtW(c.residentTax):"—",c:IP.red,icon:""},
            {l:"실지급 예정액",v:instructor.invoiceAmount>0?fmtW(c.netPay):"—",c:IP.green,icon:"✅"},
          ].map(s=>(
            <div key={s.l} style={{background:IP.card,border:`1px solid ${IP.border}`,borderRadius:13,padding:"15px 18px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:11,color:IP.muted,marginBottom:4}}>{s.icon} {s.l}</div>
              <div style={{fontSize:16,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* 탭 */}
        <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`2px solid ${IP.border}`}}>
          {[["list","청구서 목록"],["new","✏️ 새 청구서 작성"]].map(([v,l])=>(
            <button key={v} onClick={()=>{setTab(v);if(v==="new"){setEditTarget(null);setFormOpen(true);}}}
              style={{padding:"10px 24px",background:"none",border:"none",borderBottom:`3px solid ${tab===v?IP.purple:"transparent"}`,marginBottom:"-2px",color:tab===v?IP.purple:IP.muted,fontSize:13,fontWeight:700,cursor:"pointer",transition:"color .15s"}}>
              {l}
            </button>
          ))}
        </div>

        {/* 청구서 목록 */}
        {tab==="list"&&(
          <div>
            {myInvoices.length===0?(
              <div style={{background:IP.card,border:`1px solid ${IP.border}`,borderRadius:16,padding:"60px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
                <div style={{fontSize:40,marginBottom:12}}></div>
                <div style={{fontSize:15,color:IP.text,fontWeight:600,marginBottom:8}}>아직 작성한 청구서가 없습니다</div>
                <div style={{fontSize:13,color:IP.muted,marginBottom:22}}>상단 [새 청구서 작성] 탭에서 청구서를 작성해 주세요</div>
                <button onClick={()=>{setEditTarget(null);setFormOpen(true);}} style={{background:"linear-gradient(135deg,#7c3aed,#6d28d9)",color:"#fff",border:"none",borderRadius:9,padding:"11px 26px",fontSize:13,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 14px rgba(124,58,237,0.3)"}}>
                  + 새 청구서 작성
                </button>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {myInvoices.map(inv=>{
                  const tot=inv.lectures.reduce((s,l)=>s+(l.fee||0),0);
                  const tax=calcInstructor(inv.invoiceAmount);
                  const isOpen=viewTarget===inv.id;
                  return(
                    <div key={inv.id} style={{background:IP.card,border:`2px solid ${isOpen?IP.purple:IP.border}`,borderRadius:14,overflow:"hidden",boxShadow:isOpen?"0 4px 20px rgba(124,58,237,0.12)":"0 2px 8px rgba(0,0,0,0.04)",transition:"border .2s,box-shadow .2s"}}>
                      {/* 청구서 헤더 */}
                      <div style={{padding:"16px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",background:isOpen?IP.purpleDim:IP.card,transition:"background .2s"}} onClick={()=>setViewTarget(isOpen?null:inv.id)}>
                        <div style={{display:"flex",alignItems:"center",gap:14}}>
                          <div style={{fontSize:24}}></div>
                          <div>
                            <div style={{fontWeight:800,color:IP.text,fontSize:14}}>{inv.month} 청구서</div>
                            <div style={{fontSize:12,color:IP.muted,marginTop:2}}>{inv.id} · {inv.createdAt} 작성 · 강의 {inv.lectures.length}건</div>
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:14}}>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:15,fontWeight:900,color:IP.purple,fontFamily:"monospace"}}>{fmtW(inv.invoiceAmount)}</div>
                            <div style={{fontSize:11,color:IP.muted}}>실지급 {fmtW(tax.netPay)}</div>
                          </div>
                          <Badge color={statusColor[inv.status]||"gray"}>{inv.status}</Badge>
                          <span style={{color:IP.muted,fontSize:16}}>{isOpen?"▲":"▼"}</span>
                        </div>
                      </div>

                      {/* 세부내역 펼침 */}
                      {isOpen&&(
                        <div style={{borderTop:`1px solid ${IP.border}`,padding:"18px 22px"}}>
                          {/* 강의 세부내역 테이블 */}
                          <div style={{marginBottom:16,overflowX:"auto",borderRadius:10,border:`1px solid ${IP.border}`,overflow:"hidden"}}>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                              <thead>
                                <tr style={{background:IP.thead}}>
                                  {["#","날짜","강의 과목/내용","교육시간(h)","단가","강의료","메모"].map(h=>(
                                    <th key={h} style={{padding:"9px 13px",color:IP.muted,fontWeight:700,textAlign:["강의료","교육시간(h)","단가"].includes(h)?"right":"left",fontSize:11,borderBottom:`1px solid ${IP.border}`}}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {inv.lectures.map((l,i)=>(
                                  <tr key={l.id} style={{borderBottom:`1px solid ${IP.border}`,background:i%2===0?IP.row1:IP.row2}}>
                                    <td style={{padding:"9px 13px",color:IP.slate,textAlign:"center"}}>{i+1}</td>
                                    <td style={{padding:"9px 13px",color:IP.muted,fontFamily:"monospace",fontSize:11}}>{l.date}</td>
                                    <td style={{padding:"9px 13px",color:IP.text,fontWeight:600}}>{l.subject}</td>
                                    <td style={{padding:"9px 13px",color:IP.muted,textAlign:"right"}}>{l.hours}h</td>
                                    <td style={{padding:"9px 13px",color:IP.muted,textAlign:"right",fontFamily:"monospace"}}>{fmtW(l.unitPrice||0)}</td>
                                    <td style={{padding:"9px 13px",color:IP.green,fontWeight:700,textAlign:"right",fontFamily:"monospace"}}>{fmtW(l.fee)}</td>
                                    <td style={{padding:"9px 13px",color:IP.slate,fontSize:11}}>{l.memo||"—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* 원천징수 요약 */}
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:16}}>
                            {[
                              {l:"강의료 합계",v:fmtW(tot),c:IP.purple},
                              {l:"소득세 (3%)",v:fmtW(tax.incomeTax),c:IP.red},
                              {l:"주민세 (0.3%)",v:fmtW(tax.residentTax),c:IP.red},
                              {l:"실수령액",v:fmtW(tax.netPay),c:IP.green},
                            ].map(s=>(
                              <div key={s.l} style={{background:IP.dim,borderRadius:9,padding:"11px 14px",border:`1px solid ${IP.border}`}}>
                                <div style={{fontSize:10,color:IP.muted,marginBottom:3}}>{s.l}</div>
                                <div style={{fontSize:13,fontWeight:800,color:s.c,fontFamily:"monospace"}}>{s.v}</div>
                              </div>
                            ))}
                          </div>

                          {inv.memo&&(
                            <div style={{background:"#fefce8",border:"1px solid #fde68a",borderRadius:8,padding:"9px 13px",marginBottom:14,fontSize:12,color:"#92400e"}}>
                              메모: {inv.memo}
                            </div>
                          )}

                          {inv.rejectReason&&(
                            <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"9px 13px",marginBottom:14,fontSize:12,color:IP.red}}>
                              ❌ 반려 사유: {inv.rejectReason}
                            </div>
                          )}

                          {/* 액션 버튼 */}
                          <div style={{display:"flex",gap:9}}>
                            {inv.status!=="승인"&&(
                              <>
                                <button onClick={()=>{setEditTarget(inv);setFormOpen(true);}}
                                  style={{background:IP.dim,border:`1px solid ${IP.border2}`,color:IP.blue,borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                                  ✏️ 수정
                                </button>
                                <button onClick={()=>deleteInvoice(inv.id)}
                                  style={{background:"#fef2f2",border:"1px solid #fca5a5",color:IP.red,borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                                  삭제
                                </button>
                              </>
                            )}
                            {inv.status==="승인"&&(
                              <span style={{fontSize:12,color:IP.green,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>✅ 관리자 승인 완료 — 수정 불가</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <InvoiceForm
        open={formOpen}
        onClose={()=>{setFormOpen(false);setEditTarget(null);setTab("list");}}
        initial={editTarget}
        instructor={instructor}
        onSave={saveInvoice}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// ── 관리자 청구서 검토 페이지
function AdminInvoiceReview({instructors, invoices, setInvoices}){
  const [filterStatus, setFilterStatus] = useState("제출완료");
  const [selectedInv, setSelectedInv] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);

  const reloadInvoices=async()=>{
    try{ const data=await api("GET","/invoices"); setInvoices(data); }
    catch(e){ console.error("청구서 로드 실패:",e.message); }
  };

  // 모든 강사 청구서를 플랫하게 합산
  const allInvoices = instructors.flatMap(ins =>
    (invoices[ins.id]||[]).map(inv => ({...inv, instructorId:ins.id, instructorName:ins.name, subject:ins.subject}))
  ).sort((a,b)=> new Date(b.createdAt)-new Date(a.createdAt)||String(b.id).localeCompare(String(a.id)));

  const filtered = filterStatus === "전체" ? allInvoices
    : allInvoices.filter(inv => inv.status === filterStatus);

  // 상태 변경 — DB 저장
  const updateStatus = async(inv, newStatus, extra={}) => {
    try{
      const dbId = inv.dbId || inv.id;
      await api("PUT",`/invoices/${dbId}`,{
        invoiceAmount: inv.invoiceAmount,
        lectures: inv.lectures,
        memo: inv.memo,
        status: newStatus,
        ...extra,
      });
      await reloadInvoices();
    }catch(e){ console.error("상태 변경 실패:",e.message); }
    setSelectedInv(null);
  };

  // 수정 저장 — DB 저장
  const saveEdit = async(updated) => {
    try{
      const dbId = updated.dbId || updated.id;
      await api("PUT",`/invoices/${dbId}`,{
        invoiceAmount: updated.invoiceAmount,
        lectures: updated.lectures,
        memo: updated.memo,
        status: "제출완료",
      });
      await reloadInvoices();
    }catch(e){ console.error("수정 저장 실패:",e.message); }
    setEditTarget(null);
  };

  const counts = {
    전체: allInvoices.length,
    제출완료: allInvoices.filter(i=>i.status==="제출완료").length,
    승인: allInvoices.filter(i=>i.status==="승인").length,
    반려: allInvoices.filter(i=>i.status==="반려").length,
    작성중: allInvoices.filter(i=>i.status==="작성중").length,
  };

  const statusColor = {"작성중":"gray", "제출완료":"amber", "승인":"green", "반려":"red"};
  const statusEmoji = {"작성중":"", "제출완료":"", "승인":"✅", "반려":"❌"};

  return(
    <div>
      {/* 헤더 */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:"0 0 4px"}}>청구서 관리</h1>
          <div style={{fontSize:13,color:C.muted}}>강사가 제출한 청구서를 검토·수정·승인·반려합니다</div>
        </div>
        {counts.제출완료 > 0 && (
          <div style={{background:"#451a03",border:"1px solid #92400e",borderRadius:10,padding:"10px 18px",display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}></span>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:C.amberL}}>검토 대기 {counts.제출완료}건</div>
              <div style={{fontSize:11,color:C.muted}}>승인 또는 반려가 필요합니다</div>
            </div>
          </div>
        )}
      </div>

      {/* 상태 필터 탭 */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`}}>
        {["전체","제출완료","승인","반려","작성중"].map(s=>(
          <button key={s} onClick={()=>setFilterStatus(s)}
            style={{padding:"9px 20px",background:"none",border:"none",
              borderBottom:`3px solid ${filterStatus===s?C.blue:"transparent"}`,
              color:filterStatus===s?C.blueL:C.muted,fontSize:13,fontWeight:700,cursor:"pointer",
              display:"flex",alignItems:"center",gap:6}}>
            {statusEmoji[s]||""} {s}
            <span style={{background:filterStatus===s?C.blue:C.border,color:"#fff",
              fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:20,minWidth:18,textAlign:"center"}}>
              {counts[s]||0}
            </span>
          </button>
        ))}
      </div>

      {/* 청구서 목록 */}
      {filtered.length === 0 ? (
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"60px",textAlign:"center"}}>
          <div style={{fontSize:36,marginBottom:12}}></div>
          <div style={{fontSize:15,color:C.muted}}>{filterStatus === "전체" ? "등록된 청구서가 없습니다" : `${filterStatus} 상태의 청구서가 없습니다`}</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {filtered.map(inv=>{
            const tax = calcInstructor(inv.invoiceAmount);
            const isSelected = selectedInv?.id === inv.id;
            return(
              <div key={inv.id} style={{background:C.card,border:`2px solid ${isSelected?C.blue:inv.status==="제출완료"?"#d97706":C.border}`,borderRadius:14,overflow:"hidden",transition:"border .2s"}}>

                {/* 청구서 요약 행 */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",cursor:"pointer"}}
                  onClick={()=>setSelectedInv(isSelected?null:inv)}>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <div style={{fontSize:28}}>{statusEmoji[inv.status]||""}</div>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                        <span style={{fontWeight:800,color:C.text,fontSize:14}}>{inv.instructorName}</span>
                        <Badge color="purple">{inv.subject}</Badge>
                        <Badge color={statusColor[inv.status]||"gray"}>{inv.status}</Badge>
                        {inv.editedByAdmin && <Badge color="teal">관리자수정</Badge>}
                      </div>
                      <div style={{fontSize:12,color:C.muted}}>
                        {inv.id} · {inv.month} · 강의 {inv.lectures?.length||0}건 · 작성 {inv.createdAt}
                        {inv.reviewedAt && ` · 검토 ${inv.reviewedAt} (${inv.reviewedBy})`}
                      </div>
                      {inv.status==="반려" && inv.rejectReason && (
                        <div style={{marginTop:4,fontSize:12,color:C.redL,fontWeight:600}}>
                          반려사유: {inv.rejectReason}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:900,color:C.blue,fontFamily:"monospace"}}>{fmtW(inv.invoiceAmount)}</div>
                      <div style={{fontSize:11,color:C.muted}}>실지급 {fmtW(tax.netPay)}</div>
                    </div>
                    <span style={{color:C.muted,fontSize:16}}>{isSelected?"▲":"▼"}</span>
                  </div>
                </div>

                {/* 상세 펼침 */}
                {isSelected && (
                  <div style={{borderTop:`1px solid ${C.border}`,padding:"20px 20px 16px"}}>

                    {/* 강의 세부내역 테이블 */}
                    <div style={{marginBottom:16,overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                        <thead>
                          <tr style={{background:C.thead}}>
                            {["#","날짜","강의 과목/내용","교육시간(h)","단가","강의료","메모"].map(h=>(
                              <th key={h} style={{padding:"9px 12px",color:C.muted,fontWeight:700,
                                textAlign:["교육시간(h)","단가","강의료"].includes(h)?"right":"left",
                                fontSize:11,borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(inv.lectures||[]).map((l,i)=>(
                            <tr key={l.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.row1:C.row2}}>
                              <td style={{padding:"9px 12px",color:C.muted,textAlign:"center",fontSize:12}}>{i+1}</td>
                              <td style={{padding:"9px 12px",color:C.text,fontFamily:"monospace",fontSize:12}}>{l.date}</td>
                              <td style={{padding:"9px 12px",color:C.text,fontWeight:600}}>{l.subject}</td>
                              <td style={{padding:"9px 12px",textAlign:"right",color:C.text}}>{l.hours}h</td>
                              <td style={{padding:"9px 12px",textAlign:"right",color:C.text,fontFamily:"monospace"}}>{fmtW(l.unitPrice||0)}</td>
                              <td style={{padding:"9px 12px",textAlign:"right",color:C.greenL,fontWeight:700,fontFamily:"monospace"}}>{fmtW(l.fee)}</td>
                              <td style={{padding:"9px 12px",color:C.muted,fontSize:12}}>{l.memo||"—"}</td>
                            </tr>
                          ))}
                          <tr style={{background:C.dim,borderTop:`2px solid ${C.blue}`}}>
                            <td colSpan={4} style={{padding:"9px 12px",fontWeight:800,color:C.blue,fontSize:12}}>합계 {(inv.lectures||[]).filter(l=>l.subject).length}건</td>
                            <td/>
                            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:900,color:C.blue,fontFamily:"monospace",fontSize:14}}>{fmtW(inv.invoiceAmount)}</td>
                            <td/>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* 원천징수 요약 */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
                      {[
                        {l:"청구금액",v:fmtW(inv.invoiceAmount),c:C.blue},
                        {l:"소득세 (3%)",v:fmtW(tax.incomeTax),c:C.redL},
                        {l:"주민세 (0.3%)",v:fmtW(tax.residentTax),c:C.redL},
                        {l:"실지급액",v:fmtW(tax.netPay),c:C.greenL},
                      ].map(s=>(
                        <div key={s.l} style={{background:C.subBox,borderRadius:9,padding:"11px 14px",border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:4,fontWeight:700}}>{s.l}</div>
                          <div style={{fontSize:14,fontWeight:800,color:s.c,fontFamily:"monospace"}}>{s.v}</div>
                        </div>
                      ))}
                    </div>

                    {inv.memo && (
                      <div style={{background:C.memoBox,borderRadius:8,padding:"9px 14px",marginBottom:14,fontSize:12,color:C.text,border:`1px solid ${C.border}`}}>
                        강사 메모: {inv.memo}
                      </div>
                    )}

                    {/* 관리자 액션 버튼 */}
                    {inv.status !== "승인" && (
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                        {/* 수정 */}
                        <button onClick={()=>setEditTarget({...inv})}
                          style={{background:C.dim,border:`1px solid ${C.border2}`,color:C.blueL,borderRadius:9,
                            padding:"9px 20px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                          ✏️ 내용 수정
                        </button>
                        {/* 승인 */}
                        {inv.status === "제출완료" && (
                          <button onClick={()=>{
                            if(window.confirm(`"${inv.instructorName}" 강사의 ${inv.month} 청구서를 승인하시겠습니까?\n\n청구금액: ${fmtW(inv.invoiceAmount)}\n실지급액: ${fmtW(tax.netPay)}`))
                              updateStatus(inv, "승인");
                          }}
                            style={{background:"linear-gradient(135deg,#16a34a,#15803d)",color:"#fff",border:"none",
                              borderRadius:9,padding:"9px 24px",fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                            ✅ 승인
                          </button>
                        )}
                        {/* 반려 */}
                        {inv.status !== "반려" && (
                          <button onClick={()=>{ setRejectTarget(inv); setRejectReason(""); }}
                            style={{background:isDarkTheme()?"#450a0a":"#fee2e2",border:isDarkTheme()?"1px solid #7f1d1d":"1px solid #fca5a5",
                              color:isDarkTheme()?C.redL:"#b91c1c",
                              borderRadius:9,padding:"9px 24px",fontSize:13,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                            ❌ 반려
                          </button>
                        )}
                        {/* 재검토 요청 (반려→제출완료로 되돌리기) */}
                        {inv.status === "반려" && (
                          <button onClick={()=>updateStatus(inv,"제출완료",{rejectReason:""})}
                            style={{background:C.dim,border:`1px solid ${C.border2}`,color:C.amberL,
                              borderRadius:9,padding:"9px 20px",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                            반려 취소
                          </button>
                        )}
                      </div>
                    )}
                    {inv.status === "승인" && (
                      <div style={{paddingTop:12,borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:13,color:C.greenL,fontWeight:700}}>✅ 승인 완료 · {inv.reviewedAt}</span>
                        <button onClick={()=>updateStatus(inv,"제출완료")}
                          style={{background:C.subBox,border:`1px solid ${C.border}`,color:C.muted,
                            borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                          승인 취소
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 관리자 수정 모달 */}
      {editTarget && (
        <AdminEditModal
          inv={editTarget}
          instructors={instructors}
          onSave={saveEdit}
          onClose={()=>setEditTarget(null)}
        />
      )}

      {/* 반려 사유 모달 */}
      {rejectTarget && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}
          onClick={()=>setRejectTarget(null)}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:28,width:460,maxWidth:"95vw"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:900,color:C.text,marginBottom:6}}>❌ 청구서 반려</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:18}}>
              <strong style={{color:C.text}}>{rejectTarget.instructorName}</strong> 강사의 {rejectTarget.month} 청구서
            </div>
            <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6}}>반려 사유 <span style={{color:C.redL}}>*</span></label>
            <textarea
              value={rejectReason}
              onChange={e=>setRejectReason(e.target.value)}
              placeholder="반려 사유를 입력하세요 (강사에게 표시됩니다)"
              rows={4}
              style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:9,
                padding:"10px 12px",color:C.text,fontSize:13,outline:"none",resize:"vertical",boxSizing:"border-box",marginBottom:16}}
            />
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={()=>{
                  if(!rejectReason.trim()){alert("반려 사유를 입력해주세요.");return;}
                  updateStatus(rejectTarget,"반려",{rejectReason});
                  setRejectTarget(null); setRejectReason("");
                }}
                disabled={!rejectReason.trim()}
                style={{flex:1,background:isDarkTheme()?"#450a0a":"#fee2e2",
                  border:isDarkTheme()?"1px solid #7f1d1d":"1px solid #fca5a5",
                  color:isDarkTheme()?C.redL:"#b91c1c",borderRadius:9,padding:"11px",
                  fontSize:14,fontWeight:800,cursor:"pointer",opacity:rejectReason.trim()?1:0.5}}>
                반려 확인
              </button>
              <button onClick={()=>setRejectTarget(null)}
                style={{flex:1,background:C.card,border:`1px solid ${C.border}`,color:C.muted,
                  borderRadius:9,padding:"11px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 관리자 청구서 수정 모달 (강의 세부내역 수정 가능)
function AdminEditModal({inv, instructors, onSave, onClose}){
  const [f, setF] = useState({...inv, lectures: inv.lectures.map(l=>({...l}))});
  const instructor = instructors.find(i=>i.id===inv.instructorId)||{};

  const addLecture=()=>setF(p=>({...p,lectures:[...p.lectures,{id:Date.now(),date:"",subject:"",hours:1,unitPrice:0,fee:0,memo:""}]}));
  const updLecture=(i,k,v)=>setF(p=>{
    const l=[...p.lectures]; l[i]={...l[i],[k]:v};
    if(k==="hours"||k==="unitPrice") l[i].fee=Math.round((k==="hours"?Number(v):l[i].hours)*(k==="unitPrice"?Number(v):l[i].unitPrice));
    return{...p,lectures:l};
  });
  const delLecture=i=>setF(p=>({...p,lectures:p.lectures.filter((_,j)=>j!==i)}));
  const lectureTotal=f.lectures.reduce((s,l)=>s+(l.fee||0),0);

  const cs={background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:5,
    padding:"6px 8px",color:C.text,fontSize:12,fontWeight:500,outline:"none",width:"100%",boxSizing:"border-box"};

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}
      onClick={onClose}>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:28,width:820,maxWidth:"97vw",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:16,fontWeight:900,color:C.text}}>✏️ 청구서 수정 (관리자)</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>
              {inv.instructorName} · {inv.month} · {inv.id}
            </div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {/* 안내 */}
        <div style={{background:C.dim,border:`1px solid ${C.border2}`,borderRadius:9,padding:"10px 14px",marginBottom:18,fontSize:12,color:C.blueL}}>
          ⚠️ 관리자 수정 내역은 기록에 남으며, 저장 후 강사에게 알림이 전송됩니다. 청구서 상태는 <strong>제출완료</strong>로 유지됩니다.
        </div>

        {/* 강의 세부내역 */}
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:12,fontWeight:700,color:C.text}}>강의 세부내역</span>
            <button onClick={addLecture}
              style={{background:isDarkTheme()?"#052e16":"#dcfce7",border:isDarkTheme()?"1px solid #166534":"1px solid #86efac",
                color:isDarkTheme()?"#4ade80":"#15803d",borderRadius:6,padding:"5px 12px",fontSize:11,cursor:"pointer",fontWeight:700}}>
              + 강의 추가
            </button>
          </div>
          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:C.thead}}>
                  {["#","날짜","강의 과목/내용","시간(h)","단가","강의료","메모",""].map(h=>(
                    <th key={h} style={{padding:"8px 10px",color:C.muted,fontWeight:700,textAlign:["시간(h)","단가","강의료"].includes(h)?"right":"left",fontSize:11,borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {f.lectures.map((row,i)=>(
                  <tr key={row.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.row1:C.row2}}>
                    <td style={{padding:"5px 8px",color:C.muted,textAlign:"center",fontSize:11}}>{i+1}</td>
                    <td style={{padding:"4px 5px",width:105}}>
                      <input value={row.date} onChange={e=>updLecture(i,"date",e.target.value)} placeholder="2025.04.01" style={{...cs,fontFamily:"monospace",fontSize:11,width:96}}/>
                    </td>
                    <td style={{padding:"4px 5px"}}>
                      <input value={row.subject} onChange={e=>updLecture(i,"subject",e.target.value)} placeholder="강의 과목" style={{...cs,minWidth:130}}/>
                    </td>
                    <td style={{padding:"4px 5px",width:65}}>
                      <input type="number" value={row.hours} onChange={e=>updLecture(i,"hours",Number(e.target.value))} min={0.5} step={0.5} style={{...cs,textAlign:"right",width:56}}/>
                    </td>
                    <td style={{padding:"4px 5px",width:96}}>
                      <input type="text" value={(row.unitPrice||0).toLocaleString("ko-KR")}
                        onChange={e=>{
                          const val = parseInt(e.target.value.replace(/,/g,"")||"0",10);
                          updLecture(i,"unitPrice",isNaN(val)?0:val);
                        }}
                        style={{...cs,textAlign:"right",width:88}}/>
                    </td>
                    <td style={{padding:"4px 5px",width:105}}>
                      <div style={{background:isDarkTheme()?"#eff6ff":"#eff6ff",border:"1px solid #3b82f6",color:"#1e40af",
                        fontWeight:700,fontFamily:"monospace",padding:"6px 8px",borderRadius:5,fontSize:12,textAlign:"right"}}>
                        {(row.fee||0).toLocaleString("ko-KR")}
                      </div>
                    </td>
                    <td style={{padding:"4px 5px"}}>
                      <input value={row.memo} onChange={e=>updLecture(i,"memo",e.target.value)} placeholder="메모" style={{...cs,minWidth:80}}/>
                    </td>
                    <td style={{padding:"4px 6px",textAlign:"center",width:32}}>
                      <button onClick={()=>delLecture(i)} style={{background:isDarkTheme()?"#450a0a":"#fee2e2",border:isDarkTheme()?"1px solid #7f1d1d":"1px solid #fca5a5",color:isDarkTheme()?C.redL:"#dc2626",borderRadius:5,padding:"3px 6px",fontSize:11,cursor:"pointer",fontWeight:700}}>✕</button>
                    </td>
                  </tr>
                ))}
                <tr style={{background:C.dim,borderTop:`2px solid ${C.blue}`}}>
                  <td colSpan={5} style={{padding:"9px 10px",fontWeight:800,color:C.blue,fontSize:12}}>강의료 합계</td>
                  <td style={{padding:"9px 10px",textAlign:"right",fontWeight:900,color:C.blue,fontFamily:"monospace",fontSize:14}}>{fmtW(lectureTotal)}</td>
                  <td colSpan={2}/>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 메모 */}
        <div style={{marginBottom:18}}>
          <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5}}>관리자 메모</label>
          <input value={f.adminMemo||""} onChange={e=>setF(p=>({...p,adminMemo:e.target.value}))}
            placeholder="수정 사유 또는 참고 사항"
            style={{width:"100%",background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:8,
              padding:"9px 12px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
        </div>

        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>onSave({...f,invoiceAmount:lectureTotal})}
            style={{flex:1,background:"linear-gradient(135deg,#3b82f6,#2563eb)",color:"#fff",border:"none",
              borderRadius:9,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer"}}>
            수정 저장
          </button>
          <button onClick={onClose}
            style={{flex:1,background:C.card,border:`1px solid ${C.border}`,color:C.muted,
              borderRadius:9,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ★ 관리자 로그인 화면
// ══════════════════════════════════════════════════════════════
const INIT_ADMIN_ACCOUNTS = []; // DB에서 관리

// ── 계정 관리 페이지 (전체관리자 전용) — DB 연동
function AccountMgmtPage({currentUser}){
  const isSuperAdmin = currentUser?.role==="전체관리자";
  const [accounts,setAccounts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [showForm,setShowForm]=useState(false);
  const [editTarget,setEditTarget]=useState(null); // db id (숫자)
  const [form,setForm]=useState({username:"",password:"",name:"",role:"운영관리자(hr담당)",active:true});
  const [showPw,setShowPw]=useState({});
  const [errors,setErrors]=useState({});
  const [toast,setToast]=useState("");

  const ROLE_OPTIONS=["전체관리자","운영관리자(hr담당)","운영관리자(회계담당)","운영관리자(기타)"];
  const roleColor={"전체관리자":"blue","운영관리자(hr담당)":"teal","운영관리자(회계담당)":"amber","운영관리자(기타)":"gray"};

  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(""),2500);};

  // 목록 불러오기
  const loadAccounts=async()=>{
    try{
      const data=await api("GET","/users");
      setAccounts(data);
    }catch(e){showToast("❌ 계정 목록 불러오기 실패: "+e.message);}
    finally{setLoading(false);}
  };
  useEffect(()=>{if(isSuperAdmin)loadAccounts();},[isSuperAdmin]);

  const openAdd=()=>{setForm({username:"",password:"",name:"",role:"운영관리자(hr담당)",active:true});setEditTarget(null);setErrors({});setShowForm(true);};
  const openEdit=(a)=>{setForm({username:a.username,password:"",name:a.username,role:a.role,active:a.active});setEditTarget(a.id);setErrors({});setShowForm(true);};

  const validate=()=>{
    const e={};
    if(!editTarget){
      if(!form.username.trim()) e.username="필수";
      else if(!/^[a-zA-Z0-9_]+$/.test(form.username)) e.username="영문·숫자·_만 사용";
      if(!form.password||form.password.length<6) e.password="6자 이상 필수";
    } else {
      if(form.password&&form.password.length<6) e.password="6자 이상 또는 비워두세요";
    }
    if(!form.role) e.role="필수";
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSave=async()=>{
    if(!validate())return;
    setSaving(true);
    try{
      if(editTarget){
        // 역할/활성화 수정
        await api("PUT",`/users/${editTarget}`,{role:form.role,active:form.active});
        // 비밀번호 변경 (입력한 경우만)
        if(form.password) await api("PUT",`/users/${editTarget}/password`,{password:form.password});
        showToast("✅ 계정이 수정되었습니다.");
      } else {
        await api("POST","/users",{username:form.username,password:form.password,role:form.role});
        showToast("✅ 계정이 추가되었습니다.");
      }
      setShowForm(false);
      await loadAccounts();
    }catch(e){showToast("❌ 저장 실패: "+e.message);}
    finally{setSaving(false);}
  };

  const handleDelete=async(a)=>{
    if(a.username===currentUser?.id){alert("현재 로그인된 계정은 삭제할 수 없습니다.");return;}
    if(!window.confirm(`"${a.username}" 계정을 삭제하시겠습니까?\n삭제 후 복구할 수 없습니다.`))return;
    try{
      await api("DELETE",`/users/${a.id}`);
      showToast("계정이 삭제되었습니다.");
      await loadAccounts();
    }catch(e){showToast("❌ 삭제 실패: "+e.message);}
  };

  const handleToggleActive=async(a)=>{
    if(a.username===currentUser?.id){alert("현재 로그인된 계정은 비활성화할 수 없습니다.");return;}
    try{
      await api("PUT",`/users/${a.id}`,{role:a.role,active:!a.active});
      showToast(`✅ ${a.username} 계정 ${!a.active?"활성화":"비활성화"} 완료`);
      await loadAccounts();
    }catch(e){showToast("❌ 실패: "+e.message);}
  };

  if(!isSuperAdmin){
    return(
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:300,gap:12}}>
        <div style={{fontSize:36}}></div>
        <div style={{fontSize:15,fontWeight:700,color:C.text}}>접근 권한 없음</div>
        <div style={{fontSize:13,color:C.muted}}>계정 관리는 <strong>전체관리자</strong>만 접근할 수 있습니다.</div>
      </div>
    );
  }

  return(
    <div>
      {/* 토스트 */}
      {toast&&(
        <div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:700,color:C.text,zIndex:9999,boxShadow:"0 6px 24px rgba(0,0,0,0.15)"}}>
          {toast}
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:17,fontWeight:900,color:C.text,marginBottom:3}}>관리자 계정 관리</div>
          <div style={{fontSize:12,color:C.muted}}>계정 정보는 DB에 저장됩니다 · 전체관리자만 접근 가능</div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="ghost" onClick={loadAccounts}>새로고침</Btn>
          <Btn variant="primary" onClick={openAdd}>+ 계정 추가</Btn>
        </div>
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        {loading?(
          <div style={{padding:40,textAlign:"center",color:C.muted}}>불러오는 중...</div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.thead}}>
                {["아이디","역할","상태","마지막 로그인","생성일","현재 세션",""].map(h=>(
                  <th key={h} style={{padding:"11px 16px",color:C.muted,fontWeight:700,textAlign:"left",borderBottom:`1px solid ${C.border}`,fontSize:11}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.map((a,idx)=>(
                <tr key={a.id} style={{borderBottom:`1px solid ${C.border}`,background:idx%2===0?C.row1:C.row2,opacity:a.active?1:0.5}}>
                  <td style={{padding:"12px 16px",fontFamily:"monospace",fontWeight:700,color:C.text}}>{a.username}</td>
                  <td style={{padding:"12px 16px"}}><Badge color={roleColor[a.role]||"gray"}>{a.role}</Badge></td>
                  <td style={{padding:"12px 16px"}}>
                    <Badge color={a.active?"green":"gray"}>{a.active?"활성":"비활성"}</Badge>
                  </td>
                  <td style={{padding:"12px 16px",fontSize:12,color:C.muted}}>
                    {a.last_login?new Date(a.last_login).toLocaleString("ko-KR"):"없음"}
                  </td>
                  <td style={{padding:"12px 16px",fontSize:12,color:C.muted}}>
                    {a.created_at?new Date(a.created_at).toLocaleDateString("ko-KR"):"—"}
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    {a.username===currentUser?.id
                      ?<Badge color="green">● 로그인 중</Badge>
                      :<span style={{fontSize:11,color:C.muted}}>—</span>}
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <Btn size="sm" variant="ghost" onClick={()=>openEdit(a)}>수정</Btn>
                      <Btn size="sm" variant="secondary" onClick={()=>handleToggleActive(a)}
                        disabled={a.username===currentUser?.id}>
                        {a.active?"비활성화":"활성화"}
                      </Btn>
                      <Btn size="sm" variant="danger" onClick={()=>handleDelete(a)}
                        disabled={a.username===currentUser?.id}
                        style={{opacity:a.username===currentUser?.id?0.4:1}}>
                        삭제
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={showForm} onClose={()=>setShowForm(false)} title={editTarget?"계정 수정":"새 계정 추가"} width={480}>
        {editTarget&&(
          <div style={{background:C.dim,border:`1px solid ${C.border2}`,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:C.muted}}>
            ✏️ <strong style={{color:C.amberL}}>{form.username}</strong> 계정 수정 중 · 아이디 변경 불가
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
          {!editTarget&&(
            <div style={{marginBottom:14,gridColumn:"1/-1"}}>
              <label style={{display:"block",fontSize:11,color:C.muted,fontWeight:700,marginBottom:5}}>
                아이디 <span style={{color:C.redL}}>*</span>
              </label>
              <input value={form.username} onChange={e=>setForm(p=>({...p,username:e.target.value.trim()}))}
                placeholder="hancom3"
                style={{width:"100%",background:C.inputBg,border:`1px solid ${errors.username?"#ef4444":C.border2}`,borderRadius:8,padding:"9px 12px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
              {errors.username&&<div style={{fontSize:11,color:"#f87171",marginTop:3}}>{errors.username}</div>}
            </div>
          )}
          <div style={{marginBottom:14,gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:11,color:C.muted,fontWeight:700,marginBottom:5}}>
              비밀번호 <span style={{color:C.redL}}>{editTarget?"(변경 시에만 입력)":"*"}</span>
              <span style={{fontWeight:400,color:C.slate}}> 6자 이상</span>
            </label>
            <input type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))}
              placeholder={editTarget?"변경하지 않으려면 비워두세요":"비밀번호 입력"}
              style={{width:"100%",background:C.inputBg,border:`1px solid ${errors.password?"#ef4444":C.border2}`,borderRadius:8,padding:"9px 12px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
            {errors.password&&<div style={{fontSize:11,color:"#f87171",marginTop:3}}>{errors.password}</div>}
          </div>
          <div style={{marginBottom:14,gridColumn:"1/-1"}}>
            <label style={{display:"block",fontSize:11,color:C.muted,fontWeight:700,marginBottom:5}}>역할 <span style={{color:C.redL}}>*</span></label>
            <select value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}
              style={{width:"100%",background:C.inputBg,border:`1px solid ${errors.role?"#ef4444":C.border2}`,borderRadius:8,padding:"9px 12px",color:C.text,fontSize:13,outline:"none",boxSizing:"border-box"}}>
              {ROLE_OPTIONS.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            {errors.role&&<div style={{fontSize:11,color:"#f87171",marginTop:3}}>{errors.role}</div>}
          </div>
          {editTarget&&(
            <div style={{marginBottom:14,gridColumn:"1/-1",display:"flex",alignItems:"center",gap:10}}>
              <label style={{fontSize:12,color:C.muted,fontWeight:700}}>계정 상태</label>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                <input type="checkbox" checked={form.active} onChange={e=>setForm(p=>({...p,active:e.target.checked}))}/>
                <span style={{fontSize:13,color:C.text}}>{form.active?"활성":"비활성"}</span>
              </label>
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:10,marginTop:4}}>
          <Btn variant="primary" style={{flex:1}} onClick={handleSave} disabled={saving}>
            {saving?"저장 중...":editTarget?"수정 저장":"계정 추가"}
          </Btn>
          <Btn variant="secondary" style={{flex:1}} onClick={()=>setShowForm(false)}>취소</Btn>
        </div>
      </Modal>

      <div style={{marginTop:18,padding:"12px 16px",background:C.infoBox,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,color:C.muted,lineHeight:1.9}}>
        <div style={{fontWeight:700,color:C.amberL,marginBottom:4}}>⚠️ 보안 안내</div>
        <div>• 계정 정보는 PostgreSQL DB에 저장됩니다. 비밀번호는 bcrypt로 암호화됩니다.</div>
        <div>• 전체관리자 계정은 최소 1개 이상 유지하세요.</div>
        <div>• 비활성화된 계정은 로그인이 차단됩니다.</div>
      </div>
    </div>
  );
}

function AdminLogin({onLogin}){
  const [id,setId]=useState("");
  const [pw,setPw]=useState("");
  const [error,setError]=useState("");
  const [shake,setShake]=useState(false);
  const [loading,setLoading]=useState(false);
  const [showPw,setShowPw]=useState(false);

  const tryLogin=async()=>{
    if(!id.trim()||!pw.trim()){setError("아이디와 비밀번호를 입력해 주세요.");return;}
    setLoading(true);
    try{
      const data = await api("POST","/login",{username:id.trim(),password:pw});
      setToken(data.token);
      onLogin({...data.user, id:data.user.id, name:data.user.id, role:data.user.role});
    }catch(e){
      setError(e.message==="계정 없음"||e.message==="비밀번호 오류"
        ?"아이디 또는 비밀번호가 올바르지 않습니다."
        :`오류: ${e.message}`);
      setShake(true);
      setTimeout(()=>setShake(false),500);
    }finally{
      setLoading(false);
    }
  };

  // ── 관리자 로그인 전용 라이트 팔레트
  const AP={
    bg:"#f0f4ff", card:"#ffffff", border:"#dde5f4", border2:"#c7d4ef",
    text:"#1e2a4a", muted:"#6475a0", slate:"#8496bf", inputBg:"#ffffff", dim:"#eef2fb",
    blue:"#2563eb", blueL:"#3b82f6", blueDim:"#eff6ff", blueBorder:"#bfdbfe",
    green:"#16a34a", red:"#dc2626",
  };

  return(
    <div style={{minHeight:"100vh",background:"linear-gradient(145deg,#eff6ff 0%,#f0f4ff 50%,#f5f3ff 100%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif",position:"relative",overflow:"hidden"}}>
      {/* 배경 장식 */}
      <div style={{position:"absolute",top:"8%",left:"6%",width:380,height:380,borderRadius:"50%",background:"rgba(59,130,246,0.07)",filter:"blur(70px)",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:"8%",right:"6%",width:300,height:300,borderRadius:"50%",background:"rgba(124,58,237,0.06)",filter:"blur(60px)",pointerEvents:"none"}}/>

      <style>{`
        @keyframes adminShake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}
        @keyframes adminFadeIn{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @keyframes adminSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        .admin-card{animation:adminFadeIn 0.5s ease;}
        .admin-card-shake{animation:adminShake 0.4s ease!important;}
      `}</style>

      <div style={{width:"100%",maxWidth:440,padding:"16px",boxSizing:"border-box",position:"relative",zIndex:1}}>
        <div className={`admin-card${shake?" admin-card-shake":""}`}
          style={{background:AP.card,border:`1px solid ${AP.border}`,borderRadius:24,padding:"36px 28px",boxShadow:"0 20px 60px rgba(59,130,246,0.1),0 4px 16px rgba(0,0,0,0.06)"}}>

          {/* 로고 영역 */}
          <div style={{textAlign:"center",marginBottom:32}}>
            <img src="data:image/png;base64,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" alt="logo" style={{width:72,height:72,objectFit:"contain",marginBottom:14}}/>
            <div style={{fontSize:20,fontWeight:900,color:AP.text,letterSpacing:"-0.5px",marginBottom:4}}>청구서 및 급여관리 시스템</div>
            <div style={{fontSize:12,color:AP.muted,letterSpacing:"0.1em",fontWeight:600}}>ADMIN PORTAL</div>
          </div>

          {/* 안내 박스 */}
          <div style={{background:AP.blueDim,border:`1px solid ${AP.blueBorder}`,borderRadius:10,padding:"11px 14px",marginBottom:26,fontSize:11.5,color:AP.muted,lineHeight:1.9}}>
            <div style={{color:AP.blue,fontWeight:700,marginBottom:4}}>관리자 전용 포털</div>
            <div>등록된 관리자 계정으로 로그인하세요.</div>
            <div style={{color:AP.slate}}>계정 문의: 전체관리자에게 요청하세요.</div>
          </div>

          {/* 아이디 입력 */}
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:11,color:AP.blue,fontWeight:700,marginBottom:7,letterSpacing:"0.06em"}}>관리자 ID</label>
            <input
              value={id}
              onChange={e=>{setId(e.target.value);setError("");}}
              onKeyDown={e=>e.key==="Enter"&&tryLogin()}
              placeholder="아이디 입력"
              autoFocus
              style={{width:"100%",background:AP.inputBg,border:`1px solid ${error?AP.red:AP.border2}`,borderRadius:10,padding:"13px 16px",color:AP.text,fontSize:14,outline:"none",boxSizing:"border-box",transition:"border .2s",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.04)"}}
            />
          </div>

          {/* 비밀번호 입력 */}
          <div style={{marginBottom:26}}>
            <label style={{display:"block",fontSize:11,color:AP.blue,fontWeight:700,marginBottom:7,letterSpacing:"0.06em"}}>비밀번호</label>
            <div style={{position:"relative"}}>
              <input
                value={pw}
                type={showPw?"text":"password"}
                onChange={e=>{setPw(e.target.value);setError("");}}
                onKeyDown={e=>e.key==="Enter"&&tryLogin()}
                placeholder="비밀번호 입력"
                style={{width:"100%",background:AP.inputBg,border:`1px solid ${error?AP.red:AP.border2}`,borderRadius:10,padding:"13px 44px 13px 16px",color:AP.text,fontSize:14,outline:"none",boxSizing:"border-box",transition:"border .2s",boxShadow:"inset 0 1px 3px rgba(0,0,0,0.04)"}}
              />
              <button onClick={()=>setShowPw(p=>!p)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:AP.slate,fontSize:15,cursor:"pointer",padding:4}}>
                {showPw?"":""}
              </button>
            </div>
          </div>

          {/* 에러 메시지 */}
          {error&&(
            <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"9px 14px",marginBottom:18,fontSize:12.5,color:AP.red,display:"flex",alignItems:"center",gap:8}}>
              ⚠️ {error}
            </div>
          )}

          {/* 로그인 버튼 */}
          <button
            onClick={tryLogin}
            disabled={loading}
            style={{width:"100%",background:loading?"#bfdbfe":"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:800,cursor:loading?"not-allowed":"pointer",letterSpacing:"0.04em",transition:"opacity .15s",display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:loading?"none":"0 6px 20px rgba(59,130,246,0.3)"}}>
            {loading?(
              <><span style={{display:"inline-block",width:16,height:16,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"#fff",borderRadius:"50%",animation:"adminSpin 0.7s linear infinite"}}/>로그인 중...</>
            ):"관리자 로그인"}
          </button>

          <div style={{textAlign:"center",marginTop:20,fontSize:11,color:AP.slate}}>
            강사라면 아래 강사 포털을 이용해 주세요
          </div>
          <div style={{marginTop:10,textAlign:"center"}}>
            <a href="/instructor" target="_blank"
              style={{display:"inline-block",background:"#f3eeff",border:"1px solid #d4b8fc",borderRadius:8,padding:"7px 20px",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",textDecoration:"none"}}>강사 포털 바로가기
            </a>
          </div>
          <div style={{textAlign:"center",marginTop:20,fontSize:10,color:AP.slate,opacity:0.6}}>
            v{APP_VERSION} · 한컴캠퍼스 급여관리 시스템
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// ★ AI 급여 어시스턴트 (Claude API 연동)
// ══════════════════════════════════════════════════════════════
function AiAssistant({employees,instructors}){
  const [open,setOpen]=useState(false);
  const [messages,setMessages]=useState([
    {role:"assistant",text:"안녕하세요! 급여 관련 궁금한 사항을 물어보세요 \n예) 직원 평균 실수령액은? / 4대보험 계산 방법은?"}
  ]);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(false);
  const bottomRef=useRef(null);

  useEffect(()=>{
    if(open) bottomRef.current?.scrollIntoView({behavior:"smooth"});
  },[messages,open]);

  const buildContext=()=>{
    const empSummary=employees.filter(e=>e.status==="active").map(e=>{
      const c=calcEmployee(e);
      return `${e.name}(${e.dept} ${e.position}): 기본급 ${fmtW(e.baseSalary)}, 실수령 ${fmtW(c.netPay)}`;
    }).join("\n");
    const insSummary=instructors.filter(i=>i.status==="active").map(i=>{
      const c=calcInstructor(i.invoiceAmount);
      return `${i.name}(${i.subject}): 청구액 ${fmtW(i.invoiceAmount)}, 실지급 ${fmtW(c.netPay)}`;
    }).join("\n");
    return `현재 급여관리 시스템 데이터 (${THIS_MONTH}):
[직원 ${employees.filter(e=>e.status==="active").length}명]
${empSummary}
[강사 ${instructors.filter(i=>i.status==="active").length}명]
${insSummary}
보험료율: 국민연금 4.75%, 건강보험 3.595%, 장기요양 13.14%(건강보험료 기준), 고용보험 0.9%`;
  };

  const sendMessage=async()=>{
    const text=input.trim();
    if(!text||loading)return;
    setInput("");
    const userMsg={role:"user",text};
    setMessages(p=>[...p,userMsg]);
    setLoading(true);

    try{
      const context=buildContext();
      const history=messages
        .filter(m=>m.role!=="error")
        .map(m=>({role:m.role==="assistant"?"assistant":"user",content:m.text}));

      const res=await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:1000,
          system:`당신은 한국 급여관리 전문 AI 어시스턴트입니다. 사용자의 급여 시스템 데이터를 참고하여 정확하고 친절하게 답변해 주세요. 숫자는 한국 원화 형식으로 표시하고, 간결하게 핵심만 답변하세요.

${context}`,
          messages:[...history,{role:"user",content:text}],
        }),
      });

      const data=await res.json();
      const reply=data.content?.find(b=>b.type==="text")?.text||"응답을 받지 못했습니다.";
      setMessages(p=>[...p,{role:"assistant",text:reply}]);
    }catch(e){
      setMessages(p=>[...p,{role:"error",text:"오류가 발생했습니다. 잠시 후 다시 시도해 주세요."}]);
    }finally{
      setLoading(false);
    }
  };

  const isDark=isDarkTheme();

  const QUICK=[
    "직원 평균 실수령액은?",
    "이번달 총 4대보험 부담액?",
    "소득세가 가장 높은 직원은?",
    "강사 실지급 합계는?",
  ];

  return(
    <>
      {/* 플로팅 버튼 */}
      <button
        onClick={()=>setOpen(p=>!p)}
        style={{position:"fixed",bottom:28,right:28,width:54,height:54,borderRadius:"50%",background:"linear-gradient(135deg,#1d4ed8,#7c3aed)",border:"none",color:"#fff",fontSize:24,cursor:"pointer",zIndex:9000,boxShadow:"0 6px 24px rgba(59,130,246,0.45)",display:"flex",alignItems:"center",justifyContent:"center",transition:"transform .2s"}}>
        {open?"✕":""}
      </button>

      {/* 채팅 패널 */}
      {open&&(
        <div style={{position:"fixed",bottom:94,right:28,width:370,height:500,background:isDark?"#0a1428":"#ffffff",border:`1px solid ${C.border}`,borderRadius:20,display:"flex",flexDirection:"column",zIndex:8999,boxShadow:"0 20px 60px rgba(0,0,0,0.35)",overflow:"hidden"}}>
          {/* 헤더 */}
          <div style={{background:"linear-gradient(135deg,#1d4ed8,#7c3aed)",padding:"14px 18px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:34,height:34,borderRadius:10,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}></div>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:"#fff"}}>AI 급여 어시스턴트</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.6)"}}>Claude API 연동 · 현재 데이터 반영</div>
            </div>
            <div style={{marginLeft:"auto",width:8,height:8,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 6px #4ade80"}}/>
          </div>

          {/* 메시지 영역 */}
          <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
            {messages.map((m,i)=>(
              <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                <div style={{
                  maxWidth:"82%",
                  background:m.role==="user"
                    ?"linear-gradient(135deg,#1d4ed8,#3b82f6)"
                    :m.role==="error"
                      ?(isDark?"#2d0a0a":"#fee2e2")
                      :(isDark?"#0f1f3d":"#f1f5f9"),
                  color:m.role==="user"?"#fff":m.role==="error"?(isDark?"#f87171":"#b91c1c"):(isDark?"#cbd5e1":"#0f172a"),
                  borderRadius:m.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",
                  padding:"10px 14px",
                  fontSize:12.5,
                  lineHeight:1.65,
                  whiteSpace:"pre-wrap",
                  border:m.role==="user"?"none":`1px solid ${isDark?"#1e3a5f":"#e2e8f0"}`,
                }}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading&&(
              <div style={{display:"flex",gap:5,padding:"10px 14px",background:isDark?"#0f1f3d":"#f1f5f9",borderRadius:"14px 14px 14px 4px",width:"fit-content",border:`1px solid ${isDark?"#1e3a5f":"#e2e8f0"}`}}>
                {[0,1,2].map(i=>(
                  <div key={i} style={{width:7,height:7,borderRadius:"50%",background:isDark?"#3b82f6":"#94a3b8",animation:`adminSpin 1.2s ease-in-out ${i*0.2}s infinite`}}/>
                ))}
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* 빠른 질문 */}
          {messages.length<=1&&(
            <div style={{padding:"0 14px 10px",display:"flex",flexWrap:"wrap",gap:5}}>
              {QUICK.map((q,i)=>(
                <button key={i} onClick={()=>{setInput(q);}}
                  style={{background:isDark?"rgba(59,130,246,0.1)":"#eff6ff",border:`1px solid ${isDark?"rgba(59,130,246,0.25)":"#bfdbfe"}`,borderRadius:20,padding:"5px 11px",fontSize:11,color:isDark?"#60a5fa":"#1d4ed8",cursor:"pointer",fontWeight:600}}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* 입력 영역 */}
          <div style={{padding:"10px 14px",borderTop:`1px solid ${C.border}`,display:"flex",gap:8,flexShrink:0}}>
            <input
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()}
              placeholder="급여 관련 질문을 입력하세요..."
              style={{flex:1,background:isDark?"rgba(15,25,50,0.8)":"#f8fafc",border:`1px solid ${C.border2}`,borderRadius:10,padding:"9px 13px",color:C.text,fontSize:12.5,outline:"none"}}
            />
            <button
              onClick={sendMessage}
              disabled={loading||!input.trim()}
              style={{background:loading||!input.trim()?"#1e3a5f":"linear-gradient(135deg,#1d4ed8,#7c3aed)",color:"#fff",border:"none",borderRadius:10,padding:"9px 14px",fontSize:16,cursor:loading||!input.trim()?"not-allowed":"pointer",transition:"opacity .15s"}}>
              ↑
            </button>
          </div>
        </div>
      )}
    </>
  );
}


// ══════════════════════════════════════════════════════════════
// ★ 보험료율 설정 페이지
// ══════════════════════════════════════════════════════════════
function RatesPage(){
  const nowYear = new Date().getFullYear();
  const [form,setForm]=useState({
    year:       nowYear,
    pensionEmp: 4.75,  pensionEr: 4.75,
    pensionMin: 390000, pensionMax: 6170000,
    healthEmp:  3.595, healthEr: 3.595,
    ltcRate:    13.14,
    employEmp:  0.9,   employEr: 1.15,
  });
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [toast,setToast]=useState("");
  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(""),3000);};

  useEffect(()=>{
    api("GET","/rates").then(data=>{
      if(data.rates){
        setForm({
          year:       data.rates.year,
          pensionEmp: +(data.rates.pensionEmp*100).toFixed(4),
          pensionEr:  +(data.rates.pensionEr*100).toFixed(4),
          pensionMin: data.rates.pensionMin,
          pensionMax: data.rates.pensionMax,
          healthEmp:  +(data.rates.healthEmp*100).toFixed(4),
          healthEr:   +(data.rates.healthEr*100).toFixed(4),
          ltcRate:    +(data.rates.ltcRate*100).toFixed(4),
          employEmp:  +(data.rates.employEmp*100).toFixed(4),
          employEr:   +(data.rates.employEr*100).toFixed(4),
        });
      }
    }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const set=(k,v)=>setForm(p=>({...p,[k]:v}));

  const save=async()=>{
    setSaving(true);
    try{
      await api("POST","/rates",{
        year:       form.year,
        pensionEmp: form.pensionEmp/100,
        pensionEr:  form.pensionEr/100,
        pensionMin: Number(form.pensionMin),
        pensionMax: Number(form.pensionMax),
        healthEmp:  form.healthEmp/100,
        healthEr:   form.healthEr/100,
        ltcRate:    form.ltcRate/100,
        employEmp:  form.employEmp/100,
        employEr:   form.employEr/100,
      });
      // 전역 RATES 즉시 업데이트
      RATES.pension.emp = form.pensionEmp/100;
      RATES.pension.er  = form.pensionEr/100;
      RATES.pension.min = Number(form.pensionMin);
      RATES.pension.max = Number(form.pensionMax);
      RATES.health.emp  = form.healthEmp/100;
      RATES.health.er   = form.healthEr/100;
      RATES.ltc.rate    = form.ltcRate/100;
      RATES.employ.emp  = form.employEmp/100;
      RATES.employ.er   = form.employEr/100;
      showToast("✅ 보험료율이 저장되었습니다. 이후 계산부터 즉시 반영됩니다.");
    }catch(e){ showToast("❌ 저장 실패: "+e.message); }
    finally{ setSaving(false); }
  };

  const RateRow=({label,empKey,erKey,unit="%",note})=>(
    <tr style={{borderBottom:`1px solid ${C.border}`}}>
      <td style={{padding:"11px 14px",color:C.text,fontWeight:600,fontSize:13}}>{label}</td>
      <td style={{padding:"11px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <input type="number" step="0.001" value={form[empKey]}
            onChange={e=>set(empKey,parseFloat(e.target.value)||0)}
            style={{width:90,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"6px 10px",color:C.text,fontSize:13,outline:"none",textAlign:"right"}}/>
          <span style={{color:C.muted,fontSize:12}}>{unit}</span>
        </div>
      </td>
      <td style={{padding:"11px 14px"}}>
        {erKey&&(
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <input type="number" step="0.001" value={form[erKey]}
              onChange={e=>set(erKey,parseFloat(e.target.value)||0)}
              style={{width:90,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"6px 10px",color:C.text,fontSize:13,outline:"none",textAlign:"right"}}/>
            <span style={{color:C.muted,fontSize:12}}>{unit}</span>
          </div>
        )}
      </td>
      <td style={{padding:"11px 14px",color:C.muted,fontSize:12}}>{note}</td>
    </tr>
  );

  if(loading) return <div style={{padding:40,textAlign:"center",color:C.muted}}>불러오는 중...</div>;

  return(
    <div>
      {toast&&<div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:700,color:C.text,zIndex:9999,boxShadow:"0 6px 24px rgba(0,0,0,0.15)"}}>{toast}</div>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div>
          <h1 style={{fontSize:21,fontWeight:900,color:C.text,margin:0}}>보험료율 설정</h1>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>매년 변경되는 4대보험 요율을 직접 설정합니다. 저장 후 즉시 계산에 반영됩니다.</div>
        </div>
        <Btn variant="primary" onClick={save} disabled={saving}>{saving?"저장중...":"💾 저장"}</Btn>
      </div>

      {/* 적용 연도 */}
      <Card style={{marginBottom:18,padding:"14px 20px",display:"flex",alignItems:"center",gap:14}}>
        <span style={{fontSize:13,fontWeight:700,color:C.text}}>적용 연도</span>
        <input type="number" value={form.year} onChange={e=>set("year",parseInt(e.target.value)||nowYear)}
          style={{width:100,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"6px 10px",color:C.text,fontSize:14,fontWeight:700,outline:"none",textAlign:"center"}}/>
        <span style={{fontSize:12,color:C.muted}}>년도 기준 요율</span>
      </Card>

      {/* 요율 테이블 */}
      <Card style={{padding:0,overflow:"hidden",marginBottom:18}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:C.thead}}>
              <th style={{padding:"10px 14px",color:C.muted,fontWeight:700,fontSize:11,textAlign:"left",borderBottom:`1px solid ${C.border}`}}>항목</th>
              <th style={{padding:"10px 14px",color:C.muted,fontWeight:700,fontSize:11,textAlign:"left",borderBottom:`1px solid ${C.border}`}}>근로자 부담</th>
              <th style={{padding:"10px 14px",color:C.muted,fontWeight:700,fontSize:11,textAlign:"left",borderBottom:`1px solid ${C.border}`}}>사업주 부담</th>
              <th style={{padding:"10px 14px",color:C.muted,fontWeight:700,fontSize:11,textAlign:"left",borderBottom:`1px solid ${C.border}`}}>비고</th>
            </tr>
          </thead>
          <tbody>
            <RateRow label="국민연금"     empKey="pensionEmp" erKey="pensionEr"  note="기준소득월액 기준"/>
            <RateRow label="건강보험"     empKey="healthEmp"  erKey="healthEr"   note="보수월액 기준"/>
            <RateRow label="장기요양보험" empKey="ltcRate"    erKey="ltcRate"    note="건강보험료 × 요율"/>
            <RateRow label="고용보험"     empKey="employEmp"  erKey="employEr"   note="임원/대표이사 면제"/>
          </tbody>
        </table>
      </Card>

      {/* 국민연금 기준소득 범위 */}
      <Card style={{marginBottom:18}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:12}}>국민연금 기준소득 범위</div>
        <div style={{display:"flex",gap:20,alignItems:"center"}}>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>최저 기준소득 (원)</div>
            <input type="text" value={Number(form.pensionMin).toLocaleString("ko-KR")}
              onChange={e=>set("pensionMin",parseInt(e.target.value.replace(/,/g,"")||"0",10))}
              style={{width:140,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"7px 10px",color:C.text,fontSize:13,outline:"none",textAlign:"right"}}/>
          </div>
          <span style={{color:C.muted,marginTop:16}}>~</span>
          <div>
            <div style={{fontSize:11,color:C.muted,marginBottom:4}}>최고 기준소득 (원)</div>
            <input type="text" value={Number(form.pensionMax).toLocaleString("ko-KR")}
              onChange={e=>set("pensionMax",parseInt(e.target.value.replace(/,/g,"")||"0",10))}
              style={{width:140,background:C.inputBg,border:`1px solid ${C.border2}`,borderRadius:7,padding:"7px 10px",color:C.text,fontSize:13,outline:"none",textAlign:"right"}}/>
          </div>
        </div>
      </Card>

      {/* 안내 */}
      <Card style={{background:C.infoBox}}>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.8}}>
          <div style={{fontWeight:700,color:C.text,marginBottom:6}}>📌 보험료율 변경 안내</div>
          <div>• 국민연금: 매년 7월 기준소득 상한/하한 조정 (2025년 기준: 390,000원 ~ 6,170,000원)</div>
          <div>• 건강보험: 매년 1월 조정 (2025년 기준: 근로자 3.545%, 사업주 3.545%)</div>
          <div>• 장기요양보험: 건강보험료의 일정 비율 (2025년 기준: 12.95%)</div>
          <div>• 고용보험: 매년 조정 (2025년 기준: 근로자 0.9%, 사업주 1.15%)</div>
          <div style={{marginTop:8,color:C.amberL}}>⚠️ 저장 후 즉시 모든 급여 계산에 반영됩니다.</div>
        </div>
      </Card>
    </div>
  );
}

const NAV=[
  {id:"dashboard",    icon:"", label:"대시보드"},
  {id:"employees",    icon:"", label:"직원 관리"},
  {id:"instructors",  icon:"‍",label:"강사 관리"},
  {id:"invoicereview",icon:"", label:"청구서 관리"},
  {id:"calc",         icon:"", label:"급여 계산기"},
  {id:"send",         icon:"", label:"명세서 발송"},
  {id:"rates",        icon:"", label:"보험료율 설정"},
  {id:"accounts",     icon:"", label:"계정 관리", superOnly:true},
];

function App(){
  const theme = useTheme(); // 테마 변경 시 리렌더링
  const [themeOpen,setThemeOpen]=useState(false);
  const [currentThemeKey,setCurrentThemeKey]=useState("light");
  const [mode,setMode]=useState("admin"); // "admin" | "instructor-login" | "instructor-portal"
  const [page,setPage]=useState("dashboard");
  const [employees,setEmployees]=useState([]);
  const [instructors,setInstructors]=useState([]);
  const [invoices,setInvoices]=useState(INIT_INVOICES);
  const [loggedInstructor,setLoggedInstructor]=useState(null);
  const [payslipTarget,setPayslipTarget]=useState(null);
  const [payslipType,setPayslipType]=useState("employee");
  const [logs,setLogs]=useState([]);
  const [dataLoading,setDataLoading]=useState(false);
  // ── 관리자 인증
  const [adminUser,setAdminUser]=useState(()=>{
    try {
      const raw = sessionStorage.getItem("payroll_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch(e) { return null; }
  });

  // ── 로그인 후 DB에서 데이터 로드
  const loadData=async()=>{
    setDataLoading(true);
    try{
      const [emps,insts,invs]=await Promise.all([
        api("GET","/employees"),
        api("GET","/instructors"),
        api("GET","/invoices"),
      ]);
      setEmployees(emps);
      setInstructors(insts);
      setInvoices(invs);
    }catch(e){
      console.error("데이터 로드 실패:",e.message);
    }finally{
      setDataLoading(false);
    }
  };

  useEffect(()=>{
    if(adminUser) loadData();
  },[adminUser]);

  // employees 또는 instructors 변경 시 payslipTarget 자동 갱신
  useEffect(()=>{
    if(!payslipTarget) return;
    if(payslipType==="employee"){
      const updated = employees.find(e=>e.id===payslipTarget.id);
      if(updated) setPayslipTarget(updated);
    } else {
      const updated = instructors.find(i=>i.id===payslipTarget.id);
      if(updated) setPayslipTarget(updated);
    }
  },[employees, instructors]);

  const changeTheme=(key)=>{ setTheme(key); setCurrentThemeKey(key); setThemeOpen(false); };

  const totalPay=[
    ...employees.filter(e=>e.status==="active").map(e=>calcEmployee(e).netPay),
    ...instructors.filter(i=>i.status==="active").map(i=>calcInstructor(i.invoiceAmount).netPay),
  ].reduce((s,v)=>s+v,0);

  const viewPayslip=(p,t)=>{
    setPayslipTarget(p);
    setPayslipType(t);
    // 브라우저 히스토리에 기록 추가 → 뒤로가기 시 목록으로 돌아옴
    window.history.pushState({payslip:true}, "");
  };
  const addLog=e=>setLogs(p=>[{id:Date.now(),...e},...p]);

  // 브라우저 뒤로가기 감지 → 명세서 닫기 (로그아웃 방지)
  useEffect(()=>{
    const handlePop = (e)=>{
      if(payslipTarget){
        setPayslipTarget(null);
      }
      // 로그인 상태면 항상 히스토리 앞으로 유지
      if(adminUser){
        window.history.pushState({loggedIn:true}, "");
      }
    };
    window.addEventListener("popstate", handlePop);
    return ()=>window.removeEventListener("popstate", handlePop);
  },[payslipTarget, adminUser]);

  // 로그인 상태 유지 - 페이지 이탈 방지
  useEffect(()=>{
    if(!adminUser) return;
    // 히스토리 스택에 여러 개 쌓아서 뒤로가기 여러 번 눌러도 버팀
    for(let i=0; i<5; i++){
      window.history.pushState({loggedIn:true, i}, "");
    }
    // 페이지 닫기/새로고침/이탈 시 확인
    const handleBeforeUnload = (e)=>{
      e.preventDefault();
      e.returnValue = "로그아웃 하시겠습니까?";
      return "로그아웃 하시겠습니까?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return ()=>window.removeEventListener("beforeunload", handleBeforeUnload);
  },[adminUser]);

  // ── 관리자 로그인 게이트
  if(!adminUser){
    return <AdminLogin onLogin={u=>{
      setAdminUser(u);
      try { sessionStorage.setItem("payroll_admin_user", JSON.stringify(u)); } catch(e) {}
      // 로그인 시 히스토리 추가 → 뒤로가기 해도 로그아웃 안 됨
      window.history.pushState({loggedIn:true}, "");
    }}/>;
  }

  // 명세서 전체화면
  if(payslipTarget){
    return <PayslipPage
      person={payslipTarget}
      type={payslipType}
      onClose={()=>setPayslipTarget(null)}
      onSendLog={addLog}
      onUpdate={updated=>{
        if(payslipType==="employee"){
          setEmployees(prev=>prev.map(e=>e.id===updated.id?updated:e));
        } else {
          setInstructors(prev=>prev.map(i=>i.id===updated.id?updated:i));
        }
        setPayslipTarget(updated); // 현재 보여지는 명세서도 즉시 반영
      }}
    />;
  }

  // 강사 로그인 화면
  if(mode==="instructor-login"){
    return(
      <div>
        <InstructorLogin
          instructors={instructors}
          onLogin={ins=>{setLoggedInstructor(ins);setMode("instructor-portal");}}
        />
        <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)"}}>
          <button onClick={()=>setMode("admin")} style={{background:"rgba(15,23,42,0.8)",border:"1px solid #334155",color:"#475569",borderRadius:8,padding:"7px 18px",fontSize:12,cursor:"pointer"}}>
            ← 관리자 화면으로
          </button>
        </div>
      </div>
    );
  }

  // 강사 포털 화면
  if(mode==="instructor-portal"&&loggedInstructor){
    return(
      <InstructorPortal
        instructor={loggedInstructor}
        invoices={invoices}
        setInvoices={setInvoices}
        onLogout={()=>{setLoggedInstructor(null);setMode("admin");}}
      />
    );
  }

  // 관리자 화면
  return(
    <div style={{minHeight:"100vh",background:C.bg,color:C.text,fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif",display:"flex",flexDirection:"column"}}>
      {/* 헤더 */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 26px",display:"flex",alignItems:"center",justifyContent:"space-between",height:56,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:11,cursor:"pointer"}} onClick={()=>setPage("dashboard")}>
          <img src="data:image/png;base64,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" alt="logo" style={{width:40,height:40,objectFit:"contain"}}/>
          <div>
            <div style={{fontSize:14,fontWeight:900,letterSpacing:"-0.3px"}}>급여/강사료 관리 시스템</div>
            <div style={{fontSize:10,color:C.muted}}>Hancom Campus Payroll</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <Badge color="blue">{THIS_MONTH}</Badge>
          <div style={{fontSize:12,color:C.muted}}>총 지급 <strong style={{color:C.amberL}}>{fmtMan(totalPay)}</strong></div>

          {/* 테마 선택 */}
          <div style={{position:"relative"}}>
            <button onClick={()=>setThemeOpen(p=>!p)}
              style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 12px",color:C.muted,fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
              {THEMES[currentThemeKey].emoji} 테마 ▾
            </button>
            {themeOpen&&(
              <div style={{position:"absolute",top:38,right:0,background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:10,zIndex:500,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",minWidth:180}}>
                <div style={{fontSize:10,color:C.muted,fontWeight:700,marginBottom:8,padding:"0 4px",letterSpacing:"0.06em"}}>배경 테마 선택</div>
                {Object.entries(THEMES).map(([key,t])=>(
                  <button key={key} onClick={()=>changeTheme(key)}
                    style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,border:`1px solid ${currentThemeKey===key?C.blue:C.border}`,background:currentThemeKey===key?C.dim:"transparent",cursor:"pointer",marginBottom:4,textAlign:"left"}}>
                    {/* 컬러 미리보기 */}
                    <div style={{display:"flex",gap:3,flexShrink:0}}>
                      <div style={{width:14,height:14,borderRadius:3,background:t.bg,border:"1px solid rgba(255,255,255,0.15)"}}/>
                      <div style={{width:14,height:14,borderRadius:3,background:t.surface,border:"1px solid rgba(255,255,255,0.1)"}}/>
                      <div style={{width:14,height:14,borderRadius:3,background:t.card,border:"1px solid rgba(255,255,255,0.08)"}}/>
                    </div>
                    <span style={{fontSize:12,color:currentThemeKey===key?C.blueL:C.text,fontWeight:currentThemeKey===key?700:400}}>{t.emoji} {t.label}</span>
                    {currentThemeKey===key&&<span style={{marginLeft:"auto",color:C.blueL,fontSize:12}}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 강사 포털 진입 버튼 */}
          <button onClick={()=>window.open("/instructor","_blank")}
            style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",color:"#e9d5ff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            강사 포털
          </button>
          {/* 관리자 정보 + 로그아웃 */}
          <div style={{display:"flex",alignItems:"center",gap:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px"}}>
            <span style={{fontSize:12,color:C.muted}}></span>
            <span style={{fontSize:12,fontWeight:700,color:C.text}}>{adminUser.name}</span>
            <Badge color={adminUser.role==="전체관리자"?"blue":adminUser.role.includes("hr")?"teal":"amber"}>
              {adminUser.role}
            </Badge>
            <button onClick={()=>{
              if(window.confirm("로그아웃 하시겠습니까?")){
                // beforeunload 이벤트 제거 후 로그아웃
                window.onbeforeunload = null;
                setToken("");
                try { sessionStorage.removeItem("payroll_admin_user"); } catch(e) {}
                setAdminUser(null);
              }
            }}
              style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 9px",color:C.muted,fontSize:11,cursor:"pointer",marginLeft:2}}>
              로그아웃
            </button>
          </div>
        </div>
      </div>

      <div style={{display:"flex",flex:1}}>
        {/* 사이드바 */}
        <div style={{width:192,background:C.surface,borderRight:`1px solid ${C.border}`,padding:"18px 0",flexShrink:0}}>
          {NAV.filter(n=>!n.superOnly || adminUser?.role==="전체관리자").map(n=>{
            // 청구서 관리 메뉴에 대기 건수 뱃지
            const pendingCount = n.id==="invoicereview"
              ? instructors.flatMap(ins=>(invoices[ins.id]||[])).filter(inv=>inv.status==="제출완료").length
              : 0;
            return(
              <button key={n.id} onClick={()=>setPage(n.id)} style={{width:"100%",textAlign:"left",padding:"10px 22px",background:"none",border:"none",borderLeft:`3px solid ${page===n.id?C.blue:"transparent"}`,color:page===n.id?C.blueL:C.muted,fontSize:13,fontWeight:page===n.id?700:500,cursor:"pointer",display:"flex",alignItems:"center",gap:8,transition:"all .15s"}}>
                <span>{n.icon}</span>
                <span style={{flex:1}}>{n.label}</span>
                {pendingCount>0&&<span style={{background:"#d97706",color:"#fff",fontSize:10,fontWeight:800,padding:"1px 6px",borderRadius:20,minWidth:18,textAlign:"center"}}>{pendingCount}</span>}
              </button>
            );
          })}
          <div style={{margin:"18px 14px 0",padding:"12px",background:C.thead,borderRadius:9,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:10,color:C.muted,marginBottom:4,fontWeight:700}}>이번달 총 지급</div>
            <div style={{fontSize:17,fontWeight:900,color:C.blueL}}>{fmtMan(totalPay)}</div>
            <div style={{fontSize:10,color:C.slate,marginTop:2}}>직원 {employees.filter(e=>e.status==="active").length}+강사 {instructors.filter(i=>i.status==="active").length}명</div>
          </div>
          <div style={{margin:"10px 14px 0",padding:"9px 12px",background:C.thead,borderRadius:8,fontSize:11,color:C.muted}}>
            <div style={{fontWeight:700,marginBottom:3}}>발송 이력</div>
            <div style={{color:C.greenL}}>성공 {logs.filter(l=>l.status==="성공").length}건</div>
            <div style={{color:C.redL}}>실패 {logs.filter(l=>l.status==="실패").length}건</div>
          </div>
          {/* 사이드바 강사 포털 바로가기 */}
          <div style={{margin:"14px 14px 0"}}>
            <button onClick={()=>window.open("/instructor","_blank")} style={{width:"100%",background:"linear-gradient(135deg,#2e1065,#4c1d95)",border:"1px solid #7c3aed",borderRadius:9,padding:"9px",color:"#c084fc",fontSize:12,fontWeight:700,cursor:"pointer"}}>
              강사 포털 입장
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div style={{flex:1,padding:"26px 28px",overflowX:"hidden",overflowY:"auto"}}>
          {page==="dashboard"   &&<DashboardPage employees={employees} instructors={instructors}/>}
          {page==="employees"   &&<EmpPage employees={employees} setEmployees={setEmployees} onPayslip={viewPayslip} onEmpUpdate={updated=>{
            setEmployees(prev=>prev.map(e=>e.id===updated.id?{...e,...updated}:e));
            if(payslipTarget && payslipTarget.id===updated.id) setPayslipTarget(prev=>({...prev,...updated}));
          }}/>}
          {page==="instructors" &&<InsPage instructors={instructors} setInstructors={setInstructors} onPayslip={viewPayslip} onInsUpdate={updated=>{
            setInstructors(prev=>prev.map(i=>i.id===updated.id?{...i,...updated}:i));
            if(payslipTarget && payslipTarget.id===updated.id) setPayslipTarget(prev=>({...prev,...updated}));
          }}/>}
          {page==="invoicereview"&&<AdminInvoiceReview instructors={instructors} invoices={invoices} setInvoices={setInvoices}/>}
          {page==="calc"        &&<CalcPage/>}
          {page==="send"        &&<SendPage employees={employees} instructors={instructors} logs={logs} setLogs={setLogs} onPreviewPayslip={viewPayslip}/>}
          {page==="rates"       &&<RatesPage/>}
          {page==="accounts"    &&<AccountMgmtPage currentUser={adminUser}/>}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ★ 모바일 명세서 페이지 (/slip/:token)
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ★ 강사 포털 직접 접근 (/instructor)
// ══════════════════════════════════════════════════════════════
function InstructorPortalEntry(){
  const [instructors, setInstructors] = useState([]);
  const [invoices, setInvoices] = useState({});
  const [loggedInstructor, setLoggedInstructor] = useState(null);
  const [loading, setLoading] = useState(true);

  // 강사 목록 로드 (인증 없이 이름+생년월일 검증용)
  useEffect(()=>{
    fetch(`${API_BASE}/instructors-public`)
      .then(r=>r.json())
      .then(data=>{ setInstructors(data); setLoading(false); })
      .catch(()=>setLoading(false));
  },[]);

  // 강사 로그인 상태 유지 - 뒤로가기/이탈 방지
  useEffect(()=>{
    if(!loggedInstructor) return;
    for(let i=0; i<5; i++){
      window.history.pushState({instructorLoggedIn:true, i}, "");
    }
    const handleBeforeUnload = (e)=>{
      e.preventDefault();
      e.returnValue = "로그아웃 하시겠습니까?";
      return "로그아웃 하시겠습니까?";
    };
    const handlePop = ()=>{
      if(window.confirm("로그아웃 하시겠습니까?")){
        window.onbeforeunload = null;
        setLoggedInstructor(null);
      } else {
        window.history.pushState({instructorLoggedIn:true}, "");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePop);
    return ()=>{
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePop);
    };
  },[loggedInstructor]);

  // 로그인 후 청구서 로드
  useEffect(()=>{
    if(!loggedInstructor) return;
    fetch(`${API_BASE}/invoices-public/${loggedInstructor.id}`)
      .then(r=>r.json())
      .then(data=>setInvoices({[loggedInstructor.id]: data}))
      .catch(()=>{});
  },[loggedInstructor]);

  const saveInvoice=async(inv)=>{
    try{
      if(inv.dbId){
        await fetch(`${API_BASE}/invoices/${inv.dbId}`,{
          method:"PUT",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({invoiceAmount:inv.invoiceAmount,lectures:inv.lectures,memo:inv.memo,status:inv.status}),
        });
      } else {
        await fetch(`${API_BASE}/invoices`,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({instCode:loggedInstructor.id,month:inv.month,invoiceAmount:inv.invoiceAmount,lectures:inv.lectures,memo:inv.memo,status:inv.status}),
        });
      }
      // 청구서 재로드
      const data = await fetch(`${API_BASE}/invoices-public/${loggedInstructor.id}`).then(r=>r.json());
      setInvoices({[loggedInstructor.id]: data});
    }catch(e){ console.error("저장 실패:",e.message); }
  };

  const deleteInvoice=async(id)=>{
    if(!window.confirm("이 청구서를 삭제하시겠습니까?"))return;
    try{
      await fetch(`${API_BASE}/invoices/${id}`,{method:"DELETE"});
      const data = await fetch(`${API_BASE}/invoices-public/${loggedInstructor.id}`).then(r=>r.json());
      setInvoices({[loggedInstructor.id]: data});
    }catch(e){ console.error("삭제 실패:",e.message); }
  };

  if(loading) return(
    <div style={{minHeight:"100vh",background:"linear-gradient(145deg,#eef2ff,#f5f0ff,#eff6ff)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <div style={{width:48,height:48,border:"4px solid #d4b8fc",borderTopColor:"#7c3aed",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 16px"}}/>
        <div style={{fontSize:14,color:"#6475a0"}}>불러오는 중...</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if(!loggedInstructor) return(
    <div>
      <InstructorLogin instructors={instructors} onLogin={ins=>setLoggedInstructor(ins)}/>
    </div>
  );

  return(
    <InstructorPortal
      instructor={loggedInstructor}
      invoices={invoices}
      setInvoices={setInvoices}
      onSaveInvoice={saveInvoice}
      onDeleteInvoice={deleteInvoice}
      onLogout={()=>{
        if(window.confirm("로그아웃 하시겠습니까?")){
          window.onbeforeunload = null;
          setLoggedInstructor(null);
        }
      }}
    />
  );
}

function SlipPage(){
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [slip, setSlip] = useState(null);
  const [needVerify, setNeedVerify] = useState(false);
  const [birthInput, setBirthInput] = useState("");
  const [verifyError, setVerifyError] = useState("");

  useEffect(()=>{
    fetch(API_BASE+"/slip/"+token)
      .then(r=>r.json())
      .then(data=>{
        if(data.ok){
          if(data.needVerify){ setNeedVerify(true); setStatus("verify"); }
          else { setSlip(data.slip); setStatus("ok"); }
        } else setStatus("expired");
      })
      .catch(()=>setStatus("error"));
  },[token]);

  const handleVerify = async () => {
    if(birthInput.length !== 8){ setVerifyError("생년월일 8자리를 입력해주세요. (예: 19901231)"); return; }
    try {
      const res = await fetch(API_BASE+"/slip/"+token+"/verify", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({birthDate: birthInput})
      });
      const data = await res.json();
      if(data.ok){ setSlip(data.slip); setStatus("ok"); setNeedVerify(false); }
      else { setVerifyError(data.error || "생년월일이 일치하지 않습니다."); }
    } catch(e){ setVerifyError("오류가 발생했습니다."); }
  };
  if(status==="verify") return(
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{background:"#fff",borderRadius:20,padding:"40px 28px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:40,marginBottom:16}}></div>
        <div style={{fontSize:18,fontWeight:900,color:"#1e2a4a",marginBottom:8}}>본인 확인</div>
        <div style={{fontSize:13,color:"#6475a0",marginBottom:24,lineHeight:1.7}}>급여명세서 열람을 위해<br/>생년월일을 입력해 주세요.</div>
        <input
          type="number"
          placeholder="생년월일 8자리 (예: 19901231)"
          value={birthInput}
          onChange={e=>{ setBirthInput(e.target.value.slice(0,8)); setVerifyError(""); }}
          style={{width:"100%",padding:"12px 16px",border:"2px solid #dde5f4",borderRadius:10,fontSize:15,textAlign:"center",boxSizing:"border-box",marginBottom:12,outline:"none"}}
        />
        {verifyError&&<div style={{color:"#dc2626",fontSize:12,marginBottom:12}}>{verifyError}</div>}
        <button onClick={handleVerify}
          style={{width:"100%",padding:"14px",background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:"pointer"}}>
          본인 확인
        </button>
        <div style={{marginTop:16,fontSize:11,color:"#8496bf"}}>문의: 인사팀 ☎ 02-2633-1184</div>
      </div>
    </div>
  );
  if(status==="loading") return(
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <div style={{width:48,height:48,border:"4px solid #dde5f4",borderTopColor:"#3b82f6",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 16px"}}/>
        <div style={{fontSize:14,color:"#6475a0"}}>명세서를 불러오는 중...</div>
      </div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
  if(status==="expired"||status==="error") return(
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{background:"#fff",borderRadius:20,padding:"40px 32px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:48,marginBottom:16}}>{"\uD83D\uDD12"}</div>
        <div style={{fontSize:18,fontWeight:900,color:"#1e2a4a",marginBottom:8}}>
          {status==="expired"?"링크가 만료되었습니다":"오류가 발생했습니다"}
        </div>
        <div style={{fontSize:14,color:"#6475a0",lineHeight:1.7,marginBottom:24}}>
          {status==="expired"?"이 링크는 만료되었거나 존재하지 않습니다. 인사팀에 재발송을 요청해 주세요.":"일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요."}
        </div>
        <div style={{fontSize:12,color:"#8496bf"}}>문의: 인사팀 ☎ 02-2633-1184</div>
      </div>
    </div>
  );
  const monthStr=slip.month||"";
  const nameStr=slip.person_name||"";
  const sd=typeof slip.slip_data==="string"?JSON.parse(slip.slip_data):(slip.slip_data||null);
  const payItems=sd?[{l:"기본급",v:sd.baseSalary},...(sd.allowances||[]).map(a=>({l:a.name+(a.nonTaxable?" (비과세)":""),v:a.amount}))]:[];
  const deductItems=sd?[
    {l:"국민연금(4.75%)",v:sd.pension},
    {l:"건강보험(3.595%)",v:sd.health},
    {l:"장기요양보험",v:sd.ltc},
    {l:sd.isExempt?"고용보험(면제)":"고용보험(0.9%)",v:sd.employ},
    {l:"소득세",v:sd.incomeTax},
    {l:"주민세",v:sd.residentTax},
    ...(sd.deductions||[]).filter(d=>d.name&&d.amount>0).map(d=>({l:d.name+"(추가공제)",v:d.amount})),
  ]:[];
  const rowStyle={display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f0f4ff",fontSize:13};
  const cardStyle={background:"#fff",borderRadius:14,padding:"16px",marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1px solid #dde5f4"};
  return(
    <div style={{minHeight:"100vh",background:"#f0f4ff",fontFamily:"'Apple SD Gothic Neo','Malgun Gothic',sans-serif"}}>
      <div style={{background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",padding:"24px 20px 20px",color:"#fff"}}>
        <div style={{fontSize:11,color:"#bfdbfe",fontWeight:700,letterSpacing:"0.1em",marginBottom:6}}>PAY SLIP · (주)한컴캠퍼스</div>
        <div style={{fontSize:22,fontWeight:900,marginBottom:4}}>급 여 명 세 서</div>
        <div style={{fontSize:13,color:"#bfdbfe"}}>{monthStr} 분 지급</div>
        <div style={{marginTop:16,display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:40,height:40,borderRadius:12,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{"\u{1F464}"}</div>
          <div>
            <div style={{fontSize:16,fontWeight:800}}>{nameStr} 님</div>
            <div style={{fontSize:11,color:"#bfdbfe"}}>{sd&&sd.position?sd.position:"본인 확인 후 열람하세요"}</div>
          </div>
        </div>
      </div>
      <div style={{padding:"16px 16px 40px"}}>
        {sd&&(
          <div style={{background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",borderRadius:16,padding:"20px",marginBottom:14,color:"#fff",textAlign:"center"}}>
            <div style={{fontSize:11,color:"#bfdbfe",letterSpacing:"0.1em",marginBottom:6}}>NET PAY · 실수령액</div>
            <div style={{fontSize:28,fontWeight:900}}>{fmtW(sd.netPay)}</div>
            <div style={{fontSize:11,color:"#bfdbfe",marginTop:4}}>지급 {fmtW(sd.gross)} − 공제 {fmtW(sd.totalDeduct)}</div>
          </div>
        )}
        {sd&&(
          <div style={cardStyle}>
            <div style={{fontSize:12,fontWeight:700,color:"#1e2a4a",marginBottom:12}}>지급 내역</div>
            {payItems.map((r,i)=>(<div key={i} style={rowStyle}><span style={{color:"#6475a0"}}>{r.l}</span><span style={{fontWeight:700,color:"#16a34a"}}>{fmtW(r.v)}</span></div>))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 0",fontSize:14,fontWeight:900}}>
              <span>지급합계</span><span style={{color:"#16a34a"}}>{fmtW(sd.gross)}</span>
            </div>
          </div>
        )}
        {sd&&(
          <div style={cardStyle}>
            <div style={{fontSize:12,fontWeight:700,color:"#1e2a4a",marginBottom:12}}>공제 내역</div>
            {deductItems.map((r,i)=>(<div key={i} style={rowStyle}><span style={{color:"#6475a0"}}>{r.l}</span><span style={{fontWeight:700,color:r.v===0?"#9ca3af":"#dc2626"}}>{fmtW(r.v)}</span></div>))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 0",fontSize:14,fontWeight:900}}>
              <span>공제합계</span><span style={{color:"#dc2626"}}>{fmtW(sd.totalDeduct)}</span>
            </div>
          </div>
        )}
        <div style={cardStyle}>
          <div style={{fontSize:12,color:"#6475a0",lineHeight:1.8}}>
            <div>• 이 링크는 <strong style={{color:"#dc2626"}}>7일 후 만료</strong>됩니다.</div>
            <div>• 본인 외 타인에게 공유하지 마세요.</div>
            <div>• 문의: 인사팀 ☎ 02-2633-1184</div>
          </div>
        </div>
        <div style={{textAlign:"center",fontSize:11,color:"#8496bf",lineHeight:1.8}}>
          <div>본 명세서는 (주)한컴캠퍼스에서 발행하였습니다.</div>
          <div>© 한컴캠퍼스 급여관리 시스템</div>
        </div>
      </div>
    </div>
  );
}

// ── 라우터 진입점
export default function Root(){
  return(
    <BrowserRouter>
      <Routes>
        <Route path="/slip/:token" element={<><TitleSetter title="Hancom Pay"/><SlipPage/></>}/>
        <Route path="/instructor" element={<><TitleSetter title="Hancom Instructor"/><InstructorPortalEntry/></>}/>
        <Route path="/*" element={<><TitleSetter title="Hancom Pay"/><App/></>}/>
      </Routes>
    </BrowserRouter>
  );
}

function TitleSetter({title}){
  useEffect(()=>{ document.title=title; },[title]);
  return null;
}